import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { postAdminResolveWithRetry } from '../utils/api';
import { useNotification } from '../components/Notification';
import {
  createMarket,
  addLiquidity,
  batchAddOrderbookLiquidity,
  resolveMarket,
  updateMarketStatus,
  ensureUsdcAllowance,
  usdcToUnits,
  isOnChainAdmin,
  getBlockchainErrorMessage,
} from '../utils/blockchain';
import Modal from '../components/Modal';
import EventClaimStatsModal from '../components/EventClaimStatsModal';
import DataRoomTab from '../components/DataRoomTab';
import StoreAdminTab from '../components/StoreAdminTab';
import AdminNftRewardsTab from '../components/AdminNftRewardsTab';
import TargetOddsInputs from '../components/TargetOddsInputs';
import {
  DEFAULT_QUOTE_VOLUME_USDC,
  evenPctSplit,
  startingPricesFromPctRows,
  distributeEvenlyWithBalance,
} from '../utils/targetOdds';
import TiptapEditor from '../components/TiptapEditor';
import ImageUpload from '../components/ImageUpload';
import SponsoredImagesEditor from '../components/SponsoredImagesEditor';
import { normalizeSponsoredImages } from '../utils/sponsoredImages';
import { formatUsdAmount } from '../utils/money';
import { normalizeFreeTicketRequirements, formatFreeEntryRequirement } from '../utils/freeTicketRequirements';
import { utcDatetimeLocalToIso, utcIsoToDatetimeLocal, formatEventDateGmt } from '../utils/eventDate';
import { isRetiredAdSenseClient, normalizeAdSenseClient } from '../utils/adsense';
// USDC ~ USD; show $ directly (no ETH conversion hints)

const ITEMS_PER_PAGE = 20;

/** Format 0–1 price for starting-price inputs (2 decimal places). */
function formatStartingPrice(n) {
  const v = Math.max(0, Math.min(1, Number(n) || 0));
  return String(Math.round(v * 100) / 100);
}

/** Complementary side so YES + NO = 1 per outcome. */
function complementaryStartingPrice(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return null;
  return formatStartingPrice(1 - Math.max(0, Math.min(1, n)));
}

const INITIAL_LIQUIDITY_MIN = 0.01;
const INITIAL_LIQUIDITY_MAX = 10;
const INITIAL_LIQUIDITY_DEFAULT = '0.01';

function isAlreadyResolvedOnChain(err) {
  const msg = String(err?.reason || err?.shortMessage || err?.message || '').toLowerCase();
  return msg.includes('already resolved');
}

/** Clamp above max while typing; allow transient empty/partial entry. */
function sanitizeInitialLiquidityInput(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return '';
  if (!/^\d*\.?\d*$/.test(s)) return null;
  if (s === '.' || s.endsWith('.')) return s;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return '';
  if (n > INITIAL_LIQUIDITY_MAX) return String(INITIAL_LIQUIDITY_MAX);
  return s;
}

/** On blur: never empty / never below min — snap to the 0.01–10 range. */
function finalizeInitialLiquidityInput(raw) {
  const s = String(raw ?? '').trim();
  const n = parseFloat(s);
  if (s === '' || Number.isNaN(n) || n < INITIAL_LIQUIDITY_MIN) return INITIAL_LIQUIDITY_DEFAULT;
  if (n > INITIAL_LIQUIDITY_MAX) return String(INITIAL_LIQUIDITY_MAX);
  return String(Math.round(n * 100) / 100);
}

function isInitialLiquidityValid(raw) {
  const n = parseFloat(String(raw ?? '').trim());
  return Number.isFinite(n) && n >= INITIAL_LIQUIDITY_MIN && n <= INITIAL_LIQUIDITY_MAX;
}

const initialLiquidityInputProps = {
  type: 'number',
  min: INITIAL_LIQUIDITY_MIN,
  max: INITIAL_LIQUIDITY_MAX,
  step: 0.01,
  title: `${INITIAL_LIQUIDITY_MIN}–${INITIAL_LIQUIDITY_MAX} USDC`,
  required: true,
};

const InitialLiquidityInput = ({ value, onChange, placeholder = '0', className }) => (
  <input
    {...initialLiquidityInputProps}
    placeholder={placeholder}
    value={value}
    onChange={(e) => {
      const next = sanitizeInitialLiquidityInput(e.target.value);
      if (next !== null) onChange(next);
    }}
    onBlur={() => onChange(finalizeInitialLiquidityInput(value))}
    className={className}
  />
);

const AdminPoolModal = ({ kind, item, poolType, onClose }) => {
  const { showNotification } = useNotification();
  const [pool, setPool] = useState(0);
  const [amount, setAmount] = useState('');
  const [action, setAction] = useState('add');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const base = kind === 'match' ? 'matches' : 'polls';
  const path = poolType === 'jackpot' ? 'jackpot-pool' : 'boost-pool';
  const title = poolType === 'jackpot' ? 'Jackpot pool' : 'Boost pool';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/${base}/${item._id}/${path}`);
        if (!cancelled) setPool(data.pool || 0);
      } catch (e) {
        if (!cancelled) showNotification(e.response?.data?.message || 'Failed to load pool', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item._id, base, path, showNotification]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) {
      showNotification('Enter a valid amount', 'warning');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/${base}/${item._id}/${path}`, { action, amount: amt });
      setPool(data.freeJackpotPool ?? data.boostPool ?? data.pool ?? pool);
      showNotification('Pool updated', 'success');
      onClose(true);
    } catch (e) {
      showNotification(e.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={() => onClose(false)} title={`${title} — ${item.label || item.teamA || item.question || ''}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Current: {loading ? '…' : formatUsdAmount(pool)}
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={() => setAction('add')} className={`flex-1 py-2 rounded-lg border ${action === 'add' ? 'bg-blue-600 text-white border-blue-600' : ''}`}>Add</button>
          <button type="button" onClick={() => setAction('subtract')} className={`flex-1 py-2 rounded-lg border ${action === 'subtract' ? 'bg-rose-600 text-white border-rose-600' : ''}`}>Subtract</button>
        </div>
        <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="USDC amount" className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white" required />
        <div className="flex gap-2">
          <button type="button" onClick={() => onClose(false)} className="flex-1 py-2 border rounded-lg">Cancel</button>
          <button type="submit" disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">{saving ? '…' : 'Apply'}</button>
        </div>
      </form>
    </Modal>
  );
};

const Admin = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return ['matches', 'polls', 'cups', 'stages', 'store', 'blogs', 'newsletter', 'users', 'data-room', 'nft-rewards', 'settings'].includes(requested)
      ? requested
      : 'matches';
  });
  const [matches, setMatches] = useState([]);
  const [cups, setCups] = useState([]);
  const [stages, setStages] = useState([]);
  const [polls, setPolls] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const { showNotification } = useNotification();

  const requireOnChainAdmin = useCallback(async () => {
    try {
      // If the ABI/contract doesn't support admins() yet, isOnChainAdmin returns false.
      const addr = await (async () => {
        if (typeof window === 'undefined' || typeof window.ethereum === 'undefined') return null;
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        return accounts && accounts.length ? accounts[0] : null;
      })();
      if (!addr) throw new Error('Connect MetaMask to continue');

      const ok = await isOnChainAdmin(addr);
      if (!ok) {
        throw new Error(
          `Your connected wallet (${addr.slice(0, 6)}…${addr.slice(-4)}) is not an on-chain admin. ` +
            'The contract function batchAddOrderbookLiquidity is onlyAdmin. ' +
            'Switch to the deployer wallet or call setAdmin(wallet,true) from the deployer to grant admin rights.'
        );
      }
      return addr;
    } catch (e) {
      throw e;
    }
  }, []);

  useEffect(() => {
    setTablePage(1);
  }, [activeTab]);
  
  useEffect(() => {
    import('../utils/syncChainConfig').then(({ syncChainConfigFromServer }) =>
      syncChainConfigFromServer().catch((e) => console.warn('chain config sync:', e?.message || e))
    );
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'matches') {
        const response = await api.get('/admin/matches-list');
        setMatches(response.data || []);
      } else if (activeTab === 'cups') {
        const response = await api.get('/cups');
        setCups(response.data || []);
      } else if (activeTab === 'polls') {
        const response = await api.get('/admin/polls-list');
        setPolls(response.data || []);
      } else if (activeTab === 'blogs') {
        const response = await api.get('/admin/blogs');
        setBlogs(response.data || []);
      } else if (activeTab === 'settings') {
        // Settings will be handled separately
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(error.response?.data?.message || 'Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, showNotification]);

  const fetchCups = useCallback(async () => {
    try {
      const response = await api.get('/cups');
      setCups(response.data);
    } catch (error) {
      console.error('Error fetching cups:', error);
    }
  }, []);

  const fetchStages = useCallback(async (cupsData) => {
    try {
      // Fetch stages for all cups
      const allStages = [];
      for (const cup of cupsData) {
        const response = await api.get(`/cups/${cup.slug}/stages`);
        allStages.push(...response.data);
      }
      setStages(allStages);
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
  }, []);

  const fetchAllStages = useCallback(async () => {
    try {
      const response = await api.get('/stages');
      setStages(response.data);
    } catch (error) {
      console.error('Error fetching all stages:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (activeTab === 'matches' || activeTab === 'polls' || activeTab === 'stages') {
      if (activeTab === 'stages') {
        fetchAllStages();
        fetchCups();
      } else {
        // For matches and polls, fetch cups first, then stages after cups are loaded
        fetchCups().then(() => {
          // This will be handled by the cups effect
        });
      }
    }
  }, [activeTab, fetchData, fetchAllStages, fetchCups]);

  // Separate effect to fetch stages when cups change (for matches/polls tabs)
  useEffect(() => {
    if ((activeTab === 'matches' || activeTab === 'polls') && cups.length > 0) {
      fetchStages(cups);
    }
  }, [cups, activeTab, fetchStages]);

  const handleCreateStage = async (stageData) => {
    try {
      await api.post('/admin/stages', stageData);
      fetchAllStages();
      showNotification('Stage created successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to create stage', 'error');
    }
  };

  const handleUpdateStage = async (stageId, updates) => {
    try {
      await api.put(`/stages/${stageId}`, updates);
      fetchAllStages();
      showNotification('Stage updated successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update stage', 'error');
    }
  };

  const handleDeleteStage = async (stageId) => {
    try {
      await api.delete(`/stages/${stageId}`);
      fetchAllStages();
      showNotification('Stage deleted successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to delete stage', 'error');
    }
  };

  const handleCreateMatch = async (matchData) => {
    try {
      const { syncChainConfigFromServer } = await import('../utils/syncChainConfig');
      const { assertUsdcMatchesContract } = await import('../utils/blockchain');
      await syncChainConfigFromServer();
      await assertUsdcMatchesContract();
      // Orderbook seed liquidity is on-chain onlyAdmin
      await requireOnChainAdmin();

      // Create market on blockchain first (auto-connects wallet and switches network if needed)
      const options =
        matchData.drawEnabled === false ? ['TeamA', 'TeamB'] : ['TeamA', 'Draw', 'TeamB'];
      const marketId = await createMarket(false, options);
      const marketIdNum = parseInt(marketId, 10);
      showNotification(`Market created on blockchain! Market ID: ${marketIdNum}`, 'success');
      
      // Wait a moment to ensure the market is fully created on blockchain
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Add initial YES/NO liquidity (batched) if provided
      const seedRows = Array.isArray(matchData?.yesNo)
        ? matchData.yesNo
        : [
            { option: 'TeamA', yesAmount: matchData?.teamAYes || 0, noAmount: matchData?.teamANo || 0 },
            { option: 'TeamB', yesAmount: matchData?.teamBYes || 0, noAmount: matchData?.teamBNo || 0 },
            { option: 'Draw', yesAmount: matchData?.drawYes || 0, noAmount: matchData?.drawNo || 0 },
          ];
      const rows = seedRows
        .map((r) => ({
          option: String(r?.option || '').trim(),
          yesAmount: parseFloat(r?.yesAmount) || 0,
          noAmount: parseFloat(r?.noAmount) || 0,
        }))
        .filter((r) => r.option && (r.yesAmount > 0 || r.noAmount > 0))
        .filter((r) => matchData.drawEnabled !== false || r.option !== 'Draw');

      if (rows.length > 0) {
        let total = 0n;
        for (const r of rows) total += usdcToUnits(r.yesAmount) + usdcToUnits(r.noAmount);
        if (total > 0n) await ensureUsdcAllowance(total);
        await batchAddOrderbookLiquidity(marketIdNum, rows);
        showNotification('Initial YES/NO liquidity added on blockchain!', 'success');
      }
      
      // Create match in backend with marketId
      await api.post('/admin/matches', {
        ...matchData,
        marketId: marketIdNum,
        marketInitialized: rows.length > 0,
      });
      
      fetchData();
      showNotification('Match created successfully on blockchain and backend!', 'success');
    } catch (error) {
      console.error('Error creating match:', error);
      showNotification(getBlockchainErrorMessage(error) || error.message || 'Failed to create match', 'error');
    }
  };

  const handleUpdateMatch = async (matchId, updates) => {
    try {
      await api.put(`/admin/matches/${matchId}`, updates);
      fetchData();
      showNotification('Match updated successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update match', 'error');
    }
  };

  const handleDeleteMatch = async (matchId) => {
    try {
      await api.delete(`/admin/matches/${matchId}`);
      fetchData();
      showNotification('Match deleted successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to delete match', 'error');
    }
  };

  const handleAddMatchLiquidity = async (matchId, liquidity) => {
    try {
      // Auto-connects wallet and switches network if needed
      // Get match to get marketId
      const matchResponse = await api.get(`/matches/${matchId}`);
      const match = matchResponse.data;
      
      if (!match.marketId) {
        showNotification('Market not created on blockchain yet', 'error');
        return;
      }
      
      // Add liquidity on blockchain first
      try {
        const rows = [
          { option: 'TeamA', yesAmount: liquidity.teamAYes || 0, noAmount: liquidity.teamANo || 0 },
          { option: 'TeamB', yesAmount: liquidity.teamBYes || 0, noAmount: liquidity.teamBNo || 0 },
          { option: 'Draw', yesAmount: liquidity.drawYes || 0, noAmount: liquidity.drawNo || 0 },
        ].filter((r) => (parseFloat(r.yesAmount) || 0) + (parseFloat(r.noAmount) || 0) > 0);

        if (rows.length > 0) {
          let total = 0n;
          for (const r of rows) {
            total += usdcToUnits(r.yesAmount) + usdcToUnits(r.noAmount);
          }
          if (total > 0n) await ensureUsdcAllowance(total);
          await batchAddOrderbookLiquidity(match.marketId, rows);
        }

        const legacy = {
          teamALiquidity:
            (parseFloat(liquidity.teamAYes) || 0) +
            (parseFloat(liquidity.teamANo) || 0) +
            (parseFloat(liquidity.teamALiquidity) || 0),
          teamBLiquidity:
            (parseFloat(liquidity.teamBYes) || 0) +
            (parseFloat(liquidity.teamBNo) || 0) +
            (parseFloat(liquidity.teamBLiquidity) || 0),
          drawLiquidity:
            (parseFloat(liquidity.drawYes) || 0) +
            (parseFloat(liquidity.drawNo) || 0) +
            (parseFloat(liquidity.drawLiquidity) || 0),
          yesNo: rows,
        };

        showNotification('Liquidity added on blockchain!', 'success');
        
        // Update backend only after blockchain success
        await api.post(`/admin/matches/${matchId}/liquidity`, legacy);
      } catch (blockchainError) {
        console.error('Blockchain transaction failed:', blockchainError);
        showNotification(blockchainError.message || 'Blockchain transaction failed. Please try again.', 'error');
        throw blockchainError; // Re-throw to prevent backend call
      }
      fetchData();
      showNotification('Liquidity added successfully!', 'success');
    } catch (error) {
      console.error('Error adding liquidity:', error);
      showNotification(error.message || 'Failed to add liquidity', 'error');
    }
  };

  const handleResolveMatch = async (matchId, result) => {
    try {
      const matchResponse = await api.get(`/matches/${matchId}`);
      const match = matchResponse.data;

      if (!match.marketId) {
        showNotification('Market not created on blockchain yet', 'error');
        return;
      }

      let winningOption = result;
      if (result === 'TeamA' || result.toLowerCase() === 'teama') {
        winningOption = 'TeamA';
      } else if (result === 'TeamB' || result.toLowerCase() === 'teamb') {
        winningOption = 'TeamB';
      } else if (result === 'Draw' || result.toLowerCase() === 'draw') {
        winningOption = 'Draw';
      }

      if (match.isResolved) {
        await postAdminResolveWithRetry(`/admin/matches/${matchId}/resolve`, { result, reResolve: true });
        fetchData();
        showNotification('Match result updated in the platform.', 'success');
        return;
      }

      try {
        const txHash = await resolveMarket(match.marketId, winningOption);
        showNotification(`Match resolved on blockchain! TX: ${txHash.slice(0, 10)}...`, 'success');
      } catch (blockchainError) {
        if (!isAlreadyResolvedOnChain(blockchainError)) {
          console.error('Blockchain transaction failed:', blockchainError);
          showNotification(blockchainError.message || 'Blockchain transaction failed. Please try again.', 'error');
          throw blockchainError;
        }
        showNotification('Market already resolved on-chain; syncing platform...', 'info');
      }

      await postAdminResolveWithRetry(`/admin/matches/${matchId}/resolve`, { result });
      fetchData();
      showNotification('Match resolved successfully!', 'success');
    } catch (error) {
      console.error('Error resolving match:', error);
      showNotification(error.response?.data?.message || error.message || 'Failed to resolve match', 'error');
      throw error;
    }
  };

  const handleCreatePoll = async (pollData) => {
    try {
      const { syncChainConfigFromServer } = await import('../utils/syncChainConfig');
      const { assertUsdcMatchesContract } = await import('../utils/blockchain');
      await syncChainConfigFromServer();
      await assertUsdcMatchesContract();
      // Orderbook seed liquidity is on-chain onlyAdmin
      await requireOnChainAdmin();

      // Auto-connects wallet and switches network if needed
      // Create market on blockchain first
      // Polls are ALWAYS option-based now (no default YES/NO poll)
      const options = (pollData?.options || []).map((opt) => String(opt?.text || '').trim()).filter(Boolean);
      if (options.length === 0) {
        showNotification('Add at least 1 poll option', 'warning');
        return;
      }
      
      const marketId = await createMarket(true, options);
      const marketIdNum = parseInt(marketId, 10);
      showNotification(`Market created on blockchain! Market ID: ${marketIdNum}`, 'success');
      
      // Wait a moment to ensure the market is fully created on blockchain
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Add initial YES/NO liquidity per option (batched)
      const rows = (pollData?.options || [])
        .map((opt) => ({
          option: String(opt?.text || '').trim(),
          yesAmount: parseFloat(opt?.yesLiquidity) || 0,
          noAmount: parseFloat(opt?.noLiquidity) || 0,
        }))
        .filter((r) => r.option && (r.yesAmount > 0 || r.noAmount > 0));

      if (rows.length > 0) {
        let total = 0n;
        for (const r of rows) total += usdcToUnits(r.yesAmount) + usdcToUnits(r.noAmount);
        if (total > 0n) await ensureUsdcAllowance(total);
        await batchAddOrderbookLiquidity(marketIdNum, rows);
        showNotification('Initial YES/NO liquidity added on blockchain!', 'success');
      }
      
      // Create poll in backend with marketId
      await api.post('/admin/polls', {
        ...pollData,
        optionType: 'options',
        marketId: marketIdNum,
        marketInitialized: rows.length > 0,
      });
      
      fetchData();
      showNotification('Poll created successfully on blockchain and backend!', 'success');
    } catch (error) {
      console.error('Error creating poll:', error);
      showNotification(getBlockchainErrorMessage(error) || error.message || 'Failed to create poll', 'error');
    }
  };

  const handleResolvePoll = async (pollId, result, optionIndex) => {
    try {
      const pollResponse = await api.get(`/polls/${pollId}`);
      const poll = pollResponse.data;

      if (!poll.marketId) {
        showNotification('Market not created on blockchain yet', 'error');
        return;
      }

      let winningOption = result;
      if (poll.optionType === 'options' && optionIndex !== undefined) {
        winningOption = poll.options[optionIndex].text;
      } else if (result) {
        winningOption = result.toUpperCase();
      }

      const payload = optionIndex !== undefined ? { optionIndex } : { result };

      if (poll.isResolved) {
        await postAdminResolveWithRetry(`/admin/polls/${pollId}/resolve`, { ...payload, reResolve: true });
        fetchData();
        showNotification('Poll result updated in the platform.', 'success');
        return;
      }

      try {
        const txHash = await resolveMarket(poll.marketId, winningOption);
        showNotification(`Poll resolved on blockchain! TX: ${txHash.slice(0, 10)}...`, 'success');
      } catch (blockchainError) {
        if (!isAlreadyResolvedOnChain(blockchainError)) {
          console.error('Blockchain transaction failed:', blockchainError);
          showNotification(blockchainError.message || 'Blockchain transaction failed. Please try again.', 'error');
          throw blockchainError;
        }
        showNotification('Market already resolved on-chain; syncing platform...', 'info');
      }

      await postAdminResolveWithRetry(`/admin/polls/${pollId}/resolve`, payload);
      fetchData();
      showNotification('Poll resolved successfully!', 'success');
    } catch (error) {
      console.error('Error resolving poll:', error);
      showNotification(error.response?.data?.message || error.message || 'Failed to resolve poll', 'error');
      throw error;
    }
  };

  const handleUpdatePoll = async (pollId, updates) => {
    try {
      await api.put(`/admin/polls/${pollId}`, updates);
      fetchData();
      showNotification('Poll updated successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update poll', 'error');
    }
  };

  const handleUpdatePollStatus = async (pollId, updates) => {
    try {
      await api.post(`/admin/polls/${pollId}/status`, updates);
      fetchData();
      showNotification('Poll status updated successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update poll status', 'error');
    }
  };

  const handleAddPollLiquidity = async (pollId, liquidity) => {
    try {
      // Auto-connects wallet and switches network if needed
      // Get poll to get marketId
      const pollResponse = await api.get(`/polls/${pollId}`);
      const poll = pollResponse.data;
      
      if (!poll.marketId) {
        showNotification('Market not created on blockchain yet', 'error');
        return;
      }
      
      // Add liquidity on blockchain first
      try {
        if (poll.optionType === 'options') {
          if (Array.isArray(liquidity.options) && liquidity.options.length > 0) {
            for (const opt of liquidity.options) {
              const amount = parseFloat(opt?.liquidity) || 0;
              const text = String(opt?.text || '').trim();
              if (amount > 0 && text) {
                await addLiquidity(poll.marketId, text, amount);
              }
            }
          } else if (liquidity.optionIndex !== undefined && liquidity.optionIndex !== '') {
            const idx = parseInt(liquidity.optionIndex, 10);
            const optionText = poll.options && poll.options[idx] ? String(poll.options[idx].text || '').trim() : '';
            if (!optionText) {
              showNotification('Invalid option selected for this poll', 'error');
              return;
            }
            const y = parseFloat(liquidity.optionYes) || 0;
            const n = parseFloat(liquidity.optionNo) || 0;
            const legacy = parseFloat(liquidity.optionLiquidity) || 0;
            const useLegacySplit = y <= 0 && n <= 0 && legacy > 0;
            const yesAmt = useLegacySplit ? legacy / 2 : y;
            const noAmt = useLegacySplit ? legacy / 2 : n;
            const rows = [{ option: optionText, yesAmount: yesAmt, noAmount: noAmt }].filter(
              (r) => (parseFloat(r.yesAmount) || 0) + (parseFloat(r.noAmount) || 0) > 0
            );
            if (rows.length === 0) {
              showNotification('Enter YES and/or NO liquidity', 'warning');
              return;
            }
            let total = 0n;
            for (const r of rows) {
              total += usdcToUnits(r.yesAmount) + usdcToUnits(r.noAmount);
            }
            if (total > 0n) await ensureUsdcAllowance(total);
            await batchAddOrderbookLiquidity(poll.marketId, rows);
          } else {
            showNotification('Please select an option and enter liquidity', 'warning');
            return;
          }
        } else {
          // Normal Yes/No poll
          if (liquidity.yesLiquidity > 0) {
            await addLiquidity(poll.marketId, 'YES', liquidity.yesLiquidity);
          }
          if (liquidity.noLiquidity > 0) {
            await addLiquidity(poll.marketId, 'NO', liquidity.noLiquidity);
          }
        }
        
        showNotification('Liquidity added on blockchain!', 'success');
        
        // Update backend only after blockchain success
        await api.post(`/admin/polls/${pollId}/liquidity`, liquidity);
        fetchData();
        showNotification('Liquidity added successfully!', 'success');
      } catch (blockchainError) {
        console.error('Blockchain transaction failed:', blockchainError);
        showNotification(blockchainError.message || 'Blockchain transaction failed. Please try again.', 'error');
        throw blockchainError; // Re-throw to prevent backend call
      }
    } catch (error) {
      console.error('Error adding liquidity:', error);
      showNotification(error.message || 'Failed to add liquidity', 'error');
    }
  };

  const handleDeletePoll = async (pollId) => {
    try {
      await api.delete(`/admin/polls/${pollId}`);
      fetchData();
      showNotification('Poll deleted successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to delete poll', 'error');
    }
  };

  const handleCreateCup = async (cupData) => {
    try {
      await api.post('/admin/cups', cupData);
      fetchData();
      showNotification('Cup created successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to create cup', 'error');
    }
  };

  const handleUpdateCup = async (cupId, updates) => {
    try {
      await api.put(`/admin/cups/${cupId}`, updates);
      fetchData();
      showNotification('Cup updated successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update cup', 'error');
    }
  };

  const handleDeleteCup = async (cupId) => {
    try {
      await api.delete(`/admin/cups/${cupId}`);
      fetchData();
      showNotification('Cup deleted successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to delete cup', 'error');
    }
  };

  const handleUpdateNavbarOrder = async (cupOrders) => {
    try {
      await api.post('/admin/cups/navbar-order', { cupOrders });
      fetchData();
      showNotification('Navbar order updated successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update navbar order', 'error');
    }
  };

  const handleCreateBlog = async (blogData) => {
    try {
      await api.post('/admin/blogs', blogData);
      fetchData();
      showNotification('Blog created successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to create blog', 'error');
    }
  };

  const handleUpdateBlog = async (blogId, updates) => {
    try {
      await api.put(`/admin/blogs/${blogId}`, updates);
      fetchData();
      showNotification('Blog updated successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update blog', 'error');
    }
  };

  const handleDeleteBlog = async (blogId) => {
    try {
      await api.delete(`/admin/blogs/${blogId}`);
      fetchData();
      showNotification('Blog deleted successfully!', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to delete blog', 'error');
    }
  };

  const handleUpdateStatus = async (matchId, updates) => {
    try {
      // Auto-connects wallet and switches network if needed
      // Get match to get marketId
      const matchResponse = await api.get(`/matches/${matchId}`);
      const match = matchResponse.data;
      
      if (!match.marketId) {
        showNotification('Market not created on blockchain yet', 'error');
        return;
      }
      
      // Map status to blockchain enum (0=Upcoming, 1=Active, 2=Locked, 3=Resolved)
      let statusValue = 0;
      if (updates.status === 'active') statusValue = 1;
      else if (updates.status === 'locked') statusValue = 2;
      else if (updates.status === 'resolved' || updates.status === 'completed') statusValue = 3;
      
      // Update status on blockchain first
      try {
        const txHash = await updateMarketStatus(match.marketId, statusValue);
        showNotification(`Status updated on blockchain! TX: ${txHash.slice(0, 10)}...`, 'success');
        
        // Update in backend only after blockchain success
        await api.post(`/admin/matches/${matchId}/status`, updates);
        fetchData();
        showNotification('Status updated successfully!', 'success');
      } catch (blockchainError) {
        console.error('Blockchain transaction failed:', blockchainError);
        showNotification(blockchainError.message || 'Blockchain transaction failed. Please try again.', 'error');
        throw blockchainError; // Re-throw to prevent backend call
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification(error.message || 'Failed to update status', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
          Admin Dashboard
        </h1>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {['matches', 'polls', 'cups', 'stages', 'store', 'blogs', 'newsletter', 'users', 'data-room', 'nft-rewards', 'settings'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab === 'data-room'
                  ? 'Data Room'
                  : tab === 'nft-rewards'
                    ? 'NFT Rewards'
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'matches' && (
          <MatchesTab
            matches={matches}
            cups={cups}
            stages={stages}
            loading={loading}
            tablePage={tablePage}
            setTablePage={setTablePage}
            itemsPerPage={ITEMS_PER_PAGE}
            onCreateMatch={handleCreateMatch}
            onUpdateMatch={handleUpdateMatch}
            onResolveMatch={handleResolveMatch}
            onUpdateStatus={handleUpdateStatus}
            onDeleteMatch={handleDeleteMatch}
            onAddLiquidity={handleAddMatchLiquidity}
          />
        )}
        {activeTab === 'polls' && (
          <PollsTab
            polls={polls}
            cups={cups}
            stages={stages}
            loading={loading}
            tablePage={tablePage}
            setTablePage={setTablePage}
            itemsPerPage={ITEMS_PER_PAGE}
            onCreatePoll={handleCreatePoll}
            onResolvePoll={handleResolvePoll}
            onUpdatePoll={handleUpdatePoll}
            onUpdatePollStatus={handleUpdatePollStatus}
            onAddLiquidity={handleAddPollLiquidity}
            onDeletePoll={handleDeletePoll}
          />
        )}
        {activeTab === 'cups' && (
          <CupsTab 
            cups={cups} 
            loading={loading}
            tablePage={tablePage}
            setTablePage={setTablePage}
            itemsPerPage={ITEMS_PER_PAGE}
            onCreateCup={handleCreateCup}
            onUpdateCup={handleUpdateCup}
            onDeleteCup={handleDeleteCup}
            onUpdateNavbarOrder={handleUpdateNavbarOrder}
          />
        )}
        {activeTab === 'stages' && (
          <StagesTab
            cups={cups}
            stages={stages}
            loading={loading}
            tablePage={tablePage}
            setTablePage={setTablePage}
            itemsPerPage={ITEMS_PER_PAGE}
            onCreateStage={handleCreateStage}
            onUpdateStage={handleUpdateStage}
            onDeleteStage={handleDeleteStage}
          />
        )}
        {activeTab === 'blogs' && (
          <BlogsTab
            blogs={blogs}
            loading={loading}
            tablePage={tablePage}
            setTablePage={setTablePage}
            itemsPerPage={ITEMS_PER_PAGE}
            onCreateBlog={handleCreateBlog}
            onUpdateBlog={handleUpdateBlog}
            onDeleteBlog={handleDeleteBlog}
          />
        )}
        {activeTab === 'newsletter' && <NewsletterTab />}
        {activeTab === 'store' && <StoreAdminTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'data-room' && <DataRoomTab apiBase="admin" />}
        {activeTab === 'nft-rewards' && <AdminNftRewardsTab />}
        {activeTab === 'settings' && (
          <SettingsTab />
        )}
      </div>
    </div>
  );
};

const MatchesTab = ({ matches, cups, stages, loading, tablePage, setTablePage, itemsPerPage, onCreateMatch, onUpdateMatch, onResolveMatch, onUpdateStatus, onDeleteMatch, onAddLiquidity }) => {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showLiquidityModal, setShowLiquidityModal] = useState(null);
  const [poolModal, setPoolModal] = useState(null);
  const [claimStatsModal, setClaimStatsModal] = useState(null);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Matches</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Create Match
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Teams</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tickets</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Boost pool</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Platform fees</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Market fees</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Result</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800">
            {(() => {
              const paginatedMatches = matches.slice((tablePage - 1) * itemsPerPage, tablePage * itemsPerPage);
              return paginatedMatches.map((match) => (
              <React.Fragment key={match._id}>
              <tr className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {match.teamA} vs {match.teamB}
                </td>
                <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {match.date ? formatEventDateGmt(match.date) : '—'}
                </td>
                <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-gray-700 dark:text-gray-300">
                  {match.totalFreeTickets ?? 0}
                </td>
                <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-purple-700 dark:text-purple-300">
                  {formatUsdAmount(match.displayBoostJackpot ?? match.boostPool ?? 0)}
                </td>
                <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-gray-700 dark:text-gray-300">
                  {formatUsdAmount(match.platformFees ?? 0)}
                </td>
                <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-gray-700 dark:text-gray-300">
                  {formatUsdAmount(match.marketPlatformFees ?? 0)}
                </td>
                <td className="px-6 pt-4 pb-1 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    match.status === 'upcoming' ? 'bg-yellow-100 text-yellow-800' :
                    match.status === 'live' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {match.status}
                  </span>
                </td>
                <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {(() => {
                    if (!match.result) return 'Pending';
                    const result = String(match.result).trim();
                    if (result === 'TeamA' || result.toLowerCase() === 'teama') {
                      return match.teamA || 'Team A';
                    }
                    if (result === 'TeamB' || result.toLowerCase() === 'teamb') {
                      return match.teamB || 'Team B';
                    }
                    if (result === 'Draw' || result.toLowerCase() === 'draw') {
                      return 'Draw';
                    }
                    // If backend already stored a display name, show it as-is
                    return result;
                  })()}
                </td>
              </tr>
              <tr>
                <td colSpan={8} className="px-6 pb-4 pt-1">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      onClick={() => setShowStatusModal(match)}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                    >
                      Status
                    </button>
                    <button
                      onClick={() => setShowEditModal(match)}
                      className="px-3 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setPoolModal({ kind: 'match', item: { ...match, label: `${match.teamA} vs ${match.teamB}` }, poolType: 'jackpot' })}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                    >
                      Jackpot
                    </button>
                    <button
                      onClick={() => setPoolModal({ kind: 'match', item: { ...match, label: `${match.teamA} vs ${match.teamB}` }, poolType: 'boost' })}
                      className="px-3 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 text-xs"
                    >
                      Boost pool
                    </button>
                    <button
                      onClick={() => setShowLiquidityModal(match)}
                      className="px-3 py-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 text-xs"
                    >
                      Add Liquidity
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/market/match/${match._id}`)}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-xs"
                    >
                      Control
                    </button>
                    <button
                      onClick={() =>
                        setClaimStatsModal({
                          kind: 'match',
                          item: { ...match, label: `${match.teamA} vs ${match.teamB}` },
                        })
                      }
                      className="px-3 py-1.5 bg-slate-600 text-white rounded hover:bg-slate-700 text-xs"
                    >
                      View claims
                    </button>
                    <button
                      onClick={() => setShowResolveModal(match)}
                      className={`px-3 py-1.5 text-white rounded text-xs ${
                        match.isResolved
                          ? 'bg-amber-500 hover:bg-amber-600'
                          : 'bg-green-500 hover:bg-green-600'
                      }`}
                    >
                      {match.isResolved ? 'Change result' : 'Resolve'}
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(match)}
                      className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
              </React.Fragment>
            ));
            })()}
          </tbody>
        </table>
        </div>
        {matches.length > itemsPerPage && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
              disabled={tablePage <= 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {tablePage} of {Math.max(1, Math.ceil(matches.length / itemsPerPage))} ({matches.length} total)
            </span>
            <button
              onClick={() => setTablePage((p) => Math.min(Math.ceil(matches.length / itemsPerPage), p + 1))}
              disabled={tablePage >= Math.ceil(matches.length / itemsPerPage)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {poolModal && (
        <AdminPoolModal
          kind={poolModal.kind}
          item={poolModal.item}
          poolType={poolModal.poolType}
          onClose={() => setPoolModal(null)}
        />
      )}

      {claimStatsModal && (
        <EventClaimStatsModal
          kind={claimStatsModal.kind}
          item={claimStatsModal.item}
          onClose={() => setClaimStatsModal(null)}
        />
      )}

      {showCreateModal && (
        <CreateMatchModal
          cups={cups}
          stages={stages}
          onClose={() => setShowCreateModal(false)}
          onSubmit={onCreateMatch}
        />
      )}

      {showResolveModal && (
        <ResolveModal
          item={showResolveModal}
          type="match"
          onClose={() => setShowResolveModal(null)}
          onSubmit={onResolveMatch}
        />
      )}

      {showStatusModal && (
        <StatusModal
          match={showStatusModal}
          onClose={() => setShowStatusModal(null)}
          onSubmit={onUpdateStatus}
        />
      )}

      {showEditModal && (
        <EditMatchModal
          match={showEditModal}
          cups={cups}
          stages={stages}
          onClose={() => setShowEditModal(null)}
          onSubmit={(updates) => {
            onUpdateMatch(showEditModal._id, updates);
            setShowEditModal(null);
          }}
        />
      )}

      {showDeleteModal && (
        <Modal isOpen={true} onClose={() => setShowDeleteModal(null)} title="Delete Match">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{showDeleteModal.teamA} vs {showDeleteModal.teamB}</strong>? This action cannot be undone.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  onDeleteMatch(showDeleteModal._id);
                  setShowDeleteModal(null);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showLiquidityModal && (
        <AddLiquidityModal
          match={showLiquidityModal}
          onClose={() => setShowLiquidityModal(null)}
          onSubmit={async (liquidity) => {
            await onAddLiquidity(showLiquidityModal._id, liquidity);
            // Modal will close itself after successful submission
          }}
        />
      )}
    </div>
  );
};

const CreateMatchModal = ({ cups, stages, onClose, onSubmit }) => {
  // USDC ~ USD; show $ directly
  const [formData, setFormData] = useState({
    teamA: '',
    teamB: '',
    description: '',
    date: '',
    cup: '',
    stage: '',
    stageName: '',
    drawEnabled: true,
    minFreeTickets: 1,
    minGoldenTickets: 0,
    freePredictionEnabled: true,
    marketEnabled: true,
    // YES/NO seed liquidity per outcome (orderbook reference liquidity)
    teamAYes: INITIAL_LIQUIDITY_DEFAULT,
    teamANo: INITIAL_LIQUIDITY_DEFAULT,
    teamBYes: INITIAL_LIQUIDITY_DEFAULT,
    teamBNo: INITIAL_LIQUIDITY_DEFAULT,
    drawYes: INITIAL_LIQUIDITY_DEFAULT,
    drawNo: INITIAL_LIQUIDITY_DEFAULT,
    isFeatured: false,
    isSponsored: false,
    sponsoredImages: [],
    lockedTime: '',
    teamAImage: '',
    teamBImage: '',
  });
  const [targetOdds, setTargetOdds] = useState(() =>
    distributeEvenlyWithBalance(
      [
        { optionKey: 'TeamA', pct: 0 },
        { optionKey: 'Draw', pct: 0 },
        { optionKey: 'TeamB', pct: 0 },
      ],
      'TeamB'
    )
  );
  const [availableStages, setAvailableStages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setTargetOdds(
      formData.drawEnabled
        ? distributeEvenlyWithBalance(
            [
              { optionKey: 'TeamA', pct: 0 },
              { optionKey: 'Draw', pct: 0 },
              { optionKey: 'TeamB', pct: 0 },
            ],
            'TeamB'
          )
        : distributeEvenlyWithBalance(
            [
              { optionKey: 'TeamA', pct: 0 },
              { optionKey: 'TeamB', pct: 0 },
            ],
            'TeamB'
          )
    );
  }, [formData.drawEnabled]);

  useEffect(() => {
    if (formData.cup) {
      // Find the cup to get its slug
      const selectedCup = cups.find(c => c._id === formData.cup || c.slug === formData.cup);
      if (selectedCup) {
        // Fetch stages for the selected cup
        api.get(`/cups/${selectedCup.slug}/stages`)
          .then(response => {
            setAvailableStages(response.data);
          })
          .catch(error => {
            console.error('Error fetching stages:', error);
            setAvailableStages([]);
          });
      } else {
        setAvailableStages([]);
      }
    } else {
      setAvailableStages([]);
    }
  }, [formData.cup, cups]);

  const matchOddsLabel = (optionKey) => {
    if (optionKey === 'TeamA') return formData.teamA || 'Team A';
    if (optionKey === 'TeamB') return formData.teamB || 'Team B';
    if (optionKey === 'Draw') return 'Draw';
    return optionKey;
  };

  const ticketReqs = normalizeFreeTicketRequirements(formData.minFreeTickets, formData.minGoldenTickets);
  const canSubmitFreeTickets = ticketReqs.valid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitFreeTickets) {
      alert('At least one ticket type (free or golden) must be required for free play.');
      return;
    }
    const liquidityFields = [
      formData.teamAYes,
      formData.teamANo,
      formData.teamBYes,
      formData.teamBNo,
      ...(formData.drawEnabled ? [formData.drawYes, formData.drawNo] : []),
    ];
    if (!liquidityFields.every(isInitialLiquidityValid)) {
      alert(`Initial liquidity must be between ${INITIAL_LIQUIDITY_MIN} and ${INITIAL_LIQUIDITY_MAX} USDC.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const selectedStage = availableStages.find(s => s._id === formData.stage);
      const activeKeys = formData.drawEnabled ? ['TeamA', 'Draw', 'TeamB'] : ['TeamA', 'TeamB'];
      const oddsRows = targetOdds.filter((r) => activeKeys.includes(r.optionKey));
      const startingPrices = startingPricesFromPctRows(
        oddsRows.length ? oddsRows : evenPctSplit(activeKeys)
      );
      await onSubmit({
        ...formData,
        date: utcDatetimeLocalToIso(formData.date),
        lockedTime: utcDatetimeLocalToIso(formData.lockedTime),
        stageName: selectedStage?.name || formData.stageName,
        minFreeTickets: ticketReqs.minFreeTickets,
        minGoldenTickets: ticketReqs.minGoldenTickets,
        freePredictionEnabled: formData.freePredictionEnabled,
        marketEnabled: formData.marketEnabled,
        drawEnabled: formData.drawEnabled,
        startingPrices,
        sponsoredImages: normalizeSponsoredImages(formData.sponsoredImages),
        teamAYes: parseFloat(formData.teamAYes) || 0,
        teamANo: parseFloat(formData.teamANo) || 0,
        teamBYes: parseFloat(formData.teamBYes) || 0,
        teamBNo: parseFloat(formData.teamBNo) || 0,
        drawYes: parseFloat(formData.drawYes) || 0,
        drawNo: parseFloat(formData.drawNo) || 0,
        // Also send canonical rows for backend storage
        yesNo: [
          { option: 'TeamA', yesAmount: parseFloat(formData.teamAYes) || 0, noAmount: parseFloat(formData.teamANo) || 0 },
          { option: 'TeamB', yesAmount: parseFloat(formData.teamBYes) || 0, noAmount: parseFloat(formData.teamBNo) || 0 },
          { option: 'Draw', yesAmount: parseFloat(formData.drawYes) || 0, noAmount: parseFloat(formData.drawNo) || 0 },
        ],
      });
      // Only close modal after successful submission
      onClose();
    } catch (error) {
      console.error('Error creating match:', error);
      // Don't close modal on error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Match" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              placeholder="Team A"
              value={formData.teamA}
              onChange={(e) => setFormData({ ...formData, teamA: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white mb-2"
              required
            />
            <ImageUpload
              label="Team A Image"
              value={formData.teamAImage}
              onChange={(url) => setFormData({ ...formData, teamAImage: url })}
              folder="wergame/teams"
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="Team B"
              value={formData.teamB}
              onChange={(e) => setFormData({ ...formData, teamB: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white mb-2"
              required
            />
            <ImageUpload
              label="Team B Image"
              value={formData.teamBImage}
              onChange={(url) => setFormData({ ...formData, teamBImage: url })}
              folder="wergame/teams"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Match date &amp; time (GMT) <span className="text-red-500">*</span>
          </label>
        <input
          type="datetime-local"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        </div>
        <textarea
          placeholder="Description (optional)"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
        />
        <select
          value={formData.cup}
          onChange={(e) => {
            setFormData({ ...formData, cup: e.target.value, stage: '', stageName: '' });
          }}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        >
          <option value="">Select Cup</option>
          {cups.map((cup) => (
            <option key={cup._id} value={cup._id}>{cup.name}</option>
          ))}
        </select>
        
        {/* Stage Selection - Always show when cup is selected */}
        {formData.cup && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Stage <span className="text-gray-500">(optional)</span>
            </label>
            {availableStages.length > 0 ? (
              <select
                value={formData.stage}
                onChange={(e) => {
                  const selectedStage = availableStages.find(s => s._id === e.target.value);
                  setFormData({ ...formData, stage: e.target.value, stageName: selectedStage?.name || '' });
                }}
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Stage (optional)</option>
                {availableStages.map((stage) => (
                  <option key={stage._id} value={stage._id}>{stage.name}</option>
                ))}
              </select>
            ) : (
              <>
                <div className="text-sm text-yellow-600 dark:text-yellow-400 mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  No stages found for this cup. Create stages in the Stages tab first.
                </div>
                <input
                  type="text"
                  placeholder="Or enter a custom stage name"
                  value={formData.stageName}
                  onChange={(e) => setFormData({ ...formData, stageName: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </>
            )}
            {formData.stage && availableStages.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Selected: {availableStages.find(s => s._id === formData.stage)?.name}
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Min free tickets</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minFreeTickets: Math.max(0, (parseInt(f.minFreeTickets, 10) || 0) - 1) }))} className="w-10 h-10 border rounded-lg font-bold">−</button>
              <span className="flex-1 text-center text-lg font-bold tabular-nums">{formData.minFreeTickets}</span>
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minFreeTickets: (parseInt(f.minFreeTickets, 10) || 0) + 1 }))} className="w-10 h-10 border rounded-lg font-bold">+</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Min golden tickets</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minGoldenTickets: Math.max(0, (parseInt(f.minGoldenTickets, 10) || 0) - 1) }))} className="w-10 h-10 border rounded-lg font-bold">−</button>
              <span className="flex-1 text-center text-lg font-bold tabular-nums">{formData.minGoldenTickets}</span>
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minGoldenTickets: (parseInt(f.minGoldenTickets, 10) || 0) + 1 }))} className="w-10 h-10 border rounded-lg font-bold">+</button>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 -mt-2">
          {formatFreeEntryRequirement(ticketReqs)} Golden tickets can cover free tickets when free is exhausted; free tickets can never cover a golden requirement. If both are set, players stake at least free + golden tickets.
        </p>
        {!canSubmitFreeTickets && (
          <p className="text-xs font-semibold text-red-600 dark:text-red-400">
            At least one ticket type must be required — free and golden cannot both be 0.
          </p>
        )}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.freePredictionEnabled} onChange={(e) => setFormData({ ...formData, freePredictionEnabled: e.target.checked })} />
              <span className="text-sm text-gray-700 dark:text-gray-300">Free prediction enabled</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.marketEnabled} onChange={(e) => setFormData({ ...formData, marketEnabled: e.target.checked })} />
              <span className="text-sm text-gray-700 dark:text-gray-300">Market enabled</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.drawEnabled} onChange={(e) => setFormData({ ...formData, drawEnabled: e.target.checked })} />
              <span className="text-sm text-gray-700 dark:text-gray-300">Draw outcome enabled</span>
            </label>
        </div>
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Target odds (%)</p>
          <TargetOddsInputs
            rows={targetOdds.filter((r) =>
              formData.drawEnabled ? true : r.optionKey !== 'Draw'
            )}
            balanceOptionKey="TeamB"
            onUpdateRows={(rows) => {
              if (formData.drawEnabled) {
                setTargetOdds(rows);
              } else {
                const drawRow = targetOdds.find((r) => r.optionKey === 'Draw');
                setTargetOdds(drawRow ? [...rows, drawRow] : rows);
              }
            }}
            getLabel={matchOddsLabel}
          />
        </div>
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Initial liquidity is per outcome and per side (YES / NO). This seeds the orderbook reference liquidity.
            Each field: {INITIAL_LIQUIDITY_MIN}–{INITIAL_LIQUIDITY_MAX} USDC (required, defaults to {INITIAL_LIQUIDITY_DEFAULT}).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border rounded-lg p-3 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">{formData.teamA || 'Team A'}</div>
              <div className="grid grid-cols-2 gap-2">
                <InitialLiquidityInput
                  placeholder="YES (USDC)"
                  value={formData.teamAYes}
                  onChange={(teamAYes) => setFormData({ ...formData, teamAYes })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
                <InitialLiquidityInput
                  placeholder="NO (USDC)"
                  value={formData.teamANo}
                  onChange={(teamANo) => setFormData({ ...formData, teamANo })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                Total: {formatUsdAmount((parseFloat(formData.teamAYes) || 0) + (parseFloat(formData.teamANo) || 0))}
              </p>
            </div>
            {formData.drawEnabled && (
            <div className="border rounded-lg p-3 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Draw</div>
              <div className="grid grid-cols-2 gap-2">
                <InitialLiquidityInput
                  placeholder="YES (USDC)"
                  value={formData.drawYes}
                  onChange={(drawYes) => setFormData({ ...formData, drawYes })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
                <InitialLiquidityInput
                  placeholder="NO (USDC)"
                  value={formData.drawNo}
                  onChange={(drawNo) => setFormData({ ...formData, drawNo })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                Total: {formatUsdAmount((parseFloat(formData.drawYes) || 0) + (parseFloat(formData.drawNo) || 0))}
              </p>
            </div>
            )}
            <div className="border rounded-lg p-3 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">{formData.teamB || 'Team B'}</div>
              <div className="grid grid-cols-2 gap-2">
                <InitialLiquidityInput
                  placeholder="YES (USDC)"
                  value={formData.teamBYes}
                  onChange={(teamBYes) => setFormData({ ...formData, teamBYes })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
                <InitialLiquidityInput
                  placeholder="NO (USDC)"
                  value={formData.teamBNo}
                  onChange={(teamBNo) => setFormData({ ...formData, teamBNo })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                Total: {formatUsdAmount((parseFloat(formData.teamBYes) || 0) + (parseFloat(formData.teamBNo) || 0))}
              </p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Featured Match</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isSponsored}
              onChange={(e) => setFormData({ ...formData, isSponsored: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Sponsored Match</span>
          </label>
        </div>
        
        {/* Sponsored Images - Show when sponsored is checked */}
        {formData.isSponsored && (
          <SponsoredImagesEditor
            images={formData.sponsoredImages}
            onChange={(sponsoredImages) => setFormData({ ...formData, sponsoredImages })}
          />
        )}

        {/* Locked Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Locked Time (GMT, optional) - When predictions should be locked
          </label>
          <input
            type="datetime-local"
            value={formData.lockedTime}
            onChange={(e) => setFormData({ ...formData, lockedTime: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="flex space-x-2">
          <button
            type="submit"
            disabled={isSubmitting || !canSubmitFreeTickets}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

const ResolveModal = ({ item, type, onClose, onSubmit }) => {
  const initialMatchResult = () => {
    if (type !== 'match' || !item?.result) return '';
    const r = String(item.result).toLowerCase();
    if (r === 'teama') return item.teamA;
    if (r === 'teamb') return item.teamB;
    if (r === 'draw') return 'Draw';
    return item.result;
  };

  const initialPollOptionIndex = () => {
    if (type !== 'poll' || item?.optionType !== 'options' || !item?.result || !item?.options?.length) {
      return '';
    }
    const idx = item.options.findIndex(
      (opt) => String(opt.text || '').trim() === String(item.result || '').trim()
    );
    return idx >= 0 ? String(idx) : '';
  };

  const initialPollYesNo = () => {
    if (type !== 'poll' || item?.optionType === 'options' || !item?.result) return '';
    return String(item.result).toUpperCase();
  };

  const [result, setResult] = useState(() => initialMatchResult() || initialPollYesNo());
  const [optionIndex, setOptionIndex] = useState(() => initialPollOptionIndex());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReResolve = !!item?.isResolved;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Map the display value to the backend expected value
      let backendResult = result;
      if (type === 'match') {
        if (result === item.teamA) {
          backendResult = 'teamA';
        } else if (result === item.teamB) {
          backendResult = 'teamB';
        } else if (result === 'Draw') {
          backendResult = 'draw';
        }
        await onSubmit(item._id, backendResult);
      } else {
        // For polls
        if (item.optionType === 'options') {
          // Option-based poll - send optionIndex
          await onSubmit(item._id, null, parseInt(optionIndex));
        } else {
          // Normal Yes/No poll
          backendResult = result.toUpperCase();
          await onSubmit(item._id, backendResult);
        }
      }
      // Only close modal after successful submission
      onClose();
    } catch (error) {
      // Error already handled in onSubmit, just don't close modal
      console.error('Error in resolve modal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const options = type === 'match' 
    ? [item.teamA, 'Draw', item.teamB]
    : (item.optionType === 'options' && item.options) 
      ? item.options.map((opt, idx) => ({ text: opt.text, index: idx, image: opt.image }))
      : ['YES', 'NO'];

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isReResolve ? `Change ${type === 'match' ? 'Match' : 'Poll'} Result` : `Resolve ${type === 'match' ? 'Match' : 'Poll'}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isReResolve && (
          <p className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
            This {type === 'match' ? 'match' : 'poll'} is already resolved
            {item.result ? ` as “${item.result}”` : ''}. Pick a new outcome to update payouts in the platform database.
            On-chain resolution cannot be changed.
          </p>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Result
          </label>
          {type === 'poll' && item.optionType === 'options' ? (
            <div className="space-y-2">
              {options.map((opt) => (
                <label
                  key={opt.index}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    optionIndex === opt.index.toString() ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="pollOption"
                    value={opt.index}
                    checked={optionIndex === opt.index.toString()}
                    onChange={(e) => setOptionIndex(e.target.value)}
                    className="mr-3"
                    required
                  />
                  {opt.image && (
                    <img src={opt.image} alt={opt.text} className="w-10 h-10 object-cover rounded-full mr-3" />
                  )}
                  <span className="text-gray-900 dark:text-white font-medium">{opt.text}</span>
                </label>
              ))}
            </div>
          ) : (
            <select
              value={type === 'poll' && item.optionType === 'options' ? optionIndex : result}
              onChange={(e) => {
                if (type === 'poll' && item.optionType === 'options') {
                  setOptionIndex(e.target.value);
                } else {
                  setResult(e.target.value);
                }
              }}
              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="">Select result</option>
              {options.map((opt) => (
                <option key={typeof opt === 'string' ? opt : opt.index} value={typeof opt === 'string' ? opt : opt.index}>
                  {typeof opt === 'string' ? opt : opt.text}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : isReResolve ? 'Update result' : 'Resolve'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

const StatusModal = ({ match, onClose, onSubmit }) => {
  const [status, setStatus] = useState(match.status);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updates = { status };
    // If status is set to locked, immediately lock (override any locked time)
    if (status === 'locked') {
      updates.lockedTime = new Date().toISOString();
    }
    onSubmit(match._id, updates);
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Update Match Status">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
            required
          >
            <option value="upcoming">Upcoming</option>
            <option value="live">Live</option>
            <option value="locked">Locked</option>
            <option value="completed">Completed</option>
          </select>
          {status === 'locked' && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              Setting status to "Locked" will immediately lock predictions for this match.
            </p>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Update
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

const PollsTab = ({ polls, cups, stages, loading, tablePage, setTablePage, itemsPerPage, onCreatePoll, onResolvePoll, onUpdatePoll, onUpdatePollStatus, onAddLiquidity, onDeletePoll }) => {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(null);
  const [showLiquidityModal, setShowLiquidityModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [poolModal, setPoolModal] = useState(null);
  const [claimStatsModal, setClaimStatsModal] = useState(null);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Polls</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Create Poll
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Question</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date (GMT)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tickets</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Boost pool</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Platform fees</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Market fees</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Result</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800">
            {(() => {
              const paginatedPolls = polls.slice((tablePage - 1) * itemsPerPage, tablePage * itemsPerPage);
              return polls.length > 0 ? (
              paginatedPolls.map((poll) => (
                <React.Fragment key={poll._id}>
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {poll.question}
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {poll.date ? formatEventDateGmt(poll.date) : '—'}
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {poll.type}
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-gray-700 dark:text-gray-300">
                    {poll.totalFreeTickets ?? 0}
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-purple-700 dark:text-purple-300">
                    {formatUsdAmount(poll.displayBoostJackpot ?? poll.boostPool ?? 0)}
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-gray-700 dark:text-gray-300">
                    {formatUsdAmount(poll.platformFees ?? 0)}
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm tabular-nums text-gray-700 dark:text-gray-300">
                    {formatUsdAmount(poll.marketPlatformFees ?? 0)}
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      poll.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      poll.status === 'settled' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {poll.status}
                    </span>
                  </td>
                  <td className="px-6 pt-4 pb-1 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {poll.result || 'Pending'}
                  </td>
                </tr>
                <tr>
                  <td colSpan={9} className="px-6 pb-4 pt-1">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        onClick={() => setShowStatusModal(poll)}
                        className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                      >
                        Status
                      </button>
                      <button
                        onClick={() => setShowEditModal(poll)}
                        className="px-3 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-xs"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setPoolModal({ kind: 'poll', item: { ...poll, label: poll.question }, poolType: 'jackpot' })}
                        className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs"
                      >
                        Jackpot
                      </button>
                      <button
                        onClick={() => setPoolModal({ kind: 'poll', item: { ...poll, label: poll.question }, poolType: 'boost' })}
                        className="px-3 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 text-xs"
                      >
                        Boost pool
                      </button>
                      <button
                        onClick={() => setShowLiquidityModal(poll)}
                        className="px-3 py-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 text-xs"
                      >
                        Add Liquidity
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/market/poll/${poll._id}`)}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-xs"
                      >
                        Control
                      </button>
                      <button
                        onClick={() =>
                          setClaimStatsModal({
                            kind: 'poll',
                            item: { ...poll, label: poll.question },
                          })
                        }
                        className="px-3 py-1.5 bg-slate-600 text-white rounded hover:bg-slate-700 text-xs"
                      >
                        View claims
                      </button>
                      <button
                        onClick={() => setShowResolveModal(poll)}
                        className={`px-3 py-1.5 text-white rounded text-xs ${
                          poll.isResolved
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : 'bg-green-500 hover:bg-green-600'
                        }`}
                      >
                        {poll.isResolved ? 'Change result' : 'Resolve'}
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(poll)}
                        className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No polls found. Create one to get started!
                </td>
              </tr>
            );
            })()}
          </tbody>
        </table>
        </div>
        {polls.length > itemsPerPage && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
              disabled={tablePage <= 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {tablePage} of {Math.max(1, Math.ceil(polls.length / itemsPerPage))} ({polls.length} total)
            </span>
            <button
              onClick={() => setTablePage((p) => Math.min(Math.ceil(polls.length / itemsPerPage), p + 1))}
              disabled={tablePage >= Math.ceil(polls.length / itemsPerPage)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
      
      {poolModal && (
        <AdminPoolModal
          kind={poolModal.kind}
          item={poolModal.item}
          poolType={poolModal.poolType}
          onClose={() => setPoolModal(null)}
        />
      )}

      {claimStatsModal && (
        <EventClaimStatsModal
          kind={claimStatsModal.kind}
          item={claimStatsModal.item}
          onClose={() => setClaimStatsModal(null)}
        />
      )}

      {showCreateModal && (
        <CreatePollModal
          cups={cups}
          stages={stages}
          onClose={() => setShowCreateModal(false)}
          onSubmit={onCreatePoll}
        />
      )}
      {showResolveModal && (
        <ResolveModal
          item={showResolveModal}
          type="poll"
          onClose={() => setShowResolveModal(null)}
          onSubmit={onResolvePoll}
        />
      )}

      {showEditModal && (
        <EditPollModal
          poll={showEditModal}
          cups={cups}
          onClose={() => setShowEditModal(null)}
          onSubmit={(updates) => {
            onUpdatePoll(showEditModal._id, updates);
            setShowEditModal(null);
          }}
        />
      )}

      {showStatusModal && (
        <PollStatusModal
          poll={showStatusModal}
          onClose={() => setShowStatusModal(null)}
          onSubmit={onUpdatePollStatus}
        />
      )}

      {showLiquidityModal && (
        <AddPollLiquidityModal
          poll={showLiquidityModal}
          onClose={() => setShowLiquidityModal(null)}
          onSubmit={async (liquidity) => {
            await onAddLiquidity(showLiquidityModal._id, liquidity);
            // Modal will close itself after successful submission
          }}
        />
      )}

      {showDeleteModal && (
        <Modal isOpen={true} onClose={() => setShowDeleteModal(null)} title="Delete Poll">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{showDeleteModal.question}</strong>? This action cannot be undone.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  onDeletePoll(showDeleteModal._id);
                  setShowDeleteModal(null);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const CreatePollModal = ({ cups, stages, onClose, onSubmit }) => {
  // USDC ~ USD; no price API needed
  const [formData, setFormData] = useState({
    question: '',
    thumbnailImage: '',
    description: '',
    type: 'match',
    cup: '',
    optionType: 'options',
    minFreeTickets: 1,
    minGoldenTickets: 0,
    freePredictionEnabled: true,
    marketEnabled: true,
    isFeatured: false,
    isSponsored: false,
    sponsoredImages: [],
    date: '',
    lockedTime: '',
    options: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addOption = () => {
    setFormData((prev) => {
      const newOptions = [
        ...prev.options,
        { text: '', image: '', yesLiquidity: INITIAL_LIQUIDITY_DEFAULT, noLiquidity: INITIAL_LIQUIDITY_DEFAULT, targetPct: 0, quoteVolumeUsdc: DEFAULT_QUOTE_VOLUME_USDC },
      ];
      const keys = newOptions.map((_, i) => `__idx_${i}`);
      const balanceKey = keys[keys.length - 1];
      const split = distributeEvenlyWithBalance(
        keys.map((optionKey) => ({ optionKey, pct: 0 })),
        balanceKey
      );
      return {
        ...prev,
        options: newOptions.map((opt, i) => ({
          ...opt,
          targetPct: split[i]?.pct ?? 100 / newOptions.length,
        })),
      };
    });
  };

  const removeOption = (index) => {
    setFormData((prev) => {
      const newOptions = prev.options.filter((_, i) => i !== index);
      if (!newOptions.length) return { ...prev, options: [] };
      const keys = newOptions.map((_, i) => `__idx_${i}`);
      const balanceKey = keys[keys.length - 1];
      const split = distributeEvenlyWithBalance(
        keys.map((optionKey) => ({ optionKey, pct: 0 })),
        balanceKey
      );
      return {
        ...prev,
        options: newOptions.map((opt, i) => ({
          ...opt,
          targetPct: split[i]?.pct ?? 100 / newOptions.length,
        })),
      };
    });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  const pollOddsRows = formData.options.map((opt, i) => ({
    optionKey: `__idx_${i}`,
    pct: Number(opt.targetPct) || 0,
    quoteVolumeUsdc: Number(opt.quoteVolumeUsdc) || DEFAULT_QUOTE_VOLUME_USDC,
  }));

  const updatePollOdds = (rows) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) => {
        const row = rows.find((r) => r.optionKey === `__idx_${i}`);
        return {
          ...opt,
          targetPct: row?.pct ?? opt.targetPct,
          quoteVolumeUsdc: row?.quoteVolumeUsdc ?? opt.quoteVolumeUsdc ?? DEFAULT_QUOTE_VOLUME_USDC,
        };
      }),
    }));
  };

  const pollOddsLabel = (key) => {
    const i = parseInt(String(key).replace('__idx_', ''), 10);
    const opt = formData.options[i];
    return opt?.text?.trim() || `Option ${i + 1}`;
  };

  const ticketReqs = normalizeFreeTicketRequirements(formData.minFreeTickets, formData.minGoldenTickets);
  const canSubmitFreeTickets = ticketReqs.valid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitFreeTickets) {
      alert('At least one ticket type (free or golden) must be required for free play.');
      return;
    }
    const liquidityFields = formData.options.flatMap((opt) => [opt.yesLiquidity, opt.noLiquidity]);
    if (!liquidityFields.every(isInitialLiquidityValid)) {
      alert(`Initial liquidity must be between ${INITIAL_LIQUIDITY_MIN} and ${INITIAL_LIQUIDITY_MAX} USDC.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const pctRows = formData.options.map((opt, i) => ({
        optionKey: `__idx_${i}`,
        pct: Number(opt.targetPct) || 0,
        quoteVolumeUsdc: Number(opt.quoteVolumeUsdc) || DEFAULT_QUOTE_VOLUME_USDC,
      }));
      const priceRows = startingPricesFromPctRows(pctRows);
      const startingPrices = priceRows.map((sp, i) => ({
        ...sp,
        optionKey: String(formData.options[i].text || '').trim(),
      }));
      for (const sp of startingPrices) {
        if (!sp.optionKey) {
          alert('Each poll option needs text before creating the market.');
          setIsSubmitting(false);
          return;
        }
      }

      const submitData = {
        ...formData,
        isFeatured: formData.isFeatured || false,
        minFreeTickets: ticketReqs.minFreeTickets,
        minGoldenTickets: ticketReqs.minGoldenTickets,
        freePredictionEnabled: formData.freePredictionEnabled,
        marketEnabled: formData.marketEnabled,
        sponsoredImages: normalizeSponsoredImages(formData.sponsoredImages),
        startingPrices,
        date: utcDatetimeLocalToIso(formData.date),
        lockedTime: utcDatetimeLocalToIso(formData.lockedTime),
      };

      submitData.optionType = 'options';
      submitData.options = formData.options.map((opt) => ({
        text: String(opt.text || '').trim(),
        image: opt.image || undefined,
        yesLiquidity: parseFloat(opt.yesLiquidity) || 0,
        noLiquidity: parseFloat(opt.noLiquidity) || 0,
      }));

      await onSubmit(submitData);
      // Only close modal after successful submission
      onClose();
    } catch (error) {
      console.error('Error creating poll:', error);
      // Don't close modal on error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Poll" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Question"
          value={formData.question}
          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <ImageUpload
          label="Poll thumbnail"
          value={formData.thumbnailImage}
          onChange={(url) => setFormData({ ...formData, thumbnailImage: url })}
          folder="wergame/polls"
        />
        <textarea
          placeholder="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
        />
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
        >
          <option value="match">Match</option>
          <option value="team">Team</option>
          <option value="stage">Stage</option>
          <option value="award">Award</option>
        </select>
        <select
          value={formData.cup}
          onChange={(e) => setFormData({ ...formData, cup: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        >
          <option value="">Select Cup</option>
          {cups.map((cup) => (
            <option key={cup._id} value={cup._id}>{cup.name}</option>
          ))}
        </select>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Event date &amp; time (GMT) <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
            required
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Enter the wall time in GMT — this is exactly what users will see.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Min free tickets</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minFreeTickets: Math.max(0, (parseInt(f.minFreeTickets, 10) || 0) - 1) }))} className="w-10 h-10 border rounded-lg font-bold">−</button>
              <span className="flex-1 text-center text-lg font-bold tabular-nums">{formData.minFreeTickets}</span>
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minFreeTickets: (parseInt(f.minFreeTickets, 10) || 0) + 1 }))} className="w-10 h-10 border rounded-lg font-bold">+</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Min golden tickets</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minGoldenTickets: Math.max(0, (parseInt(f.minGoldenTickets, 10) || 0) - 1) }))} className="w-10 h-10 border rounded-lg font-bold">−</button>
              <span className="flex-1 text-center text-lg font-bold tabular-nums">{formData.minGoldenTickets}</span>
              <button type="button" onClick={() => setFormData((f) => ({ ...f, minGoldenTickets: (parseInt(f.minGoldenTickets, 10) || 0) + 1 }))} className="w-10 h-10 border rounded-lg font-bold">+</button>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 -mt-2">
          {formatFreeEntryRequirement(ticketReqs)} Golden tickets can cover free tickets when free is exhausted; free tickets can never cover a golden requirement. If both are set, players stake at least free + golden tickets.
        </p>
        {!canSubmitFreeTickets && (
          <p className="text-xs font-semibold text-red-600 dark:text-red-400">
            At least one ticket type must be required — free and golden cannot both be 0.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2 self-end">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.freePredictionEnabled} onChange={(e) => setFormData({ ...formData, freePredictionEnabled: e.target.checked })} />
              <span className="text-sm text-gray-700 dark:text-gray-300">Free prediction enabled</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.marketEnabled} onChange={(e) => setFormData({ ...formData, marketEnabled: e.target.checked })} />
              <span className="text-sm text-gray-700 dark:text-gray-300">Market enabled</span>
            </label>
          </div>
        </div>
        
        {/* Poll Options (option-based only; per-option YES/NO liquidity) */}
        <div className="space-y-4">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Poll Options
              </label>
              <button
                type="button"
                onClick={addOption}
                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
              >
                + Add Option
              </button>
            </div>
            {formData.options.length > 0 && (
              <div className="border rounded-lg p-4 dark:border-gray-700 mb-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Target odds (%)</p>
                <TargetOddsInputs
                  rows={pollOddsRows}
                  balanceOptionKey={pollOddsRows[pollOddsRows.length - 1]?.optionKey}
                  onUpdateRows={updatePollOdds}
                  getLabel={pollOddsLabel}
                />
              </div>
            )}
            {formData.options.map((option, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Option {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                  >
                    Remove
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Option text"
                  value={option.text}
                  onChange={(e) => updateOption(index, 'text', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  required
                />
                <ImageUpload
                  label="Option Image"
                  value={option.image}
                  onChange={(url) => updateOption(index, 'image', url)}
                  folder="wergame/poll-options"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      YES liquidity (USDC) ({INITIAL_LIQUIDITY_MIN}–{INITIAL_LIQUIDITY_MAX})
                    </label>
                    <InitialLiquidityInput
                      placeholder="0"
                      value={option.yesLiquidity}
                      onChange={(yesLiquidity) => updateOption(index, 'yesLiquidity', yesLiquidity)}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      NO liquidity (USDC) ({INITIAL_LIQUIDITY_MIN}–{INITIAL_LIQUIDITY_MAX})
                    </label>
                    <InitialLiquidityInput
                      placeholder="0"
                      value={option.noLiquidity}
                      onChange={(noLiquidity) => updateOption(index, 'noLiquidity', noLiquidity)}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Liq total: {formatUsdAmount((parseFloat(option.yesLiquidity) || 0) + (parseFloat(option.noLiquidity) || 0))}
                </p>
              </div>
            ))}
            {formData.options.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                Click "Add Option" to create poll options
              </p>
            )}
          </div>
        </div>
        
        
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Featured Poll</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isSponsored}
              onChange={(e) => setFormData({ ...formData, isSponsored: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Sponsored Poll</span>
          </label>
        </div>
        
        {/* Sponsored Images - Show when sponsored is checked */}
        {formData.isSponsored && (
          <SponsoredImagesEditor
            images={formData.sponsoredImages}
            onChange={(sponsoredImages) => setFormData({ ...formData, sponsoredImages })}
          />
        )}

        {/* Locked Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Locked Time (GMT, optional) - When predictions should be locked
          </label>
          <input
            type="datetime-local"
            value={formData.lockedTime}
            onChange={(e) => setFormData({ ...formData, lockedTime: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="flex space-x-2">
          <button 
            type="submit" 
            disabled={isSubmitting || !canSubmitFreeTickets}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
          <button 
            type="button" 
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

/** Admin control: set which match appears on the Home landing hero. */
const HeroFeaturedMatchSetting = ({ embedded = false, showNotification }) => {
  const [heroFeaturedMatchId, setHeroFeaturedMatchId] = useState('');
  const [heroFeaturedDraft, setHeroFeaturedDraft] = useState('');
  const [heroFeaturedPreview, setHeroFeaturedPreview] = useState(null);
  const [heroFeaturedSaving, setHeroFeaturedSaving] = useState(false);
  const [heroFeaturedLoading, setHeroFeaturedLoading] = useState(false);
  const [heroFeaturedError, setHeroFeaturedError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const featuredRes = await api.get('/admin/settings/heroFeaturedMatchId');
        if (cancelled) return;
        const featuredId = String(featuredRes.data?.value || '').trim();
        setHeroFeaturedMatchId(featuredId);
        setHeroFeaturedDraft(featuredId);
        if (featuredId) {
          try {
            const { data: match } = await api.get(`/matches/${featuredId}`);
            if (!cancelled) setHeroFeaturedPreview(match);
          } catch {
            if (!cancelled) setHeroFeaturedPreview(null);
          }
        } else {
          setHeroFeaturedPreview(null);
        }
      } catch (err) {
        console.error('Error loading hero featured match setting:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadHeroFeaturedPreview = async (id) => {
    const trimmed = String(id || '').trim();
    setHeroFeaturedError('');
    if (!trimmed) {
      setHeroFeaturedPreview(null);
      return;
    }
    setHeroFeaturedLoading(true);
    try {
      const { data: match } = await api.get(`/matches/${trimmed}`);
      setHeroFeaturedPreview(match);
    } catch (err) {
      setHeroFeaturedPreview(null);
      setHeroFeaturedError(err.response?.data?.message || 'Match not found');
    } finally {
      setHeroFeaturedLoading(false);
    }
  };

  const handleSaveHeroFeaturedMatch = async () => {
    const next = String(heroFeaturedDraft || '').trim();
    setHeroFeaturedSaving(true);
    setHeroFeaturedError('');
    try {
      await api.post('/admin/settings/heroFeaturedMatchId', { value: next });
      setHeroFeaturedMatchId(next);
      setHeroFeaturedDraft(next);
      if (next) {
        await loadHeroFeaturedPreview(next);
      } else {
        setHeroFeaturedPreview(null);
      }
      if (typeof showNotification === 'function') {
        showNotification(next ? 'Hero featured match saved' : 'Hero featured match cleared', 'success');
      }
    } catch (err) {
      console.error('Error saving featured match:', err);
      const msg = err.response?.data?.message || 'Failed to save featured match';
      setHeroFeaturedError(msg);
      if (typeof showNotification === 'function') showNotification(msg, 'error');
    } finally {
      setHeroFeaturedSaving(false);
    }
  };

  const handleClearHeroFeaturedMatch = async () => {
    setHeroFeaturedDraft('');
    setHeroFeaturedSaving(true);
    setHeroFeaturedError('');
    try {
      await api.post('/admin/settings/heroFeaturedMatchId', { value: '' });
      setHeroFeaturedMatchId('');
      setHeroFeaturedPreview(null);
      if (typeof showNotification === 'function') {
        showNotification('Hero featured match cleared', 'success');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to clear featured match';
      setHeroFeaturedError(msg);
      if (typeof showNotification === 'function') showNotification(msg, 'error');
    } finally {
      setHeroFeaturedSaving(false);
    }
  };

  const body = (
    <>
      <h4 className={`${embedded ? 'text-base' : 'text-lg'} font-semibold text-gray-900 dark:text-white`}>
        Hero featured match
      </h4>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
        Paste a match ID to feature it on the Home landing hero (right side). Leave empty for the
        default brand-only hero. Sponsored images on that match show under the card.
      </p>
      <p className="text-xs text-amber-700 dark:text-amber-300/90 mt-2">
        Tip: open the match in Admin → Matches, copy its ID from the URL or the match row, then paste
        here. Home landing must be enabled (Cups tab) for visitors to see it on `/`.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={heroFeaturedDraft}
          onChange={(e) => setHeroFeaturedDraft(e.target.value)}
          onBlur={() => {
            if (heroFeaturedDraft.trim() && heroFeaturedDraft.trim() !== heroFeaturedMatchId) {
              loadHeroFeaturedPreview(heroFeaturedDraft);
            }
          }}
          placeholder="Match MongoDB ObjectId (e.g. 65f…)"
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-mono"
        />
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={handleSaveHeroFeaturedMatch}
            disabled={heroFeaturedSaving}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-500 disabled:opacity-60"
          >
            {heroFeaturedSaving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleClearHeroFeaturedMatch}
            disabled={heroFeaturedSaving || (!heroFeaturedMatchId && !heroFeaturedDraft)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
          >
            Clear
          </button>
        </div>
      </div>
      {heroFeaturedError ? (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{heroFeaturedError}</p>
      ) : null}
      {heroFeaturedLoading ? (
        <p className="mt-2 text-sm text-gray-500">Loading match preview…</p>
      ) : null}
      {heroFeaturedPreview ? (
        <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 px-4 py-3 text-sm">
          <div className="font-semibold text-gray-900 dark:text-white">
            {heroFeaturedPreview.teamA} vs {heroFeaturedPreview.teamB}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            ID: {heroFeaturedPreview._id}
            {heroFeaturedPreview.isSponsored ? ' · Sponsored' : ''}
            {heroFeaturedMatchId === String(heroFeaturedPreview._id) ? ' · Live on hero' : ' · Not saved yet'}
          </div>
        </div>
      ) : heroFeaturedMatchId ? (
        <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
          Saved ID `{heroFeaturedMatchId}` but match could not be loaded.
        </p>
      ) : (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          No hero featured match set — landing hero stays brand-only.
        </p>
      )}
    </>
  );

  if (embedded) {
    return <div className="border-t border-gray-200 dark:border-gray-700 pt-5">{body}</div>;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border-l-4 border-red-600">
      {body}
    </div>
  );
};

const CupsTab = ({ cups, loading, tablePage, setTablePage, itemsPerPage, onCreateCup, onUpdateCup, onDeleteCup, onUpdateNavbarOrder }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [navbarCups, setNavbarCups] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [homeLandingEnabled, setHomeLandingEnabled] = useState(false);
  const [landingSaving, setLandingSaving] = useState(false);

  useEffect(() => {
    // Sort cups by navbarOrder and filter those with showInNavbar
    const sorted = [...cups]
      .filter(cup => cup.showInNavbar)
      .sort((a, b) => (a.navbarOrder || 0) - (b.navbarOrder || 0));
    setNavbarCups(sorted);
  }, [cups]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const landingRes = await api.get('/admin/settings/homeLandingEnabled');
        if (cancelled) return;
        setHomeLandingEnabled(landingRes.data?.value === true || landingRes.data?.value === 'true');
      } catch (err) {
        console.error('Error loading landing setting:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleHomeLanding = async () => {
    const next = !homeLandingEnabled;
    setLandingSaving(true);
    try {
      await api.post('/admin/settings/homeLandingEnabled', { value: next });
      setHomeLandingEnabled(next);
    } catch (err) {
      console.error('Error saving landing setting:', err);
      alert(err.response?.data?.message || 'Failed to update landing page setting');
    } finally {
      setLandingSaving(false);
    }
  };

  const handleToggleNavbar = async (cupId, showInNavbar) => {
    try {
      // If enabling, set a default order (max + 1)
      const updates = { showInNavbar };
      if (showInNavbar) {
        const maxOrder = Math.max(...cups.map(c => c.navbarOrder || 0), -1);
        updates.navbarOrder = maxOrder + 1;
      }
      await onUpdateCup(cupId, updates);
    } catch (error) {
      console.error('Error toggling navbar:', error);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === dropIndex) return;

    const newNavbarCups = [...navbarCups];
    const [removed] = newNavbarCups.splice(draggedItem, 1);
    newNavbarCups.splice(dropIndex, 0, removed);

    // Update navbarOrder for all affected cups
    const cupOrders = newNavbarCups.map((cup, index) => ({
      cupId: cup._id,
      navbarOrder: index,
    }));

    onUpdateNavbarOrder(cupOrders);
    setDraggedItem(null);
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Cups</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Create Cup
        </button>
      </div>

      {/* Landing page control */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Landing page &amp; hero featured match
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
              When enabled, visitors land on the Home hub (all cups, matches, and polls).
              When disabled, they land on the first cup in the navbar order below
              {navbarCups[0]?.name ? ` (currently: ${navbarCups[0].name})` : ''}.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleHomeLanding}
            disabled={landingSaving}
            className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-60 ${
              homeLandingEnabled ? 'bg-red-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
            aria-pressed={homeLandingEnabled}
            aria-label="Toggle Home as landing page"
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                homeLandingEnabled ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {homeLandingEnabled
            ? 'Home hub is the landing page.'
            : 'First navbar cup is the landing page (drag to change which cup is first).'}
        </p>

        <HeroFeaturedMatchSetting embedded />
      </div>

      {/* Navbar Management Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Manage Navbar Cups
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Drag and drop to reorder cups in the navbar. Toggle visibility to show/hide cups.
          {!homeLandingEnabled
            ? ' The top cup in this list is used as the site landing page.'
            : ''}
        </p>
        <div className="space-y-2">
          {navbarCups.length > 0 ? (
            navbarCups.map((cup, index) => (
              <div
                key={cup._id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-move hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                  <span className="font-medium text-gray-900 dark:text-white">{cup.name}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">({cup.slug})</span>
                </div>
                <button
                  onClick={() => handleToggleNavbar(cup._id, false)}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  Hide from Navbar
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No cups in navbar. Toggle "Show in Navbar" for cups below to add them.
            </p>
          )}
        </div>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Slug</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Active Matches</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Active Polls</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Navbar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {(() => {
              const paginatedCups = cups.slice((tablePage - 1) * itemsPerPage, tablePage * itemsPerPage);
              return cups.length > 0 ? (
              paginatedCups.map((cup) => (
                <tr key={cup._id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {cup.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {cup.slug}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      cup.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      cup.status === 'completed' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {cup.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {cup.activeMatches || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {cup.activePolls || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cup.showInNavbar || false}
                        onChange={(e) => handleToggleNavbar(cup._id, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setShowEditModal(cup)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-xs"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(cup)}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No cups found. Create one to get started!
                </td>
              </tr>
            );
            })()}
          </tbody>
        </table>
        </div>
        {cups.length > itemsPerPage && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
              disabled={tablePage <= 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {tablePage} of {Math.max(1, Math.ceil(cups.length / itemsPerPage))} ({cups.length} total)
            </span>
            <button
              onClick={() => setTablePage((p) => Math.min(Math.ceil(cups.length / itemsPerPage), p + 1))}
              disabled={tablePage >= Math.ceil(cups.length / itemsPerPage)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
      
      {showCreateModal && (
        <CreateCupModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={onCreateCup}
        />
      )}

      {showEditModal && (
        <EditCupModal
          cup={showEditModal}
          onClose={() => setShowEditModal(null)}
          onSubmit={(updates) => {
            onUpdateCup(showEditModal._id, updates);
            setShowEditModal(null);
          }}
        />
      )}

      {showDeleteModal && (
        <Modal isOpen={true} onClose={() => setShowDeleteModal(null)} title="Delete Cup">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{showDeleteModal.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  onDeleteCup(showDeleteModal._id);
                  setShowDeleteModal(null);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const CreateCupModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    thumbnailImage: '',
    description: '',
    status: 'upcoming',
    startDate: '',
    endDate: '',
    showInNavbar: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Cup">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <input
          type="text"
          placeholder="Slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <ImageUpload
          label="Cup thumbnail"
          value={formData.thumbnailImage}
          onChange={(url) => setFormData({ ...formData, thumbnailImage: url })}
          folder="wergame/cups"
        />
        <textarea
          placeholder="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
        />
        <select
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
        >
          <option value="upcoming">Upcoming</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.showInNavbar}
            onChange={(e) => setFormData({ ...formData, showInNavbar: e.target.checked })}
            className="mr-2"
          />
          <span className="text-gray-700 dark:text-gray-300">Show in Navbar</span>
        </label>
        <div className="flex space-x-2">
          <button type="submit" className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            Create
          </button>
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Edit Cup Modal
const EditCupModal = ({ cup, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: cup.name || '',
    slug: cup.slug || '',
    thumbnailImage: cup.thumbnailImage || '',
    description: cup.description || '',
    status: cup.status || 'upcoming',
    startDate: cup.startDate ? new Date(cup.startDate).toISOString().split('T')[0] : '',
    endDate: cup.endDate ? new Date(cup.endDate).toISOString().split('T')[0] : '',
    showInNavbar: cup.showInNavbar || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Cup">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <input
          type="text"
          placeholder="Slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <ImageUpload
          label="Cup thumbnail"
          value={formData.thumbnailImage}
          onChange={(url) => setFormData({ ...formData, thumbnailImage: url })}
          folder="wergame/cups"
        />
        <textarea
          placeholder="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
        />
        <select
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
        >
          <option value="upcoming">Upcoming</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.showInNavbar}
            onChange={(e) => setFormData({ ...formData, showInNavbar: e.target.checked })}
            className="mr-2"
          />
          <span className="text-gray-700 dark:text-gray-300">Show in Navbar</span>
        </label>
        <div className="flex space-x-2">
          <button type="submit" className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            Update
          </button>
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

const StagesTab = ({ cups, stages, loading, tablePage, setTablePage, itemsPerPage, onCreateStage, onUpdateStage, onDeleteStage }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [selectedCup, setSelectedCup] = useState(null);
  const [filteredStages, setFilteredStages] = useState([]);
  const [updatingCurrentId, setUpdatingCurrentId] = useState(null);

  useEffect(() => {
    if (selectedCup) {
      setFilteredStages(stages.filter(s => s.cup?._id === selectedCup || s.cup === selectedCup));
    } else {
      setFilteredStages(stages);
    }
  }, [selectedCup, stages]);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Stages</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Create Stage
        </button>
      </div>

      {/* Cup Filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Filter by Cup
        </label>
        <select
          value={selectedCup || ''}
          onChange={(e) => setSelectedCup(e.target.value || null)}
          className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
        >
          <option value="">All Cups</option>
          {cups.map((cup) => (
            <option key={cup._id} value={cup._id}>{cup.name}</option>
          ))}
        </select>
      </div>

      {/* Stages List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Order</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cup</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Current</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Start Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">End Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {(() => {
              const paginatedStages = filteredStages.slice((tablePage - 1) * itemsPerPage, tablePage * itemsPerPage);
              return paginatedStages.map((stage) => (
              <tr key={stage._id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {stage.order}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {stage.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {stage.cup?.name || 'Unknown'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {stage.isCurrent ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Current
                    </span>
                  ) : (
                    <button
                      disabled={updatingCurrentId === stage._id}
                      onClick={async () => {
                        try {
                          setUpdatingCurrentId(stage._id);
                          await api.post(`/admin/stages/${stage._id}/set-current`);
                          // Refresh stages list
                          window.location.reload();
                        } catch (err) {
                          console.error('Failed to set current stage', err);
                        } finally {
                          setUpdatingCurrentId(null);
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-900 dark:text-blue-400 disabled:opacity-50"
                    >
                      Set Current
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {stage.startDate ? new Date(stage.startDate).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {stage.endDate ? new Date(stage.endDate).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => setShowEditModal(stage)}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this stage?')) {
                        onDeleteStage(stage._id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ));
            })()}
          </tbody>
        </table>
        </div>
        {filteredStages.length > itemsPerPage && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
              disabled={tablePage <= 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {tablePage} of {Math.max(1, Math.ceil(filteredStages.length / itemsPerPage))} ({filteredStages.length} total)
            </span>
            <button
              onClick={() => setTablePage((p) => Math.min(Math.ceil(filteredStages.length / itemsPerPage), p + 1))}
              disabled={tablePage >= Math.ceil(filteredStages.length / itemsPerPage)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {filteredStages.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">No stages found. Create one to get started!</p>
        </div>
      )}

      {showCreateModal && (
        <CreateStageModal
          cups={cups}
          onClose={() => setShowCreateModal(false)}
          onSubmit={onCreateStage}
        />
      )}

      {showEditModal && (
        <EditStageModal
          stage={showEditModal}
          cups={cups}
          onClose={() => setShowEditModal(null)}
          onSubmit={(updates) => {
            onUpdateStage(showEditModal._id, updates);
            setShowEditModal(null);
          }}
        />
      )}
    </div>
  );
};

const CreateStageModal = ({ cups, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    cup: '',
    order: 0,
    startDate: '',
    endDate: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Stage" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Stage Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <select
          value={formData.cup}
          onChange={(e) => setFormData({ ...formData, cup: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        >
          <option value="">Select Cup</option>
          {cups.map((cup) => (
            <option key={cup._id} value={cup._id}>{cup.name}</option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Order"
          value={formData.order}
          onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
        />
        <div className="grid grid-cols-2 gap-4">
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Create
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

const EditStageModal = ({ stage, cups, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: stage.name || '',
    cup: stage.cup?._id || stage.cup || '',
    order: stage.order || 0,
    startDate: stage.startDate ? new Date(stage.startDate).toISOString().split('T')[0] : '',
    endDate: stage.endDate ? new Date(stage.endDate).toISOString().split('T')[0] : '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Stage" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Stage Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <select
          value={formData.cup}
          onChange={(e) => setFormData({ ...formData, cup: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        >
          <option value="">Select Cup</option>
          {cups.map((cup) => (
            <option key={cup._id} value={cup._id}>{cup.name}</option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Order"
          value={formData.order}
          onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
        />
        <div className="grid grid-cols-2 gap-4">
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Update
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

const BlogsTab = ({ blogs, loading, tablePage, setTablePage, itemsPerPage, onCreateBlog, onUpdateBlog, onDeleteBlog }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Blogs</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Create Blog
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Featured</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Views</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {(() => {
              const paginatedBlogs = blogs.slice((tablePage - 1) * itemsPerPage, tablePage * itemsPerPage);
              return paginatedBlogs.map((blog) => (
              <tr key={blog._id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {blog.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {blog.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    blog.isPublished 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}>
                    {blog.isPublished ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {blog.isFeatured && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full text-xs">
                      Featured
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {blog.views || 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => setShowEditModal(blog)}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(blog)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ));
            })()}
          </tbody>
        </table>
        </div>
        {blogs.length > itemsPerPage && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
              disabled={tablePage <= 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {tablePage} of {Math.max(1, Math.ceil(blogs.length / itemsPerPage))} ({blogs.length} total)
            </span>
            <button
              onClick={() => setTablePage((p) => Math.min(Math.ceil(blogs.length / itemsPerPage), p + 1))}
              disabled={tablePage >= Math.ceil(blogs.length / itemsPerPage)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {blogs.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">No blogs found. Create one to get started!</p>
        </div>
      )}

      {showCreateModal && (
        <CreateBlogModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={onCreateBlog}
        />
      )}

      {showEditModal && (
        <EditBlogModal
          blog={showEditModal}
          onClose={() => setShowEditModal(null)}
          onSubmit={(updates) => {
            onUpdateBlog(showEditModal._id, updates);
            setShowEditModal(null);
          }}
        />
      )}
      
      {showDeleteModal && (
        <Modal isOpen={true} onClose={() => setShowDeleteModal(null)} title="Delete Blog">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{showDeleteModal.title}</strong>? This action cannot be undone.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  onDeleteBlog(showDeleteModal._id);
                  setShowDeleteModal(null);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const CreateBlogModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: null, // Tiptap handles null/undefined gracefully
    thumbnail: '',
    category: 'General',
    tags: '',
    isFeatured: false,
    isPublished: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
    });
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create Blog" size="full">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <textarea
          placeholder="Short Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
          required
        />
        <ImageUpload
          label="Blog thumbnail"
          value={formData.thumbnail}
          onChange={(url) => setFormData({ ...formData, thumbnail: url })}
          folder="wergame/blogs"
        />
        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
          <input
            type="text"
            placeholder="Tags (comma separated)"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Content
          </label>
          <BlogEditor
            value={formData.content}
            onChange={(content) => setFormData({ ...formData, content })}
          />
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Featured</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isPublished}
              onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Publish</span>
          </label>
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Create
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

const EditBlogModal = ({ blog, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: blog.title || '',
    description: blog.description || '',
    content: blog.content || null, // Tiptap handles null/undefined gracefully
    thumbnail: blog.thumbnail || '',
    category: blog.category || 'General',
    tags: blog.tags?.join(', ') || '',
    isFeatured: blog.isFeatured || false,
    isPublished: blog.isPublished || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
    });
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Blog" size="full">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <textarea
          placeholder="Short Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
          required
        />
        <ImageUpload
          label="Blog thumbnail"
          value={formData.thumbnail}
          onChange={(url) => setFormData({ ...formData, thumbnail: url })}
          folder="wergame/blogs"
        />
        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
          <input
            type="text"
            placeholder="Tags (comma separated)"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Content
          </label>
          <BlogEditor
            value={formData.content}
            onChange={(content) => setFormData({ ...formData, content })}
          />
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Featured</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isPublished}
              onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Publish</span>
          </label>
        </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Update
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Simple Blog Editor wrapper for Tiptap
const BlogEditor = ({ value, onChange }) => {
  // Tiptap accepts JSON or HTML, we'll use JSON format
  // Handle conversion from old Slate format if needed
  const normalizeValue = React.useCallback((val) => {
    if (!val) {
      return null; // Tiptap handles null/undefined gracefully
    }

    // If it's already a Tiptap JSON format (has type and content properties)
    if (typeof val === 'object' && val.type) {
      return val;
    }

    // If it's a string, try to parse it
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // If it's plain text, convert to Tiptap format
        return {
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: val }],
            },
          ],
        };
      }
    }

    // If it's an array (old Slate format), convert to Tiptap format
    if (Array.isArray(val)) {
      const convertSlateToTiptap = (slateNodes) => {
        return slateNodes.map((node) => {
          if (node.type === 'paragraph') {
            return {
              type: 'paragraph',
              content: node.children
                ? node.children.map((child) => {
                    if (typeof child === 'string') {
                      return { type: 'text', text: child };
                    }
                    return {
                      type: 'text',
                      text: child.text || '',
                      marks: [
                        ...(child.bold ? [{ type: 'bold' }] : []),
                        ...(child.italic ? [{ type: 'italic' }] : []),
                      ],
                    };
                  })
                : [],
            };
          }
          if (node.type === 'heading-one') {
            return {
              type: 'heading',
              attrs: { level: 1 },
              content: node.children
                ? node.children.map((child) => ({
                    type: 'text',
                    text: typeof child === 'string' ? child : child.text || '',
                  }))
                : [],
            };
          }
          if (node.type === 'heading-two') {
            return {
              type: 'heading',
              attrs: { level: 2 },
              content: node.children
                ? node.children.map((child) => ({
                    type: 'text',
                    text: typeof child === 'string' ? child : child.text || '',
                  }))
                : [],
            };
          }
          if (node.type === 'heading-three') {
            return {
              type: 'heading',
              attrs: { level: 3 },
              content: node.children
                ? node.children.map((child) => ({
                    type: 'text',
                    text: typeof child === 'string' ? child : child.text || '',
                  }))
                : [],
            };
          }
          if (node.type === 'bulleted-list') {
            return {
              type: 'bulletList',
              content: node.children
                ? node.children.map((item) => ({
                    type: 'listItem',
                    content: [
                      {
                        type: 'paragraph',
                        content: item.children
                          ? item.children.map((child) => ({
                              type: 'text',
                              text: typeof child === 'string' ? child : child.text || '',
                            }))
                          : [],
                      },
                    ],
                  }))
                : [],
            };
          }
          if (node.type === 'numbered-list') {
            return {
              type: 'orderedList',
              content: node.children
                ? node.children.map((item) => ({
                    type: 'listItem',
                    content: [
                      {
                        type: 'paragraph',
                        content: item.children
                          ? item.children.map((child) => ({
                              type: 'text',
                              text: typeof child === 'string' ? child : child.text || '',
                            }))
                          : [],
                      },
                    ],
                  }))
                : [],
            };
          }
          // Default to paragraph
          return {
            type: 'paragraph',
            content: [],
          };
        });
      };

      return {
        type: 'doc',
        content: convertSlateToTiptap(val),
      };
    }

    // Return as-is if it's already a valid Tiptap format
    return val;
  }, []);

  const normalizedValue = React.useMemo(() => {
    return normalizeValue(value);
  }, [value, normalizeValue]);

  return (
    <div>
      <TiptapEditor value={normalizedValue} onChange={onChange} showToolbar />
    </div>
  );
};

const SettingsIconEdit = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const SettingsIconTrash = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const DEFAULT_NFT_TIER = { minHoldings: 1, normalTickets: 1, goldenTickets: 0 };

const normalizeNftTiersFromRow = (n) => {
  if (Array.isArray(n?.tiers) && n.tiers.length) {
    return n.tiers
      .map((t) => ({
        minHoldings: Math.max(1, parseInt(t.minHoldings, 10) || 1),
        normalTickets: Math.max(0, parseInt(t.normalTickets, 10) || 0),
        goldenTickets: Math.max(0, parseInt(t.goldenTickets, 10) || 0),
      }))
      .sort((a, b) => a.minHoldings - b.minHoldings);
  }
  const normal = Math.max(0, parseInt(n?.normalTickets ?? n?.dailyTickets, 10) || 0);
  const golden = Math.max(0, parseInt(n?.goldenTickets, 10) || 0);
  return [{ minHoldings: 1, normalTickets: normal, goldenTickets: golden }];
};

const formatNftTierSummary = (n) => {
  const list = normalizeNftTiersFromRow(n);
  if (!list.length) return '—';
  return list.map((t) => `${t.minHoldings}+ → ${t.normalTickets}N / ${t.goldenTickets}G`).join('; ');
};

const emptyNftBonus = () => ({
  id: `nft-${Date.now()}`,
  name: '',
  contractAddress: '',
  imageUrl: '',
  tiers: [{ ...DEFAULT_NFT_TIER }],
  normalPeriodDays: 3,
  goldenPeriodDays: 3,
  normalTickets: 1,
  goldenTickets: 0,
  dailyTickets: 1,
  link: '',
  tokenStandard: 'auto',
  tokenId: '',
  registryAddress: '',
  accountImplementation: '',
  accountSalt: '',
  innerTokenContract: '',
  innerTokenStandard: 'auto',
  innerTokenId: '',
  requireTbaDeployed: false,
});

const GOLDEN_TICKET_PRESETS = ['1', '2', '3', '4', '5'];
const GOLDEN_DAY_PRESETS = ['1', '3', '7', '30'];
const GOLDEN_DURATION_PRESETS = ['7', '14', '30', '60', '90'];

const STREAK_TYPE_OPTIONS = [
  { value: 'total', label: 'Total (login + free + boost)' },
  { value: 'login', label: 'Login' },
  { value: 'free', label: 'Free prediction' },
  { value: 'boost', label: 'Boost' },
];

const emptyStreakMilestone = () => ({
  id: `sm-${Date.now()}`,
  streakType: 'total',
  requiredStreak: 10,
  goldenTickets: 1,
  active: true,
});

const streakTypeAdminLabel = (type) =>
  STREAK_TYPE_OPTIONS.find((o) => o.value === type)?.label || type;

const resolveGoldenPresetNumber = (preset, customVal) => {
  if (preset === 'custom') {
    const n = parseInt(String(customVal || '').trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const n = parseInt(preset, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const shortAddr = (addr) => {
  if (!addr) return '—';
  const s = String(addr);
  return s.length > 12 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
};

// Settings Tab Component
const NEWSLETTER_PAGE_SIZE = 20;
const USERS_PAGE_SIZE = 20;

const JACKPOT_CLAIM_LOCK_MS = 10 * 60 * 1000;

function formatCountdownMs(ms) {
  if (ms == null || ms <= 0) return '0:00';
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function isSelectableAdminUser(user) {
  return Boolean(user?.id) && user.role !== 'admin' && user.role !== 'superAdmin';
}

function formatBulkActionResult(result, action) {
  const changed = action === 'ban' ? result?.banned || 0 : result?.unbanned || 0;
  const skipped = (result?.skippedProtected || 0) + (result?.notFound || 0);
  const already =
    action === 'ban' ? result?.alreadyBanned || 0 : result?.alreadyActive || 0;
  const verb = action === 'ban' ? 'Banned' : 'Unbanned';
  const parts = [`${verb} ${changed} user${changed === 1 ? '' : 's'}`];
  if (already) parts.push(`${already} already ${action === 'ban' ? 'banned' : 'active'}`);
  if (skipped) parts.push(`${skipped} skipped`);
  return parts.join(' · ');
}

const AdminBulkUserToolbar = ({
  selectedCount,
  pageSelectableCount,
  allPageSelected,
  somePageSelected,
  onTogglePage,
  onSelectAllFiltered,
  onClear,
  onBan,
  onUnban,
  busy,
  selectAllBusy,
  selectAllLabel = 'Select all filtered',
}) => (
  <div className="mb-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex flex-wrap items-center gap-2">
      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
        <input
          type="checkbox"
          checked={allPageSelected}
          ref={(el) => {
            if (el) el.indeterminate = somePageSelected && !allPageSelected;
          }}
          onChange={onTogglePage}
          disabled={!pageSelectableCount || busy}
          className="rounded border-gray-300"
        />
        Select page
      </label>
      <button
        type="button"
        onClick={onSelectAllFiltered}
        disabled={selectAllBusy || busy}
        className="rounded-lg border border-violet-300 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40"
      >
        {selectAllBusy ? 'Selecting…' : selectAllLabel}
      </button>
      {selectedCount > 0 ? (
        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          className="text-xs font-semibold text-gray-600 hover:underline dark:text-gray-300"
        >
          Clear selection
        </button>
      ) : null}
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
        {selectedCount} selected
      </span>
      <button
        type="button"
        onClick={onBan}
        disabled={!selectedCount || busy}
        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
      >
        {busy ? 'Working…' : 'Ban selected'}
      </button>
      <button
        type="button"
        onClick={onUnban}
        disabled={!selectedCount || busy}
        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {busy ? 'Working…' : 'Unban selected'}
      </button>
    </div>
  </div>
);

async function copyTextToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const UserReferralsModal = ({ user, onClose, onUsersUpdated }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(user?.referralCount || 0);
  const [loading, setLoading] = useState(true);
  const [banningId, setBanningId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/users/${user.id}/referrals`, {
          params: { page, limit: 50 },
        });
        if (!active) return;
        setItems(data?.items || []);
        setTotal(data?.total || 0);
        setPages(data?.pages || 1);
      } catch (error) {
        if (!active) return;
        setItems([]);
        showNotification(error.response?.data?.message || 'Failed to load referred users', 'error');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [user.id, page, showNotification]);

  const displayDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const pageSelectableIds = items.filter(isSelectableAdminUser).map((item) => String(item.id));
  const allPageSelected =
    pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageSelectableIds.some((id) => selectedIds.includes(id));

  const toggleSelected = (id) => {
    const key = String(id);
    setSelectedIds((current) =>
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    );
  };

  const togglePageSelection = () => {
    setSelectedIds((current) => {
      if (allPageSelected) return current.filter((id) => !pageSelectableIds.includes(id));
      return Array.from(new Set([...current, ...pageSelectableIds]));
    });
  };

  const selectAllFiltered = async () => {
    setSelectAllBusy(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/referrals/ids`);
      const ids = Array.isArray(data?.ids) ? data.ids.map(String) : [];
      setSelectedIds(ids);
      showNotification(
        data?.truncated
          ? `Selected the first ${ids.length} referred users`
          : `Selected ${ids.length} referred user${ids.length === 1 ? '' : 's'}`,
        'success'
      );
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to select referred users', 'error');
    } finally {
      setSelectAllBusy(false);
    }
  };

  const applyBanState = (ids, banned) => {
    const idSet = new Set(ids.map(String));
    setItems((current) =>
      current.map((item) => (idSet.has(String(item.id)) ? { ...item, banned } : item))
    );
    onUsersUpdated?.(ids, banned);
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) return;
    const label = action === 'ban' ? 'ban' : 'unban';
    if (!window.confirm(`${label === 'ban' ? 'Ban' : 'Unban'} ${selectedIds.length} selected referred user${selectedIds.length === 1 ? '' : 's'}?`)) {
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        action === 'ban' ? '/admin/users/bulk-ban' : '/admin/users/bulk-unban',
        { userIds: selectedIds }
      );
      applyBanState(action === 'ban' ? data?.bannedIds || selectedIds : data?.unbannedIds || selectedIds, action === 'ban');
      setSelectedIds([]);
      showNotification(formatBulkActionResult(data, action), 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || `Failed to ${label} selected users`, 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const banReferredUser = async (referred) => {
    if (!referred?.id || referred.banned || referred.role === 'admin' || referred.role === 'superAdmin') {
      return;
    }
    if (!window.confirm(`Ban ${referred.username}? They will not be able to log in.`)) return;
    setBanningId(referred.id);
    try {
      await api.post('/admin/users/ban', { userId: referred.id });
      applyBanState([referred.id], true);
      showNotification(`${referred.username} banned`, 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to ban referred user', 'error');
    } finally {
      setBanningId(null);
    }
  };

  const unbanReferredUser = async (referred) => {
    if (!referred?.id || !referred.banned) return;
    if (!window.confirm(`Unban ${referred.username}?`)) return;
    setBanningId(referred.id);
    try {
      await api.post('/admin/users/unban', { userId: referred.id });
      applyBanState([referred.id], false);
      showNotification(`${referred.username} unbanned`, 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to unban referred user', 'error');
    } finally {
      setBanningId(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Users referred by ${user.username}`} size="xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Total referrals: <strong className="tabular-nums">{total}</strong>
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          “Their referrals” shows how many users each referred account has referred.
        </p>
      </div>

      {items.length > 0 ? (
        <AdminBulkUserToolbar
          selectedCount={selectedIds.length}
          pageSelectableCount={pageSelectableIds.length}
          allPageSelected={allPageSelected}
          somePageSelected={somePageSelected}
          onTogglePage={togglePageSelection}
          onSelectAllFiltered={selectAllFiltered}
          onClear={() => setSelectedIds([])}
          onBan={() => runBulkAction('ban')}
          onUnban={() => runBulkAction('unban')}
          busy={bulkBusy}
          selectAllBusy={selectAllBusy}
          selectAllLabel="Select all referred"
        />
      ) : null}

      {loading ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">Loading referrals…</div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
          This user has not referred anyone yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600 dark:bg-gray-900/50 dark:text-gray-400">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected && !allPageSelected;
                    }}
                    onChange={togglePageSelection}
                    disabled={!pageSelectableIds.length || bulkBusy}
                    aria-label="Select all referred users on this page"
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="hidden px-4 py-3 font-medium sm:table-cell">Email</th>
                <th className="px-4 py-3 text-center font-medium">Referral status</th>
                <th className="px-4 py-3 text-center font-medium">Their referrals</th>
                <th className="hidden px-4 py-3 font-medium md:table-cell">Joined</th>
                <th className="px-4 py-3 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((referred) => (
                <tr key={referred.id} className="text-gray-900 dark:text-gray-100">
                  <td className="px-3 py-3">
                    {isSelectableAdminUser(referred) ? (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(String(referred.id))}
                        onChange={() => toggleSelected(referred.id)}
                        disabled={bulkBusy}
                        aria-label={`Select ${referred.username}`}
                        className="rounded border-gray-300"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{referred.username}</p>
                    {referred.banned ? <p className="text-xs text-red-600">Banned</p> : null}
                  </td>
                  <td className="hidden px-4 py-3 text-gray-600 dark:text-gray-400 sm:table-cell">
                    {referred.email || '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        referred.referralStatus === 'verified'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                      }`}
                    >
                      {referred.referralStatus === 'verified' ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold tabular-nums">
                    {referred.referralCount ?? 0}
                  </td>
                  <td className="hidden whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-400 md:table-cell">
                    {displayDate(referred.joinedAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {referred.role === 'admin' || referred.role === 'superAdmin' ? (
                      <span className="text-xs text-gray-400">Protected</span>
                    ) : referred.banned ? (
                      <button
                        type="button"
                        onClick={() => unbanReferredUser(referred)}
                        disabled={banningId === referred.id || bulkBusy}
                        className="text-xs font-semibold text-emerald-600 hover:underline disabled:opacity-50 dark:text-emerald-400"
                      >
                        {banningId === referred.id ? '…' : 'Unban'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => banReferredUser(referred)}
                        disabled={banningId === referred.id || bulkBusy}
                        className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50 dark:text-red-400"
                      >
                        {banningId === referred.id ? 'Banning…' : 'Ban'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 ? (
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            disabled={page <= 1 || loading}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-50 dark:border-gray-600"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            onClick={() => setPage((value) => Math.min(pages, value + 1))}
            disabled={page >= pages || loading}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-50 dark:border-gray-600"
          >
            Next
          </button>
        </div>
      ) : null}
    </Modal>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [bannedFilter, setBannedFilter] = useState('all');
  const [referralFilter, setReferralFilter] = useState('all');
  const [referralSort, setReferralSort] = useState('newest');
  const [emailStats, setEmailStats] = useState({ verified: 0, expired: 0, unverified: 0 });
  const [banStats, setBanStats] = useState({ banned: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [banIdentifier, setBanIdentifier] = useState('');
  const [banningId, setBanningId] = useState(null);
  const [submittingBan, setSubmittingBan] = useState(false);
  const [referralsUser, setReferralsUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const { showNotification } = useNotification();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users', {
        params: {
          page,
          limit: USERS_PAGE_SIZE,
          search: search || undefined,
          verifiedFilter: verifiedFilter !== 'all' ? verifiedFilter : undefined,
          bannedFilter: bannedFilter !== 'all' ? bannedFilter : undefined,
          referralFilter: referralFilter !== 'all' ? referralFilter : undefined,
          referralSort: referralSort !== 'newest' ? referralSort : undefined,
        },
      });
      setUsers(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      if (data.emailStats) setEmailStats(data.emailStats);
      if (data.banStats) setBanStats(data.banStats);
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to load users', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, verifiedFilter, bannedFilter, referralFilter, referralSort, showNotification]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [search, verifiedFilter, bannedFilter, referralFilter, referralSort]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleBanByIdentifier = async (e) => {
    e.preventDefault();
    const identifier = banIdentifier.trim();
    if (!identifier) return;
    if (!window.confirm(`Ban user "${identifier}"? They will not be able to log in.`)) return;
    setSubmittingBan(true);
    try {
      await api.post('/admin/users/ban', { identifier });
      showNotification('User banned', 'success');
      setBanIdentifier('');
      fetchUsers();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to ban user', 'error');
    } finally {
      setSubmittingBan(false);
    }
  };

  const handleBanRow = async (user) => {
    if (user.banned) return;
    if (!window.confirm(`Ban ${user.username}? They will not be able to log in.`)) return;
    setBanningId(user.id);
    try {
      await api.post('/admin/users/ban', { userId: user.id });
      showNotification(`${user.username} banned`, 'success');
      fetchUsers();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to ban user', 'error');
    } finally {
      setBanningId(null);
    }
  };

  const handleUnbanRow = async (user) => {
    if (!user.banned) return;
    if (!window.confirm(`Unban ${user.username}?`)) return;
    setBanningId(user.id);
    try {
      await api.post('/admin/users/unban', { userId: user.id });
      showNotification(`${user.username} unbanned`, 'success');
      fetchUsers();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to unban user', 'error');
    } finally {
      setBanningId(null);
    }
  };

  const pageSelectableIds = users.filter(isSelectableAdminUser).map((item) => String(item.id));
  const allPageSelected =
    pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageSelectableIds.some((id) => selectedIds.includes(id));

  const toggleSelected = (id) => {
    const key = String(id);
    setSelectedIds((current) =>
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    );
  };

  const togglePageSelection = () => {
    setSelectedIds((current) => {
      if (allPageSelected) return current.filter((id) => !pageSelectableIds.includes(id));
      return Array.from(new Set([...current, ...pageSelectableIds]));
    });
  };

  const selectAllFiltered = async () => {
    setSelectAllBusy(true);
    try {
      const { data } = await api.get('/admin/users/ids', {
        params: {
          search: search || undefined,
          verifiedFilter: verifiedFilter !== 'all' ? verifiedFilter : undefined,
          bannedFilter: bannedFilter !== 'all' ? bannedFilter : undefined,
          referralFilter: referralFilter !== 'all' ? referralFilter : undefined,
        },
      });
      const ids = Array.isArray(data?.ids) ? data.ids.map(String) : [];
      setSelectedIds(ids);
      showNotification(
        data?.truncated
          ? `Selected the first ${ids.length} filtered users`
          : `Selected ${ids.length} filtered user${ids.length === 1 ? '' : 's'}`,
        'success'
      );
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to select filtered users', 'error');
    } finally {
      setSelectAllBusy(false);
    }
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) return;
    const label = action === 'ban' ? 'ban' : 'unban';
    if (
      !window.confirm(
        `${action === 'ban' ? 'Ban' : 'Unban'} ${selectedIds.length} selected user${
          selectedIds.length === 1 ? '' : 's'
        }?`
      )
    ) {
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        action === 'ban' ? '/admin/users/bulk-ban' : '/admin/users/bulk-unban',
        { userIds: selectedIds }
      );
      setSelectedIds([]);
      showNotification(formatBulkActionResult(data, action), 'success');
      fetchUsers();
    } catch (error) {
      showNotification(error.response?.data?.message || `Failed to ${label} selected users`, 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const formatWallet = (addr) => {
    if (!addr) return '—';
    return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
  };

  const truncateEmail = (email) => {
    const s = String(email || '').trim();
    if (!s) return '';
    if (s.length <= 24) return s;
    const at = s.indexOf('@');
    if (at > 0) {
      const local = s.slice(0, at);
      const domain = s.slice(at + 1);
      const shortLocal = local.length > 8 ? `${local.slice(0, 6)}…` : local;
      const shortDomain = domain.length > 14 ? `${domain.slice(0, 10)}…` : domain;
      return `${shortLocal}@${shortDomain}`;
    }
    return `${s.slice(0, 10)}…${s.slice(-6)}`;
  };

  const referrerDisplay = (referrer) => {
    if (!referrer) return null;
    if (referrer.email) {
      return { label: truncateEmail(referrer.email), copyValue: referrer.email, kind: 'email' };
    }
    if (referrer.walletAddress) {
      return {
        label: formatWallet(referrer.walletAddress),
        copyValue: referrer.walletAddress,
        kind: 'wallet',
      };
    }
    if (referrer.username) {
      return { label: referrer.username, copyValue: referrer.username, kind: 'username' };
    }
    return null;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const handleCopyWallet = async (addr) => {
    if (!addr) return;
    const ok = await copyTextToClipboard(addr);
    showNotification(ok ? 'Wallet address copied' : 'Could not copy address', ok ? 'success' : 'error');
  };

  const handleCopyReferrer = async (value, kind) => {
    if (!value) return;
    const ok = await copyTextToClipboard(value);
    const noun = kind === 'email' ? 'Referrer email' : kind === 'wallet' ? 'Referrer wallet' : 'Referrer';
    showNotification(ok ? `${noun} copied` : 'Could not copy', ok ? 'success' : 'error');
  };

  const verificationBadge = (status) => {
    if (status === 'verified') {
      return (
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
          Verified
        </span>
      );
    }
    if (status === 'expired') {
      return (
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          Expired
        </span>
      );
    }
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
        Unverified
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Ban user</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Enter a username, email, or wallet address to ban an account. Banned users cannot log in and see a message
          about unusual activity.
        </p>
        <form onSubmit={handleBanByIdentifier} className="flex flex-col sm:flex-row gap-2 max-w-2xl">
          <input
            type="text"
            placeholder="Username, email, or 0x wallet address"
            value={banIdentifier}
            onChange={(e) => setBanIdentifier(e.target.value)}
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
          <button
            type="submit"
            disabled={submittingBan || !banIdentifier.trim()}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {submittingBan ? 'Banning…' : 'Ban user'}
          </button>
        </form>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">All users</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {total} user{total === 1 ? '' : 's'}
              {verifiedFilter !== 'all' ||
              bannedFilter !== 'all' ||
              referralFilter !== 'all' ||
              referralSort !== 'newest'
                ? ' (filtered)'
                : ' total'}
            </p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300">
                Verified: <strong className="tabular-nums">{emailStats.verified ?? 0}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                Unverified: <strong className="tabular-nums">{emailStats.unverified ?? 0}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300">
                Expired: <strong className="tabular-nums">{emailStats.expired ?? 0}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300">
                Banned: <strong className="tabular-nums">{banStats.banned ?? 0}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300">
                Active: <strong className="tabular-nums">{banStats.active ?? 0}</strong>
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchUsers}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        <form onSubmit={handleSearchSubmit} className="mb-4 flex flex-col sm:flex-row gap-2">
          <input
            type="search"
            placeholder="Search username, email, or wallet…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearch('');
              }}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-600 dark:text-gray-400 hover:underline"
            >
              Clear
            </button>
          )}
        </form>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Email verification:</span>
          {[
            { id: 'all', label: 'All' },
            { id: 'verified', label: 'Verified' },
            { id: 'unverified', label: 'Unverified' },
            { id: 'expired', label: 'Expired' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setVerifiedFilter(opt.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                verifiedFilter === opt.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Ban status:</span>
          {[
            { id: 'all', label: 'All' },
            { id: 'banned', label: 'Banned' },
            { id: 'active', label: 'Active' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setBannedFilter(opt.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                bannedFilter === opt.id
                  ? opt.id === 'banned'
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {opt.label}
              {opt.id === 'banned' && banStats.banned != null ? ` (${banStats.banned})` : ''}
              {opt.id === 'active' && banStats.active != null ? ` (${banStats.active})` : ''}
            </button>
          ))}
        </div>

        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-violet-200 bg-violet-50/60 p-3 dark:border-violet-900 dark:bg-violet-950/20">
          <div>
            <label
              htmlFor="admin-referral-filter"
              className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300"
            >
              Referral filter
            </label>
            <select
              id="admin-referral-filter"
              value={referralFilter}
              onChange={(e) => setReferralFilter(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            >
              <option value="all">All users</option>
              <option value="with">Users with referrals</option>
              <option value="without">Users without referrals</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="admin-referral-sort"
              className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300"
            >
              Referral order
            </label>
            <select
              id="admin-referral-sort"
              value={referralSort}
              onChange={(e) => setReferralSort(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            >
              <option value="newest">Newest users</option>
              <option value="highest">Highest referrals first</option>
              <option value="lowest">Lowest referrals first</option>
            </select>
          </div>
          {(referralFilter !== 'all' || referralSort !== 'newest') && (
            <button
              type="button"
              onClick={() => {
                setReferralFilter('all');
                setReferralSort('newest');
              }}
              className="rounded-lg px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-100 dark:text-violet-300 dark:hover:bg-violet-900/40"
            >
              Clear referral filters
            </button>
          )}
        </div>

        {users.length > 0 || selectedIds.length > 0 ? (
          <AdminBulkUserToolbar
            selectedCount={selectedIds.length}
            pageSelectableCount={pageSelectableIds.length}
            allPageSelected={allPageSelected}
            somePageSelected={somePageSelected}
            onTogglePage={togglePageSelection}
            onSelectAllFiltered={selectAllFiltered}
            onClear={() => setSelectedIds([])}
            onBan={() => runBulkAction('ban')}
            onUnban={() => runBulkAction('unban')}
            busy={bulkBusy}
            selectAllBusy={selectAllBusy}
          />
        ) : null}

        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {search ? 'No users match your search.' : 'No users found.'}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="w-10 py-3 px-3">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePageSelected && !allPageSelected;
                      }}
                      onChange={togglePageSelection}
                      disabled={!pageSelectableIds.length || bulkBusy}
                      aria-label="Select all users on this page"
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="py-3 px-4 font-medium min-w-[11rem]">Username</th>
                  <th className="py-3 px-4 font-medium hidden md:table-cell">Email</th>
                  <th className="py-3 px-4 font-medium hidden lg:table-cell">Wallet</th>
                  <th className="py-3 px-4 font-medium">Verified</th>
                  <th className="py-3 px-4 font-medium text-center">Referrals</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium hidden sm:table-cell">Joined</th>
                  <th className="py-3 px-4 font-medium w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {users.map((user) => {
                  const refInfo = referrerDisplay(user.referredBy);
                  return (
                  <tr key={user.id} className="text-gray-900 dark:text-gray-100">
                    <td className="py-3 px-3">
                      {isSelectableAdminUser(user) ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(String(user.id))}
                          onChange={() => toggleSelected(user.id)}
                          disabled={bulkBusy}
                          aria-label={`Select ${user.username}`}
                          className="rounded border-gray-300"
                        />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 align-top">
                      <div className="min-w-0 max-w-[14rem]">
                        <p className="font-medium text-gray-900 dark:text-white truncate" title={user.username}>
                          {user.username}
                        </p>
                        {user.role !== 'user' ? (
                          <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
                            {user.role}
                          </span>
                        ) : null}
                        {refInfo ? (
                          <div className="mt-1 flex items-center gap-1 min-w-0">
                            <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 shrink-0">
                              Ref
                            </span>
                            <span
                              className="text-[11px] text-violet-700 dark:text-violet-300 truncate font-medium"
                              title={
                                user.referredBy?.username
                                  ? `${user.referredBy.username} · ${refInfo.copyValue}`
                                  : refInfo.copyValue
                              }
                            >
                              {refInfo.label}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopyReferrer(refInfo.copyValue, refInfo.kind)}
                              title={`Copy referrer ${refInfo.kind}`}
                              aria-label={`Copy referrer ${refInfo.kind}`}
                              className="shrink-0 p-0.5 rounded hover:bg-violet-100 dark:hover:bg-violet-900/40 text-violet-500 dark:text-violet-400"
                            >
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell text-gray-600 dark:text-gray-400">
                      {user.email || '—'}
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell font-mono text-xs text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{formatWallet(user.walletAddress)}</span>
                        {user.walletAddress ? (
                          <button
                            type="button"
                            onClick={() => handleCopyWallet(user.walletAddress)}
                            title="Copy full wallet address"
                            aria-label="Copy wallet address"
                            className="shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 px-4">{verificationBadge(user.emailVerificationStatus)}</td>
                    <td className="py-3 px-4 text-center">
                      <p className="font-semibold tabular-nums">{user.referralCount ?? 0}</p>
                      <button
                        type="button"
                        onClick={() => setReferralsUser(user)}
                        className="mt-1 text-xs font-semibold text-violet-600 hover:underline dark:text-violet-400"
                      >
                        View referrals
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      {user.banned ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                          Banned
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="py-3 px-4">
                      {user.role === 'admin' || user.role === 'superAdmin' ? (
                        <span className="text-xs text-gray-400">Protected</span>
                      ) : user.banned ? (
                        <button
                          type="button"
                          onClick={() => handleUnbanRow(user)}
                          disabled={banningId === user.id || bulkBusy}
                          className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline disabled:opacity-50"
                        >
                          {banningId === user.id ? '…' : 'Unban'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleBanRow(user)}
                          disabled={banningId === user.id || bulkBusy}
                          className="text-xs font-semibold text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                        >
                          {banningId === user.id ? '…' : 'Ban'}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400">
            <span>
              Page {page} of {pages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages || loading}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      {referralsUser ? (
        <UserReferralsModal
          user={referralsUser}
          onClose={() => setReferralsUser(null)}
          onUsersUpdated={(ids, banned) => {
            const idSet = new Set((ids || []).map(String));
            setUsers((current) =>
              current.map((item) => (idSet.has(String(item.id)) ? { ...item, banned } : item))
            );
          }}
        />
      ) : null}
    </div>
  );
};

const NewsletterTab = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { showNotification } = useNotification();

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/newsletter', {
        params: {
          page,
          limit: NEWSLETTER_PAGE_SIZE,
          search: search || undefined,
        },
      });
      setSubscribers(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to load subscribers', 'error');
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, showNotification]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/admin/newsletter/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotification('Newsletter list exported', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this subscriber from the list?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/admin/newsletter/${id}`);
      showNotification('Subscriber removed', 'success');
      fetchSubscribers();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Newsletter</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {total} subscriber{total === 1 ? '' : 's'} total
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={fetchSubscribers}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || total === 0}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSearchSubmit} className="mb-4 flex flex-col sm:flex-row gap-2">
        <input
          type="search"
          placeholder="Search by email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
        />
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Search
        </button>
        {search && (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setSearch('');
            }}
            className="px-4 py-2 text-sm font-medium rounded-lg text-gray-600 dark:text-gray-400 hover:underline"
          >
            Clear
          </button>
        )}
      </form>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading subscribers…</div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {search ? 'No subscribers match your search.' : 'No newsletter subscribers yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-600 dark:text-gray-400">
              <tr>
                <th className="py-3 px-4 font-medium">Email</th>
                <th className="py-3 px-4 font-medium">Source</th>
                <th className="py-3 px-4 font-medium">Subscribed</th>
                <th className="py-3 px-4 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {subscribers.map((sub) => (
                <tr key={sub._id} className="text-gray-900 dark:text-gray-100">
                  <td className="py-3 px-4 font-mono text-xs sm:text-sm">{sub.email}</td>
                  <td className="py-3 px-4 capitalize">{sub.source || '—'}</td>
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                    {sub.subscribedAt
                      ? new Date(sub.subscribedAt).toLocaleString()
                      : '—'}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      type="button"
                      onClick={() => handleDelete(sub._id)}
                      disabled={deletingId === sub._id}
                      className="text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-50"
                    >
                      {deletingId === sub._id ? '…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages || loading}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const SettingsTab = () => {
  const [dailyFreeTickets, setDailyFreeTickets] = useState(1);
  const [freeTicketPeriodDays, setFreeTicketPeriodDays] = useState(1);
  const [pointsPerWin, setPointsPerWin] = useState(10);
  const [nftBonuses, setNftBonuses] = useState([]);
  const [goldenTicketRate, setGoldenTicketRate] = useState({ tickets: 1, perUsdc: 10 });
  const [goldenTicketMarketRate, setGoldenTicketMarketRate] = useState({ tickets: 1, perUsdc: 10 });
  const [gasDripSettings, setGasDripSettings] = useState({
    enabled: true,
    freeUsd: 0.08,
    boostUsd: 0.1,
    marketUsd: 0.12,
    freeCooldownDays: 7,
    boostCooldownHours: 24,
    marketCooldownHours: 24,
    walletCooldownHours: 24,
    maxDripsPerUserPerDay: 2,
    maxDripsPerWalletPerDay: 1,
    maxUsdPerUserPerDay: 0.3,
    primaryWalletOnly: true,
    minJackpotUsdForDrip: 2,
    minAccountAgeMinutes: 60,
    requirePriorActivity: true,
    maxDripsPerIpPerDay: 5,
    maxUsdPerIpPerDay: 0.75,
    maxGlobalUsdPerDay: 25,
    voucherTtlSeconds: 600,
    completionWindowMinutes: 15,
    unusedDripPenaltyHours: 48,
    maxUnusedDripsBeforePenalty: 1,
  });
  const [marketMakerMasterEnabled, setMarketMakerMasterEnabled] = useState(true);
  const [jackpotEligibleFrom, setJackpotEligibleFrom] = useState('');
  const [savingJackpotEligible, setSavingJackpotEligible] = useState(false);
  const [referralRewards, setReferralRewards] = useState({
    enabled: true,
    referralsRequired: 1,
    goldenTickets: 1,
    maxReferrals: 10,
  });
  const [referralLimitOverrides, setReferralLimitOverrides] = useState([]);
  const [referralLimitIdentifier, setReferralLimitIdentifier] = useState('');
  const [referralLimitValue, setReferralLimitValue] = useState('20');
  const [savingReferralLimit, setSavingReferralLimit] = useState(false);
  const [loadingReferralLimits, setLoadingReferralLimits] = useState(false);
  const [maxLinkedWallets, setMaxLinkedWallets] = useState(3);
  const [savingMaxWallets, setSavingMaxWallets] = useState(false);
  const [nftEditor, setNftEditor] = useState(null);
  const [giftIdentifier, setGiftIdentifier] = useState('');
  const [giftQuantityPreset, setGiftQuantityPreset] = useState('1');
  const [giftQuantityCustom, setGiftQuantityCustom] = useState('');
  const [accumIdentifier, setAccumIdentifier] = useState('');
  const [accumTicketsPreset, setAccumTicketsPreset] = useState('1');
  const [accumTicketsCustom, setAccumTicketsCustom] = useState('');
  const [accumDaysPreset, setAccumDaysPreset] = useState('7');
  const [accumDaysCustom, setAccumDaysCustom] = useState('');
  const [accumDurationPreset, setAccumDurationPreset] = useState('60');
  const [accumDurationCustom, setAccumDurationCustom] = useState('');
  const [accumMode, setAccumMode] = useState('daily');
  const [activeDailyGrants, setActiveDailyGrants] = useState([]);
  const [dailyGrantSearch, setDailyGrantSearch] = useState('');
  const [dailyGrantStatusFilter, setDailyGrantStatusFilter] = useState('all');
  const [dailyGrantPage, setDailyGrantPage] = useState(1);
  const [submittingDailyGrant, setSubmittingDailyGrant] = useState(false);
  const [grantEditor, setGrantEditor] = useState(null);
  const [savingGrantEdit, setSavingGrantEdit] = useState(false);
  const [rankingExclusions, setRankingExclusions] = useState([]);
  const [rankingExclusionIdentifier, setRankingExclusionIdentifier] = useState('');
  const [savingRankingExclusion, setSavingRankingExclusion] = useState(false);
  const [streakMilestones, setStreakMilestones] = useState([]);
  const [streakMilestoneEditor, setStreakMilestoneEditor] = useState(null);
  const [savingStreakMilestones, setSavingStreakMilestones] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    socialTwitter: '',
    socialFacebook: '',
    socialInstagram: '',
    socialYoutube: '',
    socialDiscord: '',
  });
  const [adsense, setAdsense] = useState({
    enabled: true,
    client: 'ca-pub-9986668508600694',
    slots: {
      freeSidebar: '',
      freeMobile: '',
      blogFeed: '',
      blogArticle: '',
      blogSidebar: '',
    },
  });
  const [savingAdsense, setSavingAdsense] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingTickets, setSavingTickets] = useState(false);
  const [savingNft, setSavingNft] = useState(false);
  const [savingGolden, setSavingGolden] = useState(false);
  const [savingGoldenMarket, setSavingGoldenMarket] = useState(false);
  const [savingGasDrip, setSavingGasDrip] = useState(false);
  const [savingMarketMakerMaster, setSavingMarketMakerMaster] = useState(false);
  const [savingReferralRewards, setSavingReferralRewards] = useState(false);
  const [savingSocial, setSavingSocial] = useState(false);
  const { showNotification } = useNotification();

  const fetchDailyGrants = async (status = dailyGrantStatusFilter) => {
    try {
      const { data } = await api.get('/admin/users/golden-ticket-daily-grants', {
        params: { status, limit: 200 },
      });
      setActiveDailyGrants(data?.grants || []);
    } catch {
      setActiveDailyGrants([]);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [ticketsRes, periodDaysRes, pointsRes, socialRes, rankingRes, adsenseRes] = await Promise.all([
        api.get('/admin/settings/dailyFreeTickets'),
        api.get('/admin/settings/freeTicketPeriodDays'),
        api.get('/admin/settings/pointsPerWin'),
        api.get('/admin/settings/social-links/all'),
        api.get('/admin/settings/ranking-exclusions').catch(() => ({ data: { customUsers: [] } })),
        api.get('/admin/settings/adsense').catch(() => ({ data: null })),
      ]);
      setDailyFreeTickets(ticketsRes.data?.value ?? 1);
      setFreeTicketPeriodDays(periodDaysRes.data?.value ?? 1);
      const ppw = pointsRes.data?.value;
      setPointsPerWin(typeof ppw === 'number' ? ppw : parseInt(ppw, 10) || 10);
      if (socialRes.data) {
        setSocialLinks({
          socialTwitter: socialRes.data.socialTwitter || '',
          socialFacebook: socialRes.data.socialFacebook || '',
          socialInstagram: socialRes.data.socialInstagram || '',
          socialYoutube: socialRes.data.socialYoutube || '',
          socialDiscord: socialRes.data.socialDiscord || '',
        });
      }
      setRankingExclusions(
        Array.isArray(rankingRes.data?.customUsers) ? rankingRes.data.customUsers : []
      );
      if (adsenseRes.data) {
        const retired = isRetiredAdSenseClient(adsenseRes.data.client);
        setAdsense({
          enabled: adsenseRes.data.enabled !== false,
          client: normalizeAdSenseClient(adsenseRes.data.client),
          slots: retired
            ? { freeSidebar: '', freeMobile: '', blogFeed: '', blogArticle: '', blogSidebar: '' }
            : {
                freeSidebar: adsenseRes.data.slots?.freeSidebar || '',
                freeMobile: adsenseRes.data.slots?.freeMobile || '',
                blogFeed: adsenseRes.data.slots?.blogFeed || '',
                blogArticle: adsenseRes.data.slots?.blogArticle || '',
                blogSidebar: adsenseRes.data.slots?.blogSidebar || '',
              },
        });
      }
    } catch (error) {
      console.error('Error fetching core settings:', error);
    }
    try {
      const nftRes = await api.get('/admin/settings/nftTicketBonuses');
      const list = Array.isArray(nftRes.data?.list)
        ? nftRes.data.list
        : Array.isArray(nftRes.data?.value)
          ? nftRes.data.value
          : [];
      setNftBonuses(
        list.map((n, i) => {
          const tiers = normalizeNftTiersFromRow(n);
          const top = tiers[tiers.length - 1] || DEFAULT_NFT_TIER;
          return {
            ...n,
            id: n.id || `nft-${i}`,
            tiers,
            normalTickets: top.normalTickets,
            goldenTickets: top.goldenTickets,
            dailyTickets: top.normalTickets,
            normalPeriodDays: Math.max(1, parseInt(n.normalPeriodDays, 10) || 3),
            goldenPeriodDays: Math.max(1, parseInt(n.goldenPeriodDays, 10) || 3),
          };
        })
      );
    } catch (error) {
      console.error('Error fetching NFT bonuses:', error);
      setNftBonuses([]);
    }
    try {
      const streakMsRes = await api.get('/admin/settings/streakGoldenMilestones');
      setStreakMilestones(Array.isArray(streakMsRes.data?.list) ? streakMsRes.data.list : []);
    } catch (error) {
      console.error('Error fetching streak milestones:', error);
      setStreakMilestones([]);
    }
    try {
      const [
        rateRes,
        marketRateRes,
        gasDripRes,
        grantsRes,
        referralRes,
        maxWalletsRes,
        jackpotEligRes,
        marketMakerMasterRes,
      ] = await Promise.all([
        api.get('/admin/settings/goldenTicketBoostRate'),
        api.get('/admin/settings/goldenTicketMarketRate').catch(() => ({ data: { rate: null } })),
        api.get('/admin/settings/gasDripSettings').catch(() => ({ data: { settings: null } })),
        api.get('/admin/users/golden-ticket-daily-grants', { params: { status: 'all', limit: 200 } }).catch(() => ({ data: { grants: [] } })),
        api.get('/admin/settings/referralRewards').catch(() => ({ data: { settings: null } })),
        api.get('/admin/settings/maxLinkedWallets').catch(() => ({ data: { max: 3 } })),
        api.get('/admin/settings/jackpotClaimEligibleFrom').catch(() => ({ data: { date: null } })),
        api.get('/admin/settings/marketMakerMaster').catch(() => ({ data: { enabled: true } })),
      ]);
      setMarketMakerMasterEnabled(marketMakerMasterRes.data?.enabled !== false);
      if (jackpotEligRes.data?.date) {
        setJackpotEligibleFrom(String(jackpotEligRes.data.date).slice(0, 10));
      } else {
        setJackpotEligibleFrom('');
      }
      if (maxWalletsRes.data?.max) {
        setMaxLinkedWallets(Math.max(1, parseInt(maxWalletsRes.data.max, 10) || 3));
      }
      setActiveDailyGrants(grantsRes.data?.grants || []);
      const refSettings = referralRes.data?.settings;
      if (refSettings) {
        setReferralRewards({
          enabled: refSettings.enabled !== false,
          referralsRequired: Math.max(1, parseInt(refSettings.referralsRequired, 10) || 1),
          goldenTickets: Math.max(
            0,
            parseInt(refSettings.goldenTickets ?? refSettings.goldenTicketsPerReferral, 10) || 1
          ),
          maxReferrals: Math.max(0, parseInt(refSettings.maxReferrals, 10) || 10),
        });
      }
      try {
        const limitsRes = await api.get('/admin/referral-limits');
        setReferralLimitOverrides(limitsRes.data?.overrides || []);
      } catch {
        setReferralLimitOverrides([]);
      }
      const rate = rateRes.data?.rate;
      if (rate && Number(rate.perUsdc) > 0) {
        setGoldenTicketRate({
          tickets: Math.max(0, parseInt(rate.tickets, 10) || 1),
          perUsdc: Math.max(0.01, Number(rate.perUsdc) || 10),
        });
      }
      const mRate = marketRateRes.data?.rate;
      if (mRate && Number(mRate.perUsdc) > 0) {
        setGoldenTicketMarketRate({
          tickets: Math.max(0, parseInt(mRate.tickets, 10) || 1),
          perUsdc: Math.max(0.01, Number(mRate.perUsdc) || 10),
        });
      }
      const gd = gasDripRes.data?.settings;
      if (gd) {
        setGasDripSettings({
          enabled: gd.enabled !== false,
          freeUsd: Number(gd.freeUsd) || 0.08,
          boostUsd: Number(gd.boostUsd) || 0.1,
          marketUsd: Number(gd.marketUsd) || 0.12,
          freeCooldownDays: Math.max(0, parseInt(gd.freeCooldownDays, 10) || 7),
          boostCooldownHours: Math.max(1, parseInt(gd.boostCooldownHours, 10) || 24),
          marketCooldownHours: Math.max(1, parseInt(gd.marketCooldownHours, 10) || 24),
          walletCooldownHours: Math.max(1, parseInt(gd.walletCooldownHours, 10) || 24),
          maxDripsPerUserPerDay: Math.max(1, parseInt(gd.maxDripsPerUserPerDay, 10) || 2),
          maxDripsPerWalletPerDay: Math.max(1, parseInt(gd.maxDripsPerWalletPerDay, 10) || 1),
          maxUsdPerUserPerDay: Math.max(0.01, Number(gd.maxUsdPerUserPerDay) || 0.3),
          primaryWalletOnly: gd.primaryWalletOnly !== false,
          minJackpotUsdForDrip: Math.max(0, Number(gd.minJackpotUsdForDrip) || 2),
          minAccountAgeMinutes: Math.max(0, parseInt(gd.minAccountAgeMinutes, 10) || 60),
          requirePriorActivity: gd.requirePriorActivity !== false,
          maxDripsPerIpPerDay: Math.max(1, parseInt(gd.maxDripsPerIpPerDay, 10) || 5),
          maxUsdPerIpPerDay: Math.max(0.05, Number(gd.maxUsdPerIpPerDay) || 0.75),
          maxGlobalUsdPerDay: Math.max(1, Number(gd.maxGlobalUsdPerDay) || 25),
          voucherTtlSeconds: Math.max(60, parseInt(gd.voucherTtlSeconds, 10) || 600),
          completionWindowMinutes: Math.max(5, parseInt(gd.completionWindowMinutes, 10) || 15),
          unusedDripPenaltyHours: Math.max(1, parseInt(gd.unusedDripPenaltyHours, 10) || 48),
          maxUnusedDripsBeforePenalty: Math.max(1, parseInt(gd.maxUnusedDripsBeforePenalty, 10) || 1),
        });
      }
    } catch (error) {
      console.error('Error fetching golden ticket rate:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTicketSettings = async () => {
    setSavingTickets(true);
    try {
      const ticketsPerPeriod = Math.max(0, parseInt(dailyFreeTickets, 10) || 0);
      const periodDays = Math.max(1, parseInt(freeTicketPeriodDays, 10) || 1);
      const [grantRes] = await Promise.all([
        api.post('/admin/settings/freeTicketGrant', {
          ticketsPerPeriod,
          periodDays,
          resetNow: true,
        }),
        api.post('/admin/settings/pointsPerWin', { value: parseInt(pointsPerWin, 10) || 10 }),
      ]);
      setDailyFreeTickets(grantRes.data?.ticketsPerPeriod ?? ticketsPerPeriod);
      setFreeTicketPeriodDays(grantRes.data?.periodDays ?? periodDays);
      const resetCount = grantRes.data?.reset?.usersReset;
      showNotification(
        `Ticket settings saved and normal tickets reset${resetCount != null ? ` for ${resetCount} users` : ''}`,
        'success'
      );
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save ticket settings', 'error');
    } finally {
      setSavingTickets(false);
    }
  };

  const addRankingExclusion = async (event) => {
    event?.preventDefault?.();
    const identifier = String(rankingExclusionIdentifier || '').trim();
    if (!identifier) {
      showNotification('Enter a user email or linked wallet address', 'warning');
      return;
    }
    setSavingRankingExclusion(true);
    try {
      const { data } = await api.post('/admin/settings/ranking-exclusions', { identifier });
      setRankingExclusions(Array.isArray(data?.customUsers) ? data.customUsers : []);
      setRankingExclusionIdentifier('');
      showNotification(data?.message || 'User hidden from public rankings', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Could not hide this user', 'error');
    } finally {
      setSavingRankingExclusion(false);
    }
  };

  const removeRankingExclusion = async (userId) => {
    setSavingRankingExclusion(true);
    try {
      const { data } = await api.delete(`/admin/settings/ranking-exclusions/${userId}`);
      setRankingExclusions(Array.isArray(data?.customUsers) ? data.customUsers : []);
      showNotification(data?.message || 'User restored to public rankings', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Could not restore this user', 'error');
    } finally {
      setSavingRankingExclusion(false);
    }
  };

  const saveNftBonuses = async () => {
    setSavingNft(true);
    try {
      const list = nftBonuses.map((n, i) => {
        const tiers = normalizeNftTiersFromRow(n);
        const top = tiers[tiers.length - 1] || DEFAULT_NFT_TIER;
        return {
        id: n.id || `nft-${i}`,
        name: String(n.name || '').trim(),
        contractAddress: String(n.contractAddress || '').trim(),
        imageUrl: n.imageUrl || '',
          tiers,
          normalPeriodDays: Math.max(1, parseInt(n.normalPeriodDays, 10) || 3),
          goldenPeriodDays: Math.max(1, parseInt(n.goldenPeriodDays, 10) || 3),
          normalTickets: top.normalTickets,
          goldenTickets: top.goldenTickets,
          dailyTickets: top.normalTickets,
        link: String(n.link || '').trim(),
        tokenStandard: String(n.tokenStandard || 'auto').trim() || 'auto',
        tokenId: n.tokenId != null && n.tokenId !== '' ? String(n.tokenId).trim() : '',
          registryAddress: String(n.registryAddress || '').trim(),
          accountImplementation: String(n.accountImplementation || '').trim(),
          accountSalt: n.accountSalt != null && n.accountSalt !== '' ? String(n.accountSalt).trim() : '',
          innerTokenContract: String(n.innerTokenContract || '').trim(),
          innerTokenStandard: String(n.innerTokenStandard || 'auto').trim() || 'auto',
          innerTokenId:
            n.innerTokenId != null && n.innerTokenId !== '' ? String(n.innerTokenId).trim() : '',
          requireTbaDeployed: !!n.requireTbaDeployed,
        };
      });
      const { data } = await api.post('/admin/settings/nftTicketBonuses', { list });
      const saved = Array.isArray(data?.list) ? data.list : list;
      setNftBonuses(
        saved.map((n, i) => {
          const tiers = normalizeNftTiersFromRow(n);
          const top = tiers[tiers.length - 1] || DEFAULT_NFT_TIER;
          return {
            ...n,
            id: n.id || `nft-${i}`,
            tiers,
            normalTickets: top.normalTickets,
            goldenTickets: top.goldenTickets,
            dailyTickets: top.normalTickets,
          };
        })
      );
      showNotification('NFT ticket bonuses saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save NFT bonuses', 'error');
    } finally {
      setSavingNft(false);
    }
  };

  const saveGoldenTicketRate = async () => {
    setSavingGolden(true);
    try {
      const tickets = Math.max(0, parseInt(goldenTicketRate.tickets, 10) || 0);
      const perUsdc = Math.max(0.01, Number(goldenTicketRate.perUsdc) || 10);
      const { data } = await api.post('/admin/settings/goldenTicketBoostRate', { tickets, perUsdc });
      const rate = data?.rate || { tickets, perUsdc };
      setGoldenTicketRate(rate);
      showNotification('Golden ticket boost rate saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save golden ticket rate', 'error');
    } finally {
      setSavingGolden(false);
    }
  };

  const saveGoldenTicketMarketRate = async () => {
    setSavingGoldenMarket(true);
    try {
      const tickets = Math.max(0, parseInt(goldenTicketMarketRate.tickets, 10) || 0);
      const perUsdc = Math.max(0.01, Number(goldenTicketMarketRate.perUsdc) || 10);
      const { data } = await api.post('/admin/settings/goldenTicketMarketRate', { tickets, perUsdc });
      const rate = data?.rate || { tickets, perUsdc };
      setGoldenTicketMarketRate(rate);
      showNotification('Golden ticket market rate saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save market golden ticket rate', 'error');
    } finally {
      setSavingGoldenMarket(false);
    }
  };

  const saveGasDripSettings = async () => {
    setSavingGasDrip(true);
    try {
      const payload = {
        enabled: !!gasDripSettings.enabled,
        freeUsd: Math.max(0, Number(gasDripSettings.freeUsd) || 0),
        boostUsd: Math.max(0, Number(gasDripSettings.boostUsd) || 0),
        marketUsd: Math.max(0, Number(gasDripSettings.marketUsd) || 0),
        freeCooldownDays: Math.max(0, parseInt(gasDripSettings.freeCooldownDays, 10) || 0),
        boostCooldownHours: Math.max(1, parseInt(gasDripSettings.boostCooldownHours, 10) || 24),
        marketCooldownHours: Math.max(1, parseInt(gasDripSettings.marketCooldownHours, 10) || 24),
        walletCooldownHours: Math.max(1, parseInt(gasDripSettings.walletCooldownHours, 10) || 24),
        maxDripsPerUserPerDay: Math.max(1, parseInt(gasDripSettings.maxDripsPerUserPerDay, 10) || 2),
        maxDripsPerWalletPerDay: Math.max(1, parseInt(gasDripSettings.maxDripsPerWalletPerDay, 10) || 1),
        maxUsdPerUserPerDay: Math.max(0.01, Number(gasDripSettings.maxUsdPerUserPerDay) || 0.3),
        primaryWalletOnly: !!gasDripSettings.primaryWalletOnly,
        minJackpotUsdForDrip: Math.max(0, Number(gasDripSettings.minJackpotUsdForDrip) || 0),
        minAccountAgeMinutes: Math.max(0, parseInt(gasDripSettings.minAccountAgeMinutes, 10) || 0),
        requirePriorActivity: gasDripSettings.requirePriorActivity !== false,
        maxDripsPerIpPerDay: Math.max(1, parseInt(gasDripSettings.maxDripsPerIpPerDay, 10) || 5),
        maxUsdPerIpPerDay: Math.max(0.05, Number(gasDripSettings.maxUsdPerIpPerDay) || 0.75),
        maxGlobalUsdPerDay: Math.max(1, Number(gasDripSettings.maxGlobalUsdPerDay) || 25),
        voucherTtlSeconds: Math.max(60, parseInt(gasDripSettings.voucherTtlSeconds, 10) || 600),
        completionWindowMinutes: Math.max(5, parseInt(gasDripSettings.completionWindowMinutes, 10) || 15),
        unusedDripPenaltyHours: Math.max(1, parseInt(gasDripSettings.unusedDripPenaltyHours, 10) || 48),
        maxUnusedDripsBeforePenalty: Math.max(1, parseInt(gasDripSettings.maxUnusedDripsBeforePenalty, 10) || 1),
      };
      const { data } = await api.post('/admin/settings/gasDripSettings', payload);
      if (data?.settings) setGasDripSettings((s) => ({ ...s, ...data.settings }));
      showNotification('Gas drip settings saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save gas drip settings', 'error');
    } finally {
      setSavingGasDrip(false);
    }
  };

  const toggleMarketMakerMaster = async () => {
    const enabled = !marketMakerMasterEnabled;
    setSavingMarketMakerMaster(true);
    try {
      const { data } = await api.post('/admin/settings/marketMakerMaster', { enabled });
      setMarketMakerMasterEnabled(data?.enabled !== false);
      showNotification(
        data?.enabled === false
          ? 'Market maker paused globally'
          : 'Market maker enabled globally',
        'success'
      );
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update market maker switch', 'error');
    } finally {
      setSavingMarketMakerMaster(false);
    }
  };

  const saveJackpotEligibleFrom = async ({ clear = false, syncOnChain = false } = {}) => {
    setSavingJackpotEligible(true);
    try {
      const { data } = await api.post('/admin/settings/jackpotClaimEligibleFrom', {
        date: clear ? null : jackpotEligibleFrom || null,
        recalculate: true,
        syncOnChain,
      });
      setJackpotEligibleFrom(data?.date ? String(data.date).slice(0, 10) : '');
      const n = data?.recalc?.usersUpdated;
      showNotification(
        clear
          ? 'Jackpot eligibility date cleared; balances recalculated'
          : `Jackpot eligibility saved${n != null ? ` (${n} users recalculated)` : ''}`,
        'success'
      );
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Failed to save jackpot eligibility date',
        'error'
      );
    } finally {
      setSavingJackpotEligible(false);
    }
  };

  const saveMaxLinkedWallets = async () => {
    setSavingMaxWallets(true);
    try {
      const { data } = await api.post('/admin/settings/maxLinkedWallets', {
        max: Math.max(1, Math.min(10, parseInt(maxLinkedWallets, 10) || 3)),
      });
      if (data?.max) setMaxLinkedWallets(data.max);
      showNotification('Max linked wallets saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save max wallets', 'error');
    } finally {
      setSavingMaxWallets(false);
    }
  };

  const saveReferralRewards = async () => {
    setSavingReferralRewards(true);
    try {
      const referralsRequired = Math.max(1, parseInt(referralRewards.referralsRequired, 10) || 1);
      const goldenTickets = Math.max(0, parseInt(referralRewards.goldenTickets, 10) || 0);
      const maxReferrals = Math.max(0, parseInt(referralRewards.maxReferrals, 10) || 10);
      const { data } = await api.post('/admin/settings/referralRewards', {
        enabled: referralRewards.enabled,
        referralsRequired,
        goldenTickets,
        maxReferrals,
      });
      const settings = data?.settings || {
        enabled: referralRewards.enabled,
        referralsRequired,
        goldenTickets,
        maxReferrals,
      };
      setReferralRewards({
        enabled: settings.enabled !== false,
        referralsRequired: Math.max(1, parseInt(settings.referralsRequired, 10) || 1),
        goldenTickets: Math.max(0, parseInt(settings.goldenTickets, 10) || 0),
        maxReferrals: Math.max(0, parseInt(settings.maxReferrals, 10) || 10),
      });
      showNotification('Referral rewards saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save referral rewards', 'error');
    } finally {
      setSavingReferralRewards(false);
    }
  };

  const fetchReferralLimitOverrides = async () => {
    setLoadingReferralLimits(true);
    try {
      const { data } = await api.get('/admin/referral-limits');
      setReferralLimitOverrides(data?.overrides || []);
    } catch {
      setReferralLimitOverrides([]);
    } finally {
      setLoadingReferralLimits(false);
    }
  };

  const saveUserReferralLimit = async () => {
    const identifier = String(referralLimitIdentifier || '').trim();
    if (!identifier) {
      showNotification('Enter a username, email, or wallet address', 'warning');
      return;
    }
    const maxReferrals = parseInt(referralLimitValue, 10);
    if (!Number.isInteger(maxReferrals) || maxReferrals < 0) {
      showNotification('Limit must be a non-negative whole number', 'warning');
      return;
    }
    setSavingReferralLimit(true);
    try {
      const { data } = await api.post('/admin/referral-limits', { identifier, maxReferrals });
      showNotification(data?.message || 'Referral limit updated', 'success');
      setReferralLimitIdentifier('');
      await fetchReferralLimitOverrides();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to set referral limit', 'error');
    } finally {
      setSavingReferralLimit(false);
    }
  };

  const deleteUserReferralLimit = async (userId) => {
    if (!userId) return;
    try {
      await api.delete(`/admin/referral-limits/${userId}`);
      showNotification('Custom limit removed — user uses global default', 'success');
      await fetchReferralLimitOverrides();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to remove limit', 'error');
    }
  };

  const saveSocialLinks = async () => {
    setSavingSocial(true);
    try {
      await api.post('/admin/settings/social-links', socialLinks);
      showNotification('Social links saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save social links', 'error');
    } finally {
      setSavingSocial(false);
    }
  };

  const saveAdsense = async () => {
    setSavingAdsense(true);
    try {
      const { data } = await api.post('/admin/settings/adsense', adsense);
      if (data?.config) setAdsense(data.config);
      showNotification('AdSense settings saved', 'success');
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save AdSense settings', 'error');
    } finally {
      setSavingAdsense(false);
    }
  };

  const applyNftEditor = () => {
    if (!nftEditor) return;
    const { _editIndex, ...draft } = nftEditor;
    const tiers = normalizeNftTiersFromRow(draft);
    const top = tiers[tiers.length - 1] || DEFAULT_NFT_TIER;
    const entry = {
      ...draft,
      id: draft.id || `nft-${Date.now()}`,
      name: String(draft.name || '').trim(),
      contractAddress: String(draft.contractAddress || '').trim(),
      tiers,
      normalPeriodDays: Math.max(1, parseInt(draft.normalPeriodDays, 10) || 3),
      goldenPeriodDays: Math.max(1, parseInt(draft.goldenPeriodDays, 10) || 3),
      normalTickets: top.normalTickets,
      goldenTickets: top.goldenTickets,
      dailyTickets: top.normalTickets,
      link: String(draft.link || '').trim(),
      tokenStandard: String(draft.tokenStandard || 'auto').trim() || 'auto',
      tokenId: draft.tokenId != null && draft.tokenId !== '' ? String(draft.tokenId).trim() : '',
      registryAddress: String(draft.registryAddress || '').trim(),
      accountImplementation: String(draft.accountImplementation || '').trim(),
      accountSalt: draft.accountSalt != null && draft.accountSalt !== '' ? String(draft.accountSalt).trim() : '',
      innerTokenContract: String(draft.innerTokenContract || '').trim(),
      innerTokenStandard: String(draft.innerTokenStandard || 'auto').trim() || 'auto',
      innerTokenId:
        draft.innerTokenId != null && draft.innerTokenId !== '' ? String(draft.innerTokenId).trim() : '',
      requireTbaDeployed: !!draft.requireTbaDeployed,
      imageUrl: draft.imageUrl || '',
    };
    if (!entry.name && !entry.contractAddress) {
      showNotification('Name or contract address is required', 'warning');
      return;
    }
    if (_editIndex >= 0) {
      const next = [...nftBonuses];
      next[_editIndex] = entry;
      setNftBonuses(next);
    } else {
      setNftBonuses([...nftBonuses, entry]);
    }
    setNftEditor(null);
  };

  const giftGoldenTickets = async () => {
    const raw = String(giftIdentifier || '').trim();
    const qty = resolveGoldenPresetNumber(giftQuantityPreset, giftQuantityCustom);
    if (!raw) {
      showNotification('Enter user email or wallet address', 'warning');
      return;
    }
    if (!qty) {
      showNotification('Select or enter a valid ticket quantity (1 or more)', 'warning');
      return;
    }
    const body = { quantity: qty, identifier: raw };
    if (raw.includes('@')) body.email = raw;
    else body.walletAddress = raw;
    try {
      const { data } = await api.post('/admin/users/gift-golden-tickets', body);
      showNotification(
        `Gifted ${qty} golden ticket(s). New balance: ${data.goldenTickets ?? '—'}`,
        'success'
      );
      setGiftIdentifier('');
      setGiftQuantityPreset('1');
      setGiftQuantityCustom('');
    } catch (e) {
      showNotification(e.response?.data?.message || 'Gift failed', 'error');
    }
  };

  const createDailyGoldenGrant = async () => {
    const raw = String(accumIdentifier || '').trim();
    const ticketsPerDay = resolveGoldenPresetNumber(accumTicketsPreset, accumTicketsCustom);
    const days = resolveGoldenPresetNumber(accumDaysPreset, accumDaysCustom);
    const totalDays =
      accumMode === 'range'
        ? resolveGoldenPresetNumber(accumDurationPreset, accumDurationCustom)
        : null;
    if (!raw) {
      showNotification('Enter user email or wallet address', 'warning');
      return;
    }
    if (!ticketsPerDay) {
      showNotification('Select or enter ticket amount (1 or more)', 'warning');
      return;
    }
    if (!days) {
      showNotification(
        accumMode === 'range'
          ? 'Select or enter interval in days (1 or more)'
          : 'Select or enter duration in days (1 or more)',
        'warning'
      );
      return;
    }
    if (accumMode === 'range' && !totalDays) {
      showNotification('Select or enter how many total days the range drip should last', 'warning');
      return;
    }
    const body = {
      ticketsPerDay,
      days,
      ...(accumMode === 'range' ? { totalDays } : {}),
      accumulationMode: accumMode,
      identifier: raw,
    };
    if (raw.includes('@')) body.email = raw;
    else body.walletAddress = raw;
    setSubmittingDailyGrant(true);
    try {
      const { data } = await api.post('/admin/users/golden-ticket-daily-grant', body);
      showNotification(
        data.message ||
          `Scheduled ${ticketsPerDay} ticket(s). Balance: ${data.user?.goldenTickets ?? '—'}`,
        'success'
      );
      setAccumIdentifier('');
      setAccumTicketsPreset('1');
      setAccumTicketsCustom('');
      setAccumDaysPreset('7');
      setAccumDaysCustom('');
      setAccumDurationPreset('60');
      setAccumDurationCustom('');
      await fetchDailyGrants('all');
      setDailyGrantStatusFilter('all');
    } catch (e) {
      showNotification(e.response?.data?.message || 'Failed to create grant schedule', 'error');
    } finally {
      setSubmittingDailyGrant(false);
    }
  };

  const openGrantEditor = (g) => {
    const mode = g.accumulationMode === 'range' ? 'range' : 'daily';
    const dayVal = mode === 'range' ? g.intervalDays || g.daysTotal || 7 : g.daysTotal || 7;
    const inferredDuration =
      g.endDate && g.startDate
        ? Math.max(
            1,
            Math.ceil(
              (new Date(g.endDate).getTime() - new Date(g.startDate).getTime()) /
                (24 * 60 * 60 * 1000)
            )
          )
        : null;
    const durationVal = mode === 'range' ? g.durationDays || inferredDuration || 60 : null;
    const presetDays = GOLDEN_DAY_PRESETS.includes(String(dayVal)) ? String(dayVal) : 'custom';
    const presetDuration =
      mode === 'range' && GOLDEN_DURATION_PRESETS.includes(String(durationVal))
        ? String(durationVal)
        : 'custom';
    const presetTickets = GOLDEN_TICKET_PRESETS.includes(String(g.ticketsPerDay))
      ? String(g.ticketsPerDay)
      : 'custom';
    setGrantEditor({
      _id: g._id,
      accumulationMode: mode,
      ticketsPreset: presetTickets,
      ticketsCustom: presetTickets === 'custom' ? String(g.ticketsPerDay || '') : '',
      daysPreset: presetDays,
      daysCustom: presetDays === 'custom' ? String(dayVal) : '',
      durationPreset: presetDuration,
      durationCustom: presetDuration === 'custom' ? String(durationVal || '') : '',
      active: g.active !== false,
      note: g.note || '',
      userLabel: g.user?.email || g.user?.username || g.recipientEmail || shortAddr(g.recipientWallet),
    });
  };

  const saveGrantEdit = async () => {
    if (!grantEditor?._id) return;
    const ticketsPerDay = resolveGoldenPresetNumber(grantEditor.ticketsPreset, grantEditor.ticketsCustom);
    const days = resolveGoldenPresetNumber(grantEditor.daysPreset, grantEditor.daysCustom);
    const totalDays =
      grantEditor.accumulationMode === 'range'
        ? resolveGoldenPresetNumber(grantEditor.durationPreset, grantEditor.durationCustom)
        : null;
    if (!ticketsPerDay || !days || (grantEditor.accumulationMode === 'range' && !totalDays)) {
      showNotification('Tickets and days must be valid positive numbers', 'warning');
      return;
    }
    setSavingGrantEdit(true);
    try {
      await api.patch(`/admin/users/golden-ticket-daily-grants/${grantEditor._id}`, {
        ticketsPerDay,
        days,
        ...(grantEditor.accumulationMode === 'range' ? { totalDays } : {}),
        accumulationMode: grantEditor.accumulationMode,
        active: grantEditor.active,
        note: grantEditor.note,
      });
      showNotification('Grant schedule updated', 'success');
      setGrantEditor(null);
      await fetchDailyGrants(dailyGrantStatusFilter);
    } catch (e) {
      showNotification(e.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSavingGrantEdit(false);
    }
  };

  const cancelDailyGrant = async (grantId) => {
    try {
      await api.delete(`/admin/users/golden-ticket-daily-grants/${grantId}`);
      showNotification('Grant schedule cancelled', 'success');
      setActiveDailyGrants((prev) =>
        prev.map((g) => (g._id === grantId ? { ...g, active: false } : g))
      );
      if (grantEditor?._id === grantId) setGrantEditor(null);
    } catch (e) {
      showNotification(e.response?.data?.message || 'Cancel failed', 'error');
    }
  };

  const deleteDailyGrant = async (grantId) => {
    if (!window.confirm('Permanently delete this grant record?')) return;
    try {
      await api.delete(`/admin/users/golden-ticket-daily-grants/${grantId}`, { params: { hard: true } });
      showNotification('Grant schedule deleted', 'success');
      setActiveDailyGrants((prev) => prev.filter((g) => g._id !== grantId));
      if (grantEditor?._id === grantId) setGrantEditor(null);
    } catch (e) {
      showNotification(e.response?.data?.message || 'Delete failed', 'error');
    }
  };

  const grantRateLabel = (g) => {
    const mode = g.accumulationMode === 'range' ? 'range' : 'daily';
    if (mode === 'range') {
      return `${g.ticketsPerDay} every ${g.intervalDays || g.daysTotal || '—'}d`;
    }
    return `${g.ticketsPerDay}/UTC day`;
  };

  const grantProgressLabel = (g) => {
    if (g.accumulationMode === 'range') {
      const duration = g.durationDays || '—';
      const elapsed =
        g.startDate && Number(g.durationDays) > 0
          ? Math.min(
              Number(g.durationDays),
              Math.max(
                0,
                Math.floor(
                  (Date.now() - new Date(g.startDate).getTime()) / (24 * 60 * 60 * 1000)
                ) + 1
              )
            )
          : '—';
      return `${g.daysGranted ?? 0} grants · ${elapsed}/${duration} days`;
    }
    return `${g.daysGranted ?? 0} / ${g.daysTotal ?? '—'} days`;
  };

  const saveStreakMilestones = async () => {
    setSavingStreakMilestones(true);
    try {
      const list = streakMilestones.map((m, i) => ({
        id: m.id || `sm-${i}`,
        streakType: m.streakType || 'total',
        requiredStreak: Math.max(1, parseInt(m.requiredStreak, 10) || 1),
        goldenTickets: Math.max(1, parseInt(m.goldenTickets, 10) || 1),
        active: m.active !== false,
      }));
      const { data } = await api.post('/admin/settings/streakGoldenMilestones', { list });
      setStreakMilestones(Array.isArray(data?.list) ? data.list : list);
      showNotification('Streak milestone rewards saved', 'success');
    } catch (e) {
      showNotification(e.response?.data?.message || 'Failed to save streak milestones', 'error');
    } finally {
      setSavingStreakMilestones(false);
    }
  };

  const applyStreakMilestoneEditor = () => {
    if (!streakMilestoneEditor) return;
    const { _editIndex, ...draft } = streakMilestoneEditor;
    const entry = {
      ...draft,
      id: draft.id || `sm-${Date.now()}`,
      streakType: draft.streakType || 'total',
      requiredStreak: Math.max(1, parseInt(draft.requiredStreak, 10) || 1),
      goldenTickets: Math.max(1, parseInt(draft.goldenTickets, 10) || 1),
      active: draft.active !== false,
    };
    if (_editIndex >= 0) {
      const next = [...streakMilestones];
      next[_editIndex] = entry;
      setStreakMilestones(next);
    } else {
      setStreakMilestones([...streakMilestones, entry]);
    }
    setStreakMilestoneEditor(null);
  };

  const DAILY_GRANTS_PAGE_SIZE = 5;
  const dailyGrantQuery = String(dailyGrantSearch || '').trim().toLowerCase();
  const filteredDailyGrants = (activeDailyGrants || []).filter((g) => {
    if (dailyGrantStatusFilter === 'active' && !g.active) return false;
    if (dailyGrantStatusFilter === 'inactive' && g.active) return false;
    if (!dailyGrantQuery) return true;
    const email = String(g?.user?.email || g?.recipientEmail || '').toLowerCase();
    const wallet = String(g?.recipientWallet || '').toLowerCase();
    const username = String(g?.user?.username || '').toLowerCase();
    const note = String(g?.note || '').toLowerCase();
    return (
      email.includes(dailyGrantQuery) ||
      wallet.includes(dailyGrantQuery) ||
      username.includes(dailyGrantQuery) ||
      note.includes(dailyGrantQuery)
    );
  });
  const dailyGrantTotalPages = Math.max(1, Math.ceil(filteredDailyGrants.length / DAILY_GRANTS_PAGE_SIZE));
  const safeDailyGrantPage = Math.min(Math.max(1, dailyGrantPage), dailyGrantTotalPages);
  const dailyGrantStart = (safeDailyGrantPage - 1) * DAILY_GRANTS_PAGE_SIZE;
  const pagedDailyGrants = filteredDailyGrants.slice(dailyGrantStart, dailyGrantStart + DAILY_GRANTS_PAGE_SIZE);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Settings</h2>
      
      <div className="space-y-6">
        <HeroFeaturedMatchSetting showNotification={showNotification} />

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Public ranking visibility
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                Hide internal or custom accounts from the leaderboard and public streak rankings,
                including the landing-page previews. Their private streak progress and rewards are not changed.
              </p>
            </div>
            <span className="inline-flex self-start px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              Admin roles hidden automatically
            </span>
          </div>

          <form onSubmit={addRankingExclusion} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={rankingExclusionIdentifier}
              onChange={(event) => setRankingExclusionIdentifier(event.target.value)}
              placeholder="User email or linked wallet address (0x…)"
              className="flex-1 min-w-0 px-4 py-2.5 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <button
              type="submit"
              disabled={savingRankingExclusion || !rankingExclusionIdentifier.trim()}
              className="px-5 py-2.5 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-semibold text-sm hover:opacity-90 disabled:opacity-50"
            >
              {savingRankingExclusion ? 'Updating…' : 'Hide account'}
            </button>
          </form>

          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Admins, super admins, and the internal market-maker account remain hidden even if they are
            not listed below.
          </p>

          <div className="mt-5">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Custom hidden accounts ({rankingExclusions.length})
            </p>
            {rankingExclusions.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {rankingExclusions.map((account) => (
                  <div
                    key={account._id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {account.username || account.email || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {account.email || (account.walletAddress ? shortAddr(account.walletAddress) : account._id)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRankingExclusion(account._id)}
                      disabled={savingRankingExclusion}
                      className="shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 disabled:opacity-50"
                    >
                      Show again
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 py-5 text-sm text-gray-500 dark:text-gray-400 text-center">
                No custom accounts are hidden.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ticket settings</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Free tickets per period
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  How many normal free tickets each user receives each period (does not accumulate if unused).
              </p>
              <input
                type="number"
                min="0"
                value={dailyFreeTickets}
                onChange={(e) => setDailyFreeTickets(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Period (days)
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Length of each grant period in days (1 = daily, 7 = weekly, etc.).
                </p>
                <input
                  type="number"
                  min="1"
                  value={freeTicketPeriodDays}
                  onChange={(e) => setFreeTicketPeriodDays(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 px-3 py-2">
              Users get{' '}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {Math.max(0, parseInt(dailyFreeTickets, 10) || 0)} ticket
                {(parseInt(dailyFreeTickets, 10) || 0) === 1 ? '' : 's'}
              </span>{' '}
              every{' '}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {Math.max(1, parseInt(freeTicketPeriodDays, 10) || 1)} day
                {(parseInt(freeTicketPeriodDays, 10) || 1) === 1 ? '' : 's'}
              </span>
              . Unused platform tickets do not carry into the next period. Saving these settings resets
              every user’s platform normal-ticket allowance immediately. NFT holder bonuses remain separate.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Points per Win
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                Points awarded to users for each winning prediction
              </p>
              <input
                type="number"
                min="1"
                value={pointsPerWin}
                onChange={(e) => setPointsPerWin(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                placeholder="10"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={saveTicketSettings}
            disabled={savingTickets}
            className="mt-4 px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
          >
            {savingTickets ? 'Saving and resetting…' : 'Save settings & reset normal tickets now'}
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">NFT ticket bonuses</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Configure holding tiers for linked wallets (verified on-chain). Supports ERC-721, ERC-1155, ERC-20,
            and ERC-6551 (Token Bound Accounts). Higher holdings unlock better rates.
          </p>
          {nftBonuses.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
              <table className="w-full text-sm min-w-[860px]">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="p-3">Collection</th>
                    <th className="p-3">Contract</th>
                    <th className="p-3">Reward tiers</th>
                    <th className="p-3 text-right">Periods</th>
                    <th className="p-3">Link</th>
                    <th className="p-3 text-right w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {nftBonuses.map((n, i) => (
                    <tr key={n.id || i} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {n.imageUrl ? (
                            <img src={n.imageUrl} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
                          ) : (
                            <span className="w-9 h-9 rounded bg-gray-200 dark:bg-gray-600 shrink-0" />
                          )}
                          <span className="font-medium text-gray-900 dark:text-white">{n.name || '—'}</span>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs text-gray-600 dark:text-gray-400" title={n.contractAddress}>
                        {shortAddr(n.contractAddress)}
                      </td>
                      <td className="p-3 text-xs text-gray-700 dark:text-gray-300 max-w-[280px]">
                        {formatNftTierSummary(n)}
                      </td>
                      <td className="p-3 text-right tabular-nums text-xs whitespace-nowrap">
                        <div>Normal / {n.normalPeriodDays || 3}d</div>
                        <div className="text-amber-700 dark:text-amber-300">Golden / {n.goldenPeriodDays || 3}d</div>
                      </td>
                      <td className="p-3 max-w-[140px] truncate">
                        {n.link ? (
                          <a href={n.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-xs">
                            View
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            title="Edit"
                            onClick={() =>
                              setNftEditor({
                                ...n,
                                tiers: normalizeNftTiersFromRow(n),
                                _editIndex: i,
                              })
                            }
                            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          >
                            <SettingsIconEdit />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => setNftBonuses(nftBonuses.filter((_, j) => j !== i))}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                          >
                            <SettingsIconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 italic">No NFT bonuses configured yet.</p>
          )}
          {nftEditor && (
            <div className="border rounded-lg p-4 dark:border-gray-700 space-y-3 mb-4 bg-gray-50 dark:bg-gray-900/40">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {nftEditor._editIndex >= 0 ? 'Edit NFT bonus' : 'Add NFT bonus'}
              </p>
              <input
                type="text"
                placeholder="Collection name"
                value={nftEditor.name || ''}
                onChange={(e) => setNftEditor({ ...nftEditor, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
              <input
                type="text"
                placeholder="Token contract address (0x…)"
                value={nftEditor.contractAddress || ''}
                onChange={(e) => setNftEditor({ ...nftEditor, contractAddress: e.target.value.trim() })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm font-mono"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Token type</label>
                  <select
                    value={nftEditor.tokenStandard || 'auto'}
                    onChange={(e) => setNftEditor({ ...nftEditor, tokenStandard: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="auto">Auto-detect</option>
                    <option value="erc721">ERC-721 (NFT)</option>
                    <option value="erc1155">ERC-1155 (FT)</option>
                    <option value="erc20">ERC-20 (fungible)</option>
                    <option value="erc6551">ERC-6551 (Token Bound Account)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Token ID (ERC-1155 / specific NFT)</label>
                  <input
                    type="text"
                    placeholder="0 (default)"
                    value={nftEditor.tokenId ?? ''}
                    onChange={(e) => setNftEditor({ ...nftEditor, tokenId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>
              {(nftEditor.tokenStandard || 'auto') === 'erc6551' && (
                <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/20 p-3 space-y-2">
                  <p className="text-xs font-semibold text-violet-900 dark:text-violet-200">
                    ERC-6551 (Token Bound Account)
                  </p>
                  <p className="text-xs text-violet-800/80 dark:text-violet-300/80 leading-relaxed">
                    Contract address above = parent ERC-721 collection. By default we count NFTs held in the linked
                    wallet. Leave registry/implementation blank to use canonical defaults. Optionally verify assets
                    held inside each NFT&apos;s token-bound account.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Registry (optional)</label>
                      <input
                        type="text"
                        placeholder="0x000000006551c19487814612e58FE06813775758"
                        value={nftEditor.registryAddress || ''}
                        onChange={(e) => setNftEditor({ ...nftEditor, registryAddress: e.target.value.trim() })}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Account implementation (optional)</label>
                      <input
                        type="text"
                        placeholder="Tokenbound proxy / custom implementation"
                        value={nftEditor.accountImplementation || ''}
                        onChange={(e) =>
                          setNftEditor({ ...nftEditor, accountImplementation: e.target.value.trim() })
                        }
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Inner token contract (optional)</label>
                      <input
                        type="text"
                        placeholder="Asset held inside TBA (0x…)"
                        value={nftEditor.innerTokenContract || ''}
                        onChange={(e) =>
                          setNftEditor({ ...nftEditor, innerTokenContract: e.target.value.trim() })
                        }
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Inner token type</label>
                      <select
                        value={nftEditor.innerTokenStandard || 'auto'}
                        onChange={(e) => setNftEditor({ ...nftEditor, innerTokenStandard: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                      >
                        <option value="auto">Auto-detect</option>
                        <option value="erc20">ERC-20</option>
                        <option value="erc1155">ERC-1155</option>
                        <option value="erc721">ERC-721</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Inner token ID (ERC-1155)</label>
                      <input
                        type="text"
                        placeholder="0"
                        value={nftEditor.innerTokenId ?? ''}
                        onChange={(e) => setNftEditor({ ...nftEditor, innerTokenId: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!nftEditor.requireTbaDeployed}
                          onChange={(e) =>
                            setNftEditor({ ...nftEditor, requireTbaDeployed: e.target.checked })
                          }
                          className="rounded"
                        />
                        Require TBA deployed on-chain
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <ImageUpload
                label="Image"
                value={nftEditor.imageUrl || ''}
                onChange={(url) => setNftEditor({ ...nftEditor, imageUrl: url })}
                folder="wergame/nft-bonuses"
              />
              <div className="grid grid-cols-2 gap-2 max-w-md">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Normal refresh every (days)</label>
              <input
                type="number"
                    min="1"
                    value={nftEditor.normalPeriodDays ?? 3}
                    onChange={(e) =>
                      setNftEditor({ ...nftEditor, normalPeriodDays: parseInt(e.target.value, 10) || 3 })
                    }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Golden grant every (days)</label>
                  <input
                    type="number"
                    min="1"
                    value={nftEditor.goldenPeriodDays ?? 3}
                    onChange={(e) =>
                      setNftEditor({ ...nftEditor, goldenPeriodDays: parseInt(e.target.value, 10) || 3 })
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Holding tiers
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const tiers = normalizeNftTiersFromRow(nftEditor);
                      const lastMin = tiers.length ? tiers[tiers.length - 1].minHoldings : 1;
                      setNftEditor({
                        ...nftEditor,
                        tiers: [
                          ...tiers,
                          { minHoldings: lastMin + 1, normalTickets: 0, goldenTickets: 0 },
                        ],
                      });
                    }}
                    className="text-xs font-semibold text-green-700 dark:text-green-400 hover:underline"
                  >
                    + Add tier
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Example: 1+ → 1 normal / 0 golden; 20+ → 2 normal / 5 golden. The highest tier the user qualifies
                  for applies.
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm min-w-[420px]">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="p-2 text-left">Min held</th>
                        <th className="p-2 text-right">Normal</th>
                        <th className="p-2 text-right">Golden</th>
                        <th className="p-2 w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {normalizeNftTiersFromRow(nftEditor).map((tier, ti) => (
                        <tr key={ti} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="p-2">
                            <input
                              type="number"
                              min="1"
                              value={tier.minHoldings}
                              onChange={(e) => {
                                const tiers = normalizeNftTiersFromRow(nftEditor);
                                tiers[ti] = {
                                  ...tiers[ti],
                                  minHoldings: Math.max(1, parseInt(e.target.value, 10) || 1),
                                };
                                setNftEditor({ ...nftEditor, tiers });
                              }}
                              className="w-full px-2 py-1.5 border rounded dark:bg-gray-700 dark:text-white text-sm"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              min="0"
                              value={tier.normalTickets}
                              onChange={(e) => {
                                const tiers = normalizeNftTiersFromRow(nftEditor);
                                tiers[ti] = {
                                  ...tiers[ti],
                                  normalTickets: Math.max(0, parseInt(e.target.value, 10) || 0),
                                };
                                setNftEditor({ ...nftEditor, tiers });
                              }}
                              className="w-full px-2 py-1.5 border rounded dark:bg-gray-700 dark:text-white text-sm text-right"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              min="0"
                              value={tier.goldenTickets}
                              onChange={(e) => {
                                const tiers = normalizeNftTiersFromRow(nftEditor);
                                tiers[ti] = {
                                  ...tiers[ti],
                                  goldenTickets: Math.max(0, parseInt(e.target.value, 10) || 0),
                                };
                                setNftEditor({ ...nftEditor, tiers });
                              }}
                              className="w-full px-2 py-1.5 border rounded dark:bg-gray-700 dark:text-white text-sm text-right"
                            />
                          </td>
                          <td className="p-2 text-center">
                            {normalizeNftTiersFromRow(nftEditor).length > 1 ? (
                              <button
                                type="button"
                                title="Remove tier"
                                onClick={() => {
                                  const tiers = normalizeNftTiersFromRow(nftEditor).filter((_, j) => j !== ti);
                                  setNftEditor({ ...nftEditor, tiers });
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                              >
                                <SettingsIconTrash className="w-3.5 h-3.5" />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <input
                type="url"
                placeholder="Buy / collection link"
                value={nftEditor.link || ''}
                onChange={(e) => setNftEditor({ ...nftEditor, link: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
              <div className="flex gap-2">
                <button type="button" onClick={applyNftEditor} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold">
                  {nftEditor._editIndex >= 0 ? 'Update row' : 'Add to list'}
                </button>
                <button type="button" onClick={() => setNftEditor(null)} className="px-4 py-2 border rounded-lg text-sm dark:border-gray-600">
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={() => setNftEditor({ ...emptyNftBonus(), _editIndex: -1 })}
              className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold"
            >
              + Add NFT bonus
            </button>
            <button
              type="button"
              onClick={saveNftBonuses}
              disabled={savingNft}
              className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
            >
              {savingNft ? 'Saving…' : 'Save NFT ticket bonuses'}
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Golden tickets on boost</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            How many golden tickets users earn per USDC staked on boost or add-stake. Tickets are floored to a
            whole number — extra dollars below a full increment never round up (e.g. $18 at 1 per $10 → 1
            ticket).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mb-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Golden tickets
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={goldenTicketRate.tickets ?? 1}
                onChange={(e) =>
                  setGoldenTicketRate((r) => ({ ...r, tickets: parseInt(e.target.value, 10) || 1 }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Per USDC staked
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={goldenTicketRate.perUsdc ?? 10}
                onChange={(e) =>
                  setGoldenTicketRate((r) => ({ ...r, perUsdc: parseFloat(e.target.value) || 10 }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          </div>
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
            Preview:{' '}
            <strong>
              {goldenTicketRate.tickets || 1} golden ticket{(goldenTicketRate.tickets || 1) === 1 ? '' : 's'} per each $
              {goldenTicketRate.perUsdc || 10} USDC (floored)
            </strong>
          </p>
          <button
            type="button"
            onClick={saveGoldenTicketRate}
            disabled={savingGolden}
            className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
          >
            {savingGolden ? 'Saving…' : 'Save golden ticket rate'}
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Golden tickets on market buys</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Award golden tickets when users buy on the orderbook (filled USDC notional). Set tickets to 0 to disable.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mb-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Golden tickets
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={goldenTicketMarketRate.tickets ?? 1}
                onChange={(e) =>
                  setGoldenTicketMarketRate((r) => ({
                    ...r,
                    tickets: parseInt(e.target.value, 10) || 0,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Per USDC bought
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={goldenTicketMarketRate.perUsdc ?? 10}
                onChange={(e) =>
                  setGoldenTicketMarketRate((r) => ({
                    ...r,
                    perUsdc: parseFloat(e.target.value) || 10,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          </div>
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
            Preview:{' '}
            <strong>
              {goldenTicketMarketRate.tickets || 0} golden ticket
              {(goldenTicketMarketRate.tickets || 0) === 1 ? '' : 's'} per $
              {goldenTicketMarketRate.perUsdc || 10} USDC bought
            </strong>
          </p>
          <button
            type="button"
            onClick={saveGoldenTicketMarketRate}
            disabled={savingGoldenMarket}
            className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
          >
            {savingGoldenMarket ? 'Saving…' : 'Save market golden ticket rate'}
          </button>
        </div>

        <div
          className={`rounded-lg border-2 p-6 shadow-md ${
            marketMakerMasterEnabled
              ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
              : 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30'
          }`}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Master market maker switch
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Status:{' '}
                <strong className={marketMakerMasterEnabled ? 'text-emerald-700' : 'text-red-700'}>
                  {marketMakerMasterEnabled ? 'RUNNING' : 'PAUSED'}
                </strong>
              </p>
              <p className="mt-2 max-w-3xl text-xs text-gray-500 dark:text-gray-400">
                Pausing stops scheduled maintenance, automatic requotes, liquidity absorption, and
                order-triggered market-maker work across every match and poll. User market pages remain
                available.
              </p>
            </div>
            <button
              type="button"
              onClick={toggleMarketMakerMaster}
              disabled={savingMarketMakerMaster}
              className={`shrink-0 rounded-lg px-5 py-3 text-sm font-bold text-white disabled:opacity-50 ${
                marketMakerMasterEnabled
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {savingMarketMakerMaster
                ? 'Updating…'
                : marketMakerMasterEnabled
                  ? 'Pause market maker globally'
                  : 'Unpause market maker globally'}
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Relayer gas drip</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            USD-worth of Base ETH sent when a user needs gas. All play types are cooldown-limited with
            daily caps so the relayer cannot be drained. Single drip hard-capped at $0.15.
          </p>
          <label className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-800 dark:text-gray-200">
            <input
              type="checkbox"
              checked={!!gasDripSettings.enabled}
              onChange={(e) => setGasDripSettings((s) => ({ ...s, enabled: e.target.checked }))}
              className="rounded border-gray-300"
            />
            Drip enabled (uncheck to kill-switch immediately)
          </label>
          <label className="flex items-center gap-2 mb-4 text-sm font-medium text-gray-800 dark:text-gray-200">
            <input
              type="checkbox"
              checked={gasDripSettings.primaryWalletOnly !== false}
              onChange={(e) =>
                setGasDripSettings((s) => ({ ...s, primaryWalletOnly: e.target.checked }))
              }
              className="rounded border-gray-300"
            />
            Primary / first linked wallet only (gas drip)
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3 mb-4 ml-6">
            When on, only the oldest linked wallet on the account can use the relayer. Other wallets must fund Base ETH.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Min Free jackpot for drip ($)
              </label>
              <input
                type="number"
                min="0"
                max="1000"
                step="0.01"
                value={gasDripSettings.minJackpotUsdForDrip ?? 2}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    minJackpotUsdForDrip: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                Users need at least this much claimable Free jackpot before gas drip. Below that they
                must fund Base ETH themselves.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Min account age (minutes)
              </label>
              <input
                type="number"
                min="0"
                max="10080"
                step="1"
                value={gasDripSettings.minAccountAgeMinutes ?? 60}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    minAccountAgeMinutes: parseInt(e.target.value, 10) || 0,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                Brand-new accounts cannot request drip until this age.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Free drip ($)
              </label>
              <input
                type="number"
                min="0"
                max="0.5"
                step="0.01"
                value={gasDripSettings.freeUsd}
                onChange={(e) =>
                  setGasDripSettings((s) => ({ ...s, freeUsd: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Boost drip ($)
              </label>
              <input
                type="number"
                min="0"
                max="0.5"
                step="0.01"
                value={gasDripSettings.boostUsd}
                onChange={(e) =>
                  setGasDripSettings((s) => ({ ...s, boostUsd: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Market drip ($)
              </label>
              <input
                type="number"
                min="0"
                max="0.5"
                step="0.01"
                value={gasDripSettings.marketUsd}
                onChange={(e) =>
                  setGasDripSettings((s) => ({ ...s, marketUsd: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Free cooldown (days)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={gasDripSettings.freeCooldownDays}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    freeCooldownDays: parseInt(e.target.value, 10) || 0,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Boost cooldown (hours)
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={gasDripSettings.boostCooldownHours ?? 24}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    boostCooldownHours: parseInt(e.target.value, 10) || 24,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Market cooldown (hours)
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={gasDripSettings.marketCooldownHours ?? 24}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    marketCooldownHours: parseInt(e.target.value, 10) || 24,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Wallet cooldown (hours)
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={gasDripSettings.walletCooldownHours ?? 6}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    walletCooldownHours: parseInt(e.target.value, 10) || 6,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Max drips / user / day
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={gasDripSettings.maxDripsPerUserPerDay ?? 3}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    maxDripsPerUserPerDay: parseInt(e.target.value, 10) || 3,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Max drips / wallet / day
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={gasDripSettings.maxDripsPerWalletPerDay ?? 2}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    maxDripsPerWalletPerDay: parseInt(e.target.value, 10) || 2,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Max USD / user / day
              </label>
              <input
                type="number"
                min="0.01"
                max="5"
                step="0.01"
                value={gasDripSettings.maxUsdPerUserPerDay ?? 0.75}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    maxUsdPerUserPerDay: parseFloat(e.target.value) || 0.75,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <label className="flex items-center gap-2 text-sm col-span-full">
              <input
                type="checkbox"
                checked={gasDripSettings.requirePriorActivity !== false}
                onChange={(e) =>
                  setGasDripSettings((s) => ({ ...s, requirePriorActivity: e.target.checked }))
                }
              />
              Require prior prediction before first drip (jackpot claims exempt)
            </label>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Max drips / IP / day
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={gasDripSettings.maxDripsPerIpPerDay ?? 5}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    maxDripsPerIpPerDay: parseInt(e.target.value, 10) || 5,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Max USD / IP / day
              </label>
              <input
                type="number"
                min="0.05"
                step="0.01"
                value={gasDripSettings.maxUsdPerIpPerDay ?? 0.75}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    maxUsdPerIpPerDay: parseFloat(e.target.value) || 0.75,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Global USD budget / day
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={gasDripSettings.maxGlobalUsdPerDay ?? 25}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    maxGlobalUsdPerDay: parseFloat(e.target.value) || 25,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Complete action within (min)
              </label>
              <input
                type="number"
                min="5"
                max="120"
                value={gasDripSettings.completionWindowMinutes ?? 15}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    completionWindowMinutes: parseInt(e.target.value, 10) || 15,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Unused-drip penalty (hours)
              </label>
              <input
                type="number"
                min="1"
                max="168"
                value={gasDripSettings.unusedDripPenaltyHours ?? 48}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    unusedDripPenaltyHours: parseInt(e.target.value, 10) || 48,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Unused drips before penalty
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={gasDripSettings.maxUnusedDripsBeforePenalty ?? 1}
                onChange={(e) =>
                  setGasDripSettings((s) => ({
                    ...s,
                    maxUnusedDripsBeforePenalty: parseInt(e.target.value, 10) || 1,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Drips now require an action voucher (claim / boost / market). USDC transfers never get drip.
            If a user cancels after receiving ETH, they hit an unused-drip penalty. Logs store purpose +
            whether they completed a follow-up tx.
          </p>
          <button
            type="button"
            onClick={saveGasDripSettings}
            disabled={savingGasDrip}
            className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
          >
            {savingGasDrip ? 'Saving…' : 'Save gas drip settings'}
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Free jackpot claim eligibility date
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Only Free jackpot wins from events on or after this date count toward claimable balance.
            Saving recalculates all user balances immediately. Leave empty / clear to allow all dates.
          </p>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Eligible from (UTC date)
              </label>
              <input
                type="date"
                value={jackpotEligibleFrom}
                onChange={(e) => setJackpotEligibleFrom(e.target.value)}
                className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => saveJackpotEligibleFrom({ syncOnChain: false })}
              disabled={savingJackpotEligible}
              className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
            >
              {savingJackpotEligible ? 'Saving…' : 'Save & recalculate'}
            </button>
            <button
              type="button"
              onClick={() => saveJackpotEligibleFrom({ clear: true })}
              disabled={savingJackpotEligible}
              className="px-5 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 disabled:opacity-50 text-sm font-semibold"
            >
              Clear date
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Max linked wallets per account
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Users must sign a message to prove wallet ownership when linking. When they hit this limit,
            they see a modal listing their linked addresses (truncated) and can keep using those wallets.
          </p>
          <div className="flex flex-wrap items-end gap-3 max-w-md">
            <div className="flex-1 min-w-[8rem]">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Max wallets (1–10)
              </label>
              <input
                type="number"
                min="1"
                max="10"
                step="1"
                value={maxLinkedWallets}
                onChange={(e) => setMaxLinkedWallets(parseInt(e.target.value, 10) || 1)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <button
              type="button"
              onClick={saveMaxLinkedWallets}
              disabled={savingMaxWallets}
              className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
            >
              {savingMaxWallets ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Referral rewards</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Golden tickets awarded to referrers after referred players sign up <em>and</em> make their first
            free prediction. Set how many verified referrals are required for each reward (e.g. 5 players → 1
            golden ticket). Default is 1 verified player → 1 golden ticket.
          </p>
          <div className="flex items-center gap-3 mb-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={referralRewards.enabled !== false}
                onChange={(e) => setReferralRewards((r) => ({ ...r, enabled: e.target.checked }))}
                className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              Referral program enabled
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mb-4">
            <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Verified referrals required
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={referralRewards.referralsRequired ?? 1}
                onChange={(e) =>
                  setReferralRewards((r) => ({
                    ...r,
                    referralsRequired: Math.max(1, parseInt(e.target.value, 10) || 1),
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Golden tickets awarded
            </label>
            <input
              type="number"
              min="0"
              step="1"
                value={referralRewards.goldenTickets ?? 1}
              onChange={(e) =>
                setReferralRewards((r) => ({
                  ...r,
                    goldenTickets: Math.max(0, parseInt(e.target.value, 10) || 0),
                }))
              }
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
            />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Max referrals per user
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={referralRewards.maxReferrals ?? 10}
                onChange={(e) =>
                  setReferralRewards((r) => ({
                    ...r,
                    maxReferrals: Math.max(0, parseInt(e.target.value, 10) || 0),
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              />
              <p className="text-[11px] text-gray-500 mt-1">Default 10. Link pauses when reached.</p>
            </div>
          </div>
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
            Preview:{' '}
            <strong>
              {referralRewards.goldenTickets ?? 1} golden ticket
              {(referralRewards.goldenTickets ?? 1) === 1 ? '' : 's'} for every{' '}
              {referralRewards.referralsRequired ?? 1} verified referral
              {(referralRewards.referralsRequired ?? 1) === 1 ? '' : 's'}
            </strong>
            {' · '}
            <strong>cap {referralRewards.maxReferrals ?? 10} referrals / user</strong>
          </p>
          <button
            type="button"
            onClick={saveReferralRewards}
            disabled={savingReferralRewards}
            className="px-5 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 text-sm font-semibold"
          >
            {savingReferralRewards ? 'Saving…' : 'Save referral rewards'}
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Per-user referral limits
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Raise (or lower) the referral cap for a specific user by username, email, or wallet. Removing a
            custom limit restores the global max ({referralRewards.maxReferrals ?? 10}).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mb-4">
            <input
              type="text"
              placeholder="Username, email, or 0x wallet"
              value={referralLimitIdentifier}
              onChange={(e) => setReferralLimitIdentifier(e.target.value)}
              className="sm:col-span-2 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
            />
            <input
              type="number"
              min="0"
              step="1"
              placeholder="New limit"
              value={referralLimitValue}
              onChange={(e) => setReferralLimitValue(e.target.value)}
              className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            <button
              type="button"
              onClick={saveUserReferralLimit}
              disabled={savingReferralLimit}
              className="px-5 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 text-sm font-semibold"
            >
              {savingReferralLimit ? 'Saving…' : 'Save user limit'}
            </button>
            <button
              type="button"
              onClick={fetchReferralLimitOverrides}
              disabled={loadingReferralLimits}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {loadingReferralLimits ? 'Loading…' : 'Refresh list'}
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Override</th>
                  <th className="px-3 py-2">Used</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {referralLimitOverrides.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-gray-500 dark:text-gray-400">
                      No custom limits yet. Global max is {referralRewards.maxReferrals ?? 10}.
                    </td>
                  </tr>
                ) : (
                  referralLimitOverrides.map((row) => (
                    <tr key={row.userId}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900 dark:text-white">{row.username}</div>
                        <div className="text-xs text-gray-500">{row.email || row.referralCode || row.userId}</div>
                      </td>
                      <td className="px-3 py-2 tabular-nums">{row.override}</td>
                      <td className="px-3 py-2 tabular-nums">
                        {row.used}/{row.limit}
                      </td>
                      <td className="px-3 py-2">
                        {row.atCap ? (
                          <span className="text-amber-600 dark:text-amber-400 font-semibold">At cap</span>
                        ) : (
                          <span className="text-emerald-600 dark:text-emerald-400">Active</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right space-x-2">
                        <button
                          type="button"
                          className="text-violet-600 hover:underline text-xs font-semibold"
                          onClick={() => {
                            setReferralLimitIdentifier(row.username || row.email || '');
                            setReferralLimitValue(String(row.override ?? row.limit));
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline text-xs font-semibold"
                          onClick={() => deleteUserReferralLimit(row.userId)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Send golden tickets</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            One-time grant. User receives the full amount immediately.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Email or wallet address (0x…)"
              value={giftIdentifier}
              onChange={(e) => setGiftIdentifier(e.target.value)}
              className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white sm:col-span-2 lg:col-span-2"
            />
            <div className="flex flex-col gap-2">
              <select
                value={giftQuantityPreset}
                onChange={(e) => setGiftQuantityPreset(e.target.value)}
                className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              >
                {GOLDEN_TICKET_PRESETS.map((n) => (
                  <option key={n} value={n}>
                    {n} golden ticket{n === '1' ? '' : 's'}
                  </option>
                ))}
                <option value="custom">Custom amount</option>
              </select>
              {giftQuantityPreset === 'custom' && (
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Custom quantity"
                  value={giftQuantityCustom}
                  onChange={(e) => setGiftQuantityCustom(e.target.value)}
                  className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              )}
            </div>
            <button
              type="button"
              onClick={giftGoldenTickets}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-semibold h-fit"
            >
              Send tickets
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Golden tickets accumulate until spent. Use the user&apos;s registered email or a linked wallet address.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Golden ticket accumulation
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Schedule golden tickets for a user by email or linked wallet. Choose how tickets accrue, then
            manage schedules below (edit, cancel, or delete).
          </p>

          <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/20 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">Accumulation mode</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-start gap-2 cursor-pointer max-w-md">
                <input
                  type="radio"
                  name="accumMode"
                  checked={accumMode === 'daily'}
                  onChange={() => setAccumMode('daily')}
                  className="mt-1"
                />
                <span className="text-xs text-amber-950 dark:text-amber-100 leading-relaxed">
                  <strong>Daily (fixed duration)</strong> — e.g. 2 tickets + 3 days → user gets 2 tickets
                  every UTC day for exactly 3 days (6 tickets total), then stops.
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer max-w-md">
                <input
                  type="radio"
                  name="accumMode"
                  checked={accumMode === 'range'}
                  onChange={() => setAccumMode('range')}
                  className="mt-1"
                />
                <span className="text-xs text-amber-950 dark:text-amber-100 leading-relaxed">
                  <strong>Range (fixed recurring window)</strong> — e.g. 2 tickets every 7 days for 60 total
                  days. The first grant is immediate and the schedule stops automatically after day 60.
                </span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <input
              type="text"
              placeholder="Email or wallet address (0x…)"
              value={accumIdentifier}
              onChange={(e) => setAccumIdentifier(e.target.value)}
              className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white lg:col-span-2"
            />
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {accumMode === 'range' ? 'Tickets per grant' : 'Tickets per UTC day'}
              </label>
              <select
                value={accumTicketsPreset}
                onChange={(e) => setAccumTicketsPreset(e.target.value)}
                className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              >
                {GOLDEN_TICKET_PRESETS.map((n) => (
                  <option key={n} value={n}>
                    {n} ticket{n === '1' ? '' : 's'}
                  </option>
                ))}
                <option value="custom">Custom</option>
              </select>
              {accumTicketsPreset === 'custom' && (
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Custom amount"
                  value={accumTicketsCustom}
                  onChange={(e) => setAccumTicketsCustom(e.target.value)}
                  className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {accumMode === 'range' ? 'Every (days)' : 'Duration (days)'}
              </label>
              <select
                value={accumDaysPreset}
                onChange={(e) => setAccumDaysPreset(e.target.value)}
                className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              >
                {GOLDEN_DAY_PRESETS.map((n) => (
                  <option key={n} value={n}>
                    {n} day{n === '1' ? '' : 's'}
                  </option>
                ))}
                <option value="custom">Custom</option>
              </select>
              {accumDaysPreset === 'custom' && (
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder={accumMode === 'range' ? 'Interval days' : 'Total days'}
                  value={accumDaysCustom}
                  onChange={(e) => setAccumDaysCustom(e.target.value)}
                  className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              )}
            </div>
            {accumMode === 'range' && (
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Total drip duration (days)
                </label>
                <select
                  value={accumDurationPreset}
                  onChange={(e) => setAccumDurationPreset(e.target.value)}
                  className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                >
                  {GOLDEN_DURATION_PRESETS.map((n) => (
                    <option key={n} value={n}>
                      {n} days
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
                {accumDurationPreset === 'custom' && (
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Total active days"
                    value={accumDurationCustom}
                    onChange={(e) => setAccumDurationCustom(e.target.value)}
                    className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                )}
              </div>
            )}
            <button
              type="button"
              onClick={createDailyGoldenGrant}
              disabled={submittingDailyGrant}
              className={`px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold disabled:opacity-50 h-fit self-end ${
                accumMode === 'range' ? 'lg:col-span-1' : 'lg:col-span-2'
              }`}
            >
              {submittingDailyGrant ? 'Scheduling…' : accumMode === 'range' ? 'Start range grant' : 'Start daily grant'}
            </button>
          </div>

          {grantEditor && (
            <div className="mt-6 border rounded-lg p-4 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 space-y-3">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Edit schedule — {grantEditor.userLabel}
              </p>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="radio"
                    checked={grantEditor.accumulationMode === 'daily'}
                    onChange={() => setGrantEditor({ ...grantEditor, accumulationMode: 'daily' })}
                  />
                  Daily (fixed duration)
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="radio"
                    checked={grantEditor.accumulationMode === 'range'}
                    onChange={() =>
                      setGrantEditor({
                        ...grantEditor,
                        accumulationMode: 'range',
                        durationPreset: grantEditor.durationPreset || '60',
                        durationCustom: grantEditor.durationCustom || '',
                      })
                    }
                  />
                  Range (fixed recurring window)
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select
                  value={grantEditor.ticketsPreset}
                  onChange={(e) => setGrantEditor({ ...grantEditor, ticketsPreset: e.target.value })}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                >
                  {GOLDEN_TICKET_PRESETS.map((n) => (
                    <option key={n} value={n}>
                      {n} tickets
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
                {grantEditor.ticketsPreset === 'custom' && (
                  <input
                    type="number"
                    min="1"
                    value={grantEditor.ticketsCustom}
                    onChange={(e) => setGrantEditor({ ...grantEditor, ticketsCustom: e.target.value })}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  />
                )}
                <select
                  value={grantEditor.daysPreset}
                  onChange={(e) => setGrantEditor({ ...grantEditor, daysPreset: e.target.value })}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                >
                  {GOLDEN_DAY_PRESETS.map((n) => (
                    <option key={n} value={n}>
                      {n} days
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
                {grantEditor.daysPreset === 'custom' && (
                  <input
                    type="number"
                    min="1"
                    value={grantEditor.daysCustom}
                    onChange={(e) => setGrantEditor({ ...grantEditor, daysCustom: e.target.value })}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  />
                )}
                {grantEditor.accumulationMode === 'range' && (
                  <select
                    value={grantEditor.durationPreset || '60'}
                    onChange={(e) =>
                      setGrantEditor({ ...grantEditor, durationPreset: e.target.value })
                    }
                    aria-label="Total drip duration"
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  >
                    {GOLDEN_DURATION_PRESETS.map((n) => (
                      <option key={n} value={n}>
                        Lasts {n} days
                      </option>
                    ))}
                    <option value="custom">Custom total days</option>
                  </select>
                )}
                {grantEditor.accumulationMode === 'range' &&
                  grantEditor.durationPreset === 'custom' && (
                    <input
                      type="number"
                      min="1"
                      placeholder="Total active days"
                      value={grantEditor.durationCustom}
                      onChange={(e) =>
                        setGrantEditor({ ...grantEditor, durationCustom: e.target.value })
                      }
                      className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                    />
                  )}
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={grantEditor.active}
                  onChange={(e) => setGrantEditor({ ...grantEditor, active: e.target.checked })}
                />
                Active (uncheck to pause without deleting)
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveGrantEdit}
                  disabled={savingGrantEdit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                >
                  {savingGrantEdit ? 'Saving…' : 'Save changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setGrantEditor(null)}
                  className="px-4 py-2 border rounded-lg text-sm dark:border-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          )}

            <div className="mt-6 overflow-x-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Grant schedules</h4>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <select
                  value={dailyGrantStatusFilter}
                  onChange={(e) => {
                    setDailyGrantStatusFilter(e.target.value);
                    setDailyGrantPage(1);
                    fetchDailyGrants(e.target.value);
                  }}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active only</option>
                  <option value="inactive">Completed / cancelled</option>
                </select>
                <input
                  type="text"
                  placeholder="Search email, wallet, note…"
                  value={dailyGrantSearch}
                  onChange={(e) => {
                    setDailyGrantSearch(e.target.value);
                    setDailyGrantPage(1);
                  }}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm w-full sm:w-72"
                />
              </div>
            </div>
            {filteredDailyGrants.length > 0 ? (
              <>
              <table className="min-w-full text-sm text-left">
                <thead>
                  <tr className="border-b dark:border-gray-600 text-gray-600 dark:text-gray-400">
                    <th className="py-2 pr-4">User</th>
                      <th className="py-2 pr-4">Mode</th>
                      <th className="py-2 pr-4">Rate</th>
                    <th className="py-2 pr-4">Progress</th>
                      <th className="py-2 pr-4">Ends / type</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDailyGrants.map((g) => (
                    <tr key={g._id} className="border-b dark:border-gray-700">
                      <td className="py-2 pr-4 text-gray-900 dark:text-white">
                        {g.user?.email || g.user?.username || g.recipientEmail || shortAddr(g.recipientWallet)}
                      </td>
                        <td className="py-2 pr-4 capitalize">{g.accumulationMode === 'range' ? 'Range' : 'Daily'}</td>
                        <td className="py-2 pr-4">{grantRateLabel(g)}</td>
                        <td className="py-2 pr-4">{grantProgressLabel(g)}</td>
                      <td className="py-2 pr-4">
                          {g.endDate ? new Date(g.endDate).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 pr-4">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                              g.active
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {g.active ? 'Active' : 'Inactive'}
                          </span>
                      </td>
                      <td className="py-2">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => openGrantEditor(g)}
                              className="text-blue-600 hover:text-blue-700 text-xs font-semibold"
                            >
                              Edit
                            </button>
                            {g.active && (
                        <button
                          type="button"
                          onClick={() => cancelDailyGrant(g._id)}
                                className="text-amber-700 hover:text-amber-800 text-xs font-semibold"
                        >
                          Cancel
                        </button>
                            )}
                            <button
                              type="button"
                              onClick={() => deleteDailyGrant(g._id)}
                              className="text-red-600 hover:text-red-700 text-xs font-semibold"
                            >
                              Delete
                            </button>
                          </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {filteredDailyGrants.length === 0 ? 0 : dailyGrantStart + 1}–
                  {Math.min(dailyGrantStart + DAILY_GRANTS_PAGE_SIZE, filteredDailyGrants.length)} of{' '}
                  {filteredDailyGrants.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDailyGrantPage((p) => Math.max(1, p - 1))}
                    disabled={safeDailyGrantPage <= 1}
                    className="px-3 py-1.5 border rounded-lg text-xs font-semibold dark:border-gray-600 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    Page {safeDailyGrantPage} / {dailyGrantTotalPages}
                  </div>
                  <button
                    type="button"
                    onClick={() => setDailyGrantPage((p) => Math.min(dailyGrantTotalPages, p + 1))}
                    disabled={safeDailyGrantPage >= dailyGrantTotalPages}
                    className="px-3 py-1.5 border rounded-lg text-xs font-semibold dark:border-gray-600 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic py-4">
                No grant schedules match your filters.
              </p>
            )}
            </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Streak golden ticket milestones
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Users earn golden tickets once when their personal best streak reaches each milestone.
            Types: <strong>Total</strong> (sum of login + free + boost), <strong>Login</strong>,{' '}
            <strong>Free prediction</strong>, or <strong>Boost</strong>.
          </p>
          {streakMilestones.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="p-3">Streak type</th>
                    <th className="p-3 text-right">Required</th>
                    <th className="p-3 text-right">Golden tickets</th>
                    <th className="p-3 text-right w-28">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {streakMilestones.map((m, i) => (
                    <tr key={m.id || i} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="p-3 font-medium text-gray-900 dark:text-white">
                        {streakTypeAdminLabel(m.streakType)}
                      </td>
                      <td className="p-3 text-right tabular-nums">{m.requiredStreak}</td>
                      <td className="p-3 text-right tabular-nums text-amber-700 dark:text-amber-300">
                        +{m.goldenTickets}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => setStreakMilestoneEditor({ ...m, _editIndex: i })}
                            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          >
                            <SettingsIconEdit />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => setStreakMilestones(streakMilestones.filter((_, j) => j !== i))}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                          >
                            <SettingsIconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 italic">No streak milestones configured.</p>
          )}
          {streakMilestoneEditor && (
            <div className="border rounded-lg p-4 dark:border-gray-700 space-y-3 mb-4 bg-gray-50 dark:bg-gray-900/40">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {streakMilestoneEditor._editIndex >= 0 ? 'Edit milestone' : 'Add milestone'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Streak type</label>
                  <select
                    value={streakMilestoneEditor.streakType || 'total'}
                    onChange={(e) =>
                      setStreakMilestoneEditor({ ...streakMilestoneEditor, streakType: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  >
                    {STREAK_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Required streak count</label>
                  <input
                    type="number"
                    min="1"
                    value={streakMilestoneEditor.requiredStreak ?? 1}
                    onChange={(e) =>
                      setStreakMilestoneEditor({
                        ...streakMilestoneEditor,
                        requiredStreak: parseInt(e.target.value, 10) || 1,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Golden tickets reward</label>
                  <input
                    type="number"
                    min="1"
                    value={streakMilestoneEditor.goldenTickets ?? 1}
                    onChange={(e) =>
                      setStreakMilestoneEditor({
                        ...streakMilestoneEditor,
                        goldenTickets: parseInt(e.target.value, 10) || 1,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Example: +2 golden at 30 free streak → user receives 2 golden tickets once when their free
                prediction best streak reaches 30.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={applyStreakMilestoneEditor}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold"
                >
                  {streakMilestoneEditor._editIndex >= 0 ? 'Update row' : 'Add to list'}
                </button>
                <button
                  type="button"
                  onClick={() => setStreakMilestoneEditor(null)}
                  className="px-4 py-2 border rounded-lg text-sm dark:border-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setStreakMilestoneEditor({ ...emptyStreakMilestone(), _editIndex: -1 })}
              className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold"
            >
              + Add milestone
            </button>
            <button
              type="button"
              onClick={saveStreakMilestones}
              disabled={savingStreakMilestones}
              className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
            >
              {savingStreakMilestones ? 'Saving…' : 'Save streak milestones'}
            </button>
          </div>
        </div>

        {/* Google AdSense */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Google AdSense</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Ads display only on Free details and Blog pages. Keep Auto ads off in AdSense (or exclude Market, Wallet,
            and Store pages) so paid trading screens stay ad-free. Create Display ad units in AdSense and paste the
            slot IDs below for stronger fill; the publisher snippet still verifies the site without slots.
          </p>
          <label className="flex items-center gap-3 mb-4">
            <input
              type="checkbox"
              checked={adsense.enabled}
              onChange={(e) => setAdsense({ ...adsense, enabled: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Enable AdSense placements</span>
          </label>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Publisher ID
              </label>
              <input
                type="text"
                value={adsense.client}
                onChange={(e) => setAdsense({ ...adsense, client: e.target.value })}
                placeholder="ca-pub-9986668508600694"
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Free details sidebar slot
                </label>
                <input
                  type="text"
                  value={adsense.slots.freeSidebar}
                  onChange={(e) =>
                    setAdsense({ ...adsense, slots: { ...adsense.slots, freeSidebar: e.target.value } })
                  }
                  placeholder="e.g. 1234567890"
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Free details mobile slot
                </label>
                <input
                  type="text"
                  value={adsense.slots.freeMobile}
                  onChange={(e) =>
                    setAdsense({ ...adsense, slots: { ...adsense.slots, freeMobile: e.target.value } })
                  }
                  placeholder="Optional — uses sidebar slot if empty"
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Blog list in-feed slot
                </label>
                <input
                  type="text"
                  value={adsense.slots.blogFeed}
                  onChange={(e) =>
                    setAdsense({ ...adsense, slots: { ...adsense.slots, blogFeed: e.target.value } })
                  }
                  placeholder="e.g. 1234567890"
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Blog article in-content slot
                </label>
                <input
                  type="text"
                  value={adsense.slots.blogArticle}
                  onChange={(e) =>
                    setAdsense({ ...adsense, slots: { ...adsense.slots, blogArticle: e.target.value } })
                  }
                  placeholder="e.g. 1234567890"
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Blog sidebar slot
                </label>
                <input
                  type="text"
                  value={adsense.slots.blogSidebar}
                  onChange={(e) =>
                    setAdsense({ ...adsense, slots: { ...adsense.slots, blogSidebar: e.target.value } })
                  }
                  placeholder="e.g. 1234567890"
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={saveAdsense}
            disabled={savingAdsense}
            className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 font-semibold"
          >
            {savingAdsense ? 'Saving…' : 'Save AdSense settings'}
          </button>
        </div>

        {/* Social Media Links */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Social Media Links</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Configure social media links that appear in the navbar. Icons are always visible. Set a link to make them clickable, or leave empty to show disabled icons.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                X (Twitter) Link
              </label>
              <input
                type="text"
                value={socialLinks.socialTwitter}
                onChange={(e) => setSocialLinks({ ...socialLinks, socialTwitter: e.target.value })}
                placeholder="https://twitter.com/yourhandle or any link"
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Facebook Link
              </label>
              <input
                type="text"
                value={socialLinks.socialFacebook}
                onChange={(e) => setSocialLinks({ ...socialLinks, socialFacebook: e.target.value })}
                placeholder="https://facebook.com/yourpage or any link"
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Instagram Link
              </label>
              <input
                type="text"
                value={socialLinks.socialInstagram}
                onChange={(e) => setSocialLinks({ ...socialLinks, socialInstagram: e.target.value })}
                placeholder="https://instagram.com/yourhandle or any link"
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                YouTube Link
              </label>
              <input
                type="text"
                value={socialLinks.socialYoutube}
                onChange={(e) => setSocialLinks({ ...socialLinks, socialYoutube: e.target.value })}
                placeholder="https://youtube.com/@yourchannel or any link"
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Discord Invite Link
              </label>
              <input
                type="text"
                value={socialLinks.socialDiscord}
                onChange={(e) => setSocialLinks({ ...socialLinks, socialDiscord: e.target.value })}
                placeholder="https://discord.gg/your-community"
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Use a permanent Discord server invite so the navbar link does not expire.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={saveSocialLinks}
          disabled={savingSocial}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 font-semibold"
        >
          {savingSocial ? 'Saving…' : 'Save social links'}
        </button>
      </div>
    </div>
  );
};

// Edit Match Modal
const EditMatchModal = ({ match, cups, stages, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    teamA: match.teamA || '',
    teamB: match.teamB || '',
    description: match.description || '',
    date: utcIsoToDatetimeLocal(match.date),
    cup: match.cup?._id || match.cup || '',
    stage: match.stage?._id || match.stage || '',
    stageName: match.stageName || '',
    isFeatured: match.isFeatured || false,
    isSponsored: match.isSponsored || false,
    freePredictionEnabled: match.freePredictionEnabled !== false,
    marketEnabled: match.marketEnabled !== false,
    sponsoredImages: normalizeSponsoredImages(match.sponsoredImages),
    lockedTime: utcIsoToDatetimeLocal(match.lockedTime),
    teamAImage: match.teamAImage || '',
    teamBImage: match.teamBImage || '',
  });
  const [availableStages, setAvailableStages] = useState([]);

  useEffect(() => {
    if (formData.cup) {
      const selectedCup = cups.find(c => c._id === formData.cup || c.slug === formData.cup);
      if (selectedCup) {
        api.get(`/cups/${selectedCup.slug}/stages`)
          .then(response => setAvailableStages(response.data))
          .catch(() => setAvailableStages([]));
      }
    }
  }, [formData.cup, cups]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedStage = availableStages.find(s => s._id === formData.stage);
    onSubmit({
      ...formData,
      date: utcDatetimeLocalToIso(formData.date),
      lockedTime: utcDatetimeLocalToIso(formData.lockedTime),
      stageName: selectedStage?.name || formData.stageName,
      sponsoredImages: normalizeSponsoredImages(formData.sponsoredImages),
    });
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Match" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              placeholder="Team A"
              value={formData.teamA}
              onChange={(e) => setFormData({ ...formData, teamA: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white mb-2"
              required
            />
            <ImageUpload
              label="Team A Image"
              value={formData.teamAImage}
              onChange={(url) => setFormData({ ...formData, teamAImage: url })}
              folder="wergame/teams"
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="Team B"
              value={formData.teamB}
              onChange={(e) => setFormData({ ...formData, teamB: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white mb-2"
              required
            />
            <ImageUpload
              label="Team B Image"
              value={formData.teamBImage}
              onChange={(url) => setFormData({ ...formData, teamBImage: url })}
              folder="wergame/teams"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Match date &amp; time (GMT) <span className="text-red-500">*</span>
          </label>
        <input
          type="datetime-local"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        </div>
        <textarea
          placeholder="Description (optional)"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
        />
        <select
          value={formData.cup}
          onChange={(e) => setFormData({ ...formData, cup: e.target.value, stage: '' })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        >
          <option value="">Select Cup</option>
          {cups.map((cup) => (
            <option key={cup._id} value={cup._id}>{cup.name}</option>
          ))}
        </select>
        {formData.cup && availableStages.length > 0 && (
          <select
            value={formData.stage}
            onChange={(e) => {
              const selectedStage = availableStages.find(s => s._id === e.target.value);
              setFormData({ ...formData, stage: e.target.value, stageName: selectedStage?.name || '' });
            }}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select Stage</option>
            {availableStages.map((stage) => (
              <option key={stage._id} value={stage._id}>{stage.name}</option>
            ))}
          </select>
        )}
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Featured Match</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isSponsored}
              onChange={(e) => setFormData({ ...formData, isSponsored: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Sponsored Match</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.freePredictionEnabled}
              onChange={(e) => setFormData({ ...formData, freePredictionEnabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Free prediction enabled</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.marketEnabled}
              onChange={(e) => setFormData({ ...formData, marketEnabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Market enabled</span>
          </label>
        </div>
        
        {/* Sponsored Images - Show when sponsored is checked */}
        {formData.isSponsored && (
          <SponsoredImagesEditor
            images={formData.sponsoredImages}
            onChange={(sponsoredImages) => setFormData({ ...formData, sponsoredImages })}
          />
        )}

        {/* Locked Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Locked Time (GMT, optional) - When predictions should be locked
          </label>
          <input
            type="datetime-local"
            value={formData.lockedTime}
            onChange={(e) => setFormData({ ...formData, lockedTime: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="flex space-x-2">
          <button type="submit" className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            Update
          </button>
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Add Liquidity Modal for Match (YES / NO per outcome, single batched on-chain tx)
const AddLiquidityModal = ({ match, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    teamAYes: '',
    teamANo: '',
    teamBYes: '',
    teamBNo: '',
    drawYes: '',
    drawNo: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit({
        teamAYes: parseFloat(formData.teamAYes) || 0,
        teamANo: parseFloat(formData.teamANo) || 0,
        teamBYes: parseFloat(formData.teamBYes) || 0,
        teamBNo: parseFloat(formData.teamBNo) || 0,
        drawYes: parseFloat(formData.drawYes) || 0,
        drawNo: parseFloat(formData.drawNo) || 0,
      });
      onClose();
    } catch (error) {
      console.error('Error in add liquidity modal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const row = (label, yesKey, noKey) => (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label} — YES (USDC)</label>
        <input
          type="number"
          step="0.01"
          value={formData[yesKey]}
          onChange={(e) => setFormData({ ...formData, [yesKey]: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
          placeholder="0"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label} — NO (USDC)</label>
        <input
          type="number"
          step="0.01"
          value={formData[noKey]}
          onChange={(e) => setFormData({ ...formData, [noKey]: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
          placeholder="0"
        />
      </div>
    </div>
  );

  return (
    <Modal isOpen={true} onClose={onClose} title={`Add Liquidity - ${match.teamA} vs ${match.teamB}`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Initial pools are credited in one batched contract call (orderbook reference liquidity).
        </p>
        {row(match.teamA || 'Team A', 'teamAYes', 'teamANo')}
        {row(match.teamB || 'Team B', 'teamBYes', 'teamBNo')}
        {row('Draw', 'drawYes', 'drawNo')}
        <div className="flex space-x-2 pt-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting…' : 'Add Liquidity'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Edit Poll Modal
const EditPollModal = ({ poll, cups, onClose, onSubmit }) => {
  // USDC ~ USD; no price API needed
  const liqMap = new Map(
    (poll?.orderbook?.liquidityYesNo || []).map((x) => [String(x?.optionKey || '').trim(), { yes: x?.yes || 0, no: x?.no || 0 }])
  );
  const [formData, setFormData] = useState({
    question: poll.question || '',
    thumbnailImage: poll.thumbnailImage || '',
    description: poll.description || '',
    type: poll.type || 'match',
    cup: poll.cup?._id || poll.cup || '',
    isFeatured: poll.isFeatured || false,
    isSponsored: poll.isSponsored || false,
    freePredictionEnabled: poll.freePredictionEnabled !== false,
    marketEnabled: poll.marketEnabled !== false,
    sponsoredImages: normalizeSponsoredImages(poll.sponsoredImages),
    date: utcIsoToDatetimeLocal(poll.date || poll.createdAt),
    lockedTime: utcIsoToDatetimeLocal(poll.lockedTime),
    optionType: 'options',
    options: poll.options
      ? poll.options.map((opt) => {
          const key = String(opt?.text || '').trim();
          const seed = liqMap.get(key) || { yes: 0, no: 0 };
          return {
            text: opt.text || '',
            image: opt.image || '',
            yesLiquidity: seed.yes || '',
            noLiquidity: seed.no || '',
          };
        })
      : [],
  });

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { text: '', image: '', yesLiquidity: '', noLiquidity: '' }],
    });
  };

  const removeOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    
    submitData.optionType = 'options';
    submitData.options = formData.options.map((opt) => ({
      text: String(opt.text || '').trim(),
      image: opt.image || undefined,
      yesLiquidity: parseFloat(opt.yesLiquidity) || 0,
      noLiquidity: parseFloat(opt.noLiquidity) || 0,
    }));

    submitData.date = utcDatetimeLocalToIso(formData.date);
    submitData.lockedTime = utcDatetimeLocalToIso(formData.lockedTime);
    submitData.sponsoredImages = normalizeSponsoredImages(formData.sponsoredImages);
    
    onSubmit(submitData);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Poll" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Question"
          value={formData.question}
          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        />
        <ImageUpload
          label="Poll thumbnail"
          value={formData.thumbnailImage}
          onChange={(url) => setFormData({ ...formData, thumbnailImage: url })}
          folder="wergame/polls"
        />
        <textarea
          placeholder="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          rows="3"
        />
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
        >
          <option value="match">Match</option>
          <option value="team">Team</option>
          <option value="stage">Stage</option>
          <option value="award">Award</option>
        </select>
        <select
          value={formData.cup}
          onChange={(e) => setFormData({ ...formData, cup: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          required
        >
          <option value="">Select Cup</option>
          {cups.map((cup) => (
            <option key={cup._id} value={cup._id}>{cup.name}</option>
          ))}
        </select>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Event date &amp; time (GMT)
          </label>
          <input
            type="datetime-local"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Enter the wall time in GMT — this is exactly what users will see.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.freePredictionEnabled}
              onChange={(e) => setFormData({ ...formData, freePredictionEnabled: e.target.checked })}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Free prediction enabled</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.marketEnabled}
              onChange={(e) => setFormData({ ...formData, marketEnabled: e.target.checked })}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Market enabled</span>
          </label>
        </div>
        
        {/* Poll Options (option-based only) */}
        <div className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Poll Options
              </label>
              <button
                type="button"
                onClick={addOption}
                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
              >
                + Add Option
              </button>
            </div>
            {formData.options.map((option, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Option {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                  >
                    Remove
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Option text"
                  value={option.text}
                  onChange={(e) => updateOption(index, 'text', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  required
                />
                <ImageUpload
                  label="Option Image"
                  value={option.image}
                  onChange={(url) => updateOption(index, 'image', url)}
                  folder="wergame/poll-options"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      YES liquidity (USDC)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={option.yesLiquidity}
                      onChange={(e) => updateOption(index, 'yesLiquidity', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      NO liquidity (USDC)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={option.noLiquidity}
                      onChange={(e) => updateOption(index, 'noLiquidity', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Total: {formatUsdAmount((parseFloat(option.yesLiquidity) || 0) + (parseFloat(option.noLiquidity) || 0))}
                </p>
              </div>
            ))}
            {formData.options.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                Click "Add Option" to create poll options
              </p>
            )}
          </div>
        
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Featured Poll</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isSponsored}
              onChange={(e) => setFormData({ ...formData, isSponsored: e.target.checked })}
              className="mr-2"
            />
            <span className="text-gray-700 dark:text-gray-300">Sponsored Poll</span>
          </label>
        </div>
        
        {/* Sponsored Images - Show when sponsored is checked */}
        {formData.isSponsored && (
          <SponsoredImagesEditor
            images={formData.sponsoredImages}
            onChange={(sponsoredImages) => setFormData({ ...formData, sponsoredImages })}
          />
        )}

        {/* Locked Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Locked Time (GMT, optional) - When predictions should be locked
          </label>
          <input
            type="datetime-local"
            value={formData.lockedTime}
            onChange={(e) => setFormData({ ...formData, lockedTime: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="flex space-x-2">
          <button type="submit" className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            Update
          </button>
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Poll Status Modal
const PollStatusModal = ({ poll, onClose, onSubmit }) => {
  const [status, setStatus] = useState(poll.status);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updates = { status };
    // If status is set to locked, immediately lock (override any locked time)
    if (status === 'locked') {
      updates.lockedTime = new Date().toISOString();
    }
    onSubmit(poll._id, updates);
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Update Poll Status">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
            required
          >
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="locked">Locked</option>
            <option value="settled">Settled</option>
          </select>
          {status === 'locked' && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              Setting status to "Locked" will immediately lock predictions for this poll.
            </p>
          )}
        </div>
        <div className="flex space-x-2">
          <button type="submit" className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            Update
          </button>
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Add Liquidity Modal for Poll
const AddPollLiquidityModal = ({ poll, onClose, onSubmit }) => {
  // USDC ~ USD; no price API needed
  const [formData, setFormData] = useState({
    optionIndex: '',
    optionYes: '',
    optionNo: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit({
        optionIndex: parseInt(formData.optionIndex, 10),
        optionYes: parseFloat(formData.optionYes) || 0,
        optionNo: parseFloat(formData.optionNo) || 0,
      });
      // Only close modal after successful submission
      onClose();
    } catch (error) {
      // Error already handled in onSubmit, just don't close modal
      console.error('Error in add poll liquidity modal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Add Liquidity - ${poll.question}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Option
            </label>
            <select
              value={formData.optionIndex}
              onChange={(e) => setFormData({ ...formData, optionIndex: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="">Select an option</option>
              {poll.options && poll.options.map((option, index) => (
                <option key={index} value={index}>
                  {option.text}
                </option>
              ))}
            </select>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  YES side (USDC)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.optionYes}
                  onChange={(e) => setFormData({ ...formData, optionYes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  NO side (USDC)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.optionNo}
                  onChange={(e) => setFormData({ ...formData, optionNo: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="0"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Batched on-chain via <code className="text-xs">batchAddOrderbookLiquidity</code>.
            </p>
          </div>
        <div className="flex space-x-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting…' : 'Add Liquidity'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default Admin;
